/**
 * Cliente HTTP tipado do backend Valbot.
 *
 * A URL base vem de NEXT_PUBLIC_API_URL (ex.: https://api-valbot.lcmti.com.br).
 * O prefixo NEXT_PUBLIC_ e obrigatorio porque estas chamadas acontecem no
 * NAVEGADOR do usuario — por isso a URL precisa ser a publica, e nunca o nome
 * interno do container (http://backend:8000), que so resolve dentro da rede
 * docker "valbot-net".
 */

const RAW_BASE_URL =
  process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

/**
 * A API de cada host em que o portal pode ser aberto, alem do padrao
 * (16/10/2026): a central serve o mesmo portal em app-valbot.lcmti.com.br e em
 * etl.valbot.com.br, e cada um fala com a API do PROPRIO dominio
 * (api-valbot.lcmti.com.br / api-etl.valbot.com.br). Nao e enfeite: o cookie de
 * sessao e host-only da API e o de gate vai no dominio dela, entao o portal de
 * um dominio falando com a API do outro perderia a sessao.
 *
 * Formato (embutido no build, como NEXT_PUBLIC_API_URL):
 * "host=https://api,host2=https://api2". Host fora do mapa usa o padrao.
 *
 * So no NAVEGADOR: no servidor do Next nao ha `window`, e o valor e o padrao.
 * Por isso nenhuma URL da API pode sair no HTML do servidor (ela vem dos
 * fetch do cliente, depois da hidratacao) — senao o atributo ficaria com a API
 * do padrao no outro dominio.
 */
function apiDoHost(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window.location.hostname.toLowerCase();
  for (const par of (process.env.NEXT_PUBLIC_API_URL_POR_HOST ?? "").split(",")) {
    const [nome, url] = par.split("=").map((parte) => parte.trim());
    if (nome && url && nome.toLowerCase() === host) return url;
  }
  return undefined;
}

/** URL base normalizada (sem barra final). */
export const API_BASE_URL: string = (apiDoHost() ?? RAW_BASE_URL).replace(/\/+$/, "");

/* -------------------------------------------------------------------------- */
/* Tipos                                                                       */
/* -------------------------------------------------------------------------- */

export interface Health {
  /** Ex.: "ok" | "healthy" | "degraded". */
  status: string;
  /** Estado da conexao com o Postgres, quando o backend reporta. */
  database?: string | null;
  /** Versao da API, quando o backend reporta. */
  version?: string | null;
}

export interface User {
  id: number;
  email: string;
  /**
   * A sessao do acesso externo (aberta pelo botao do valbot.com.br, 16/10/2026):
   * so le. A API recusa (403) tudo o que muda estado; a tela so avisa.
   */
  somente_leitura?: boolean;
}

export interface Session {
  user: User;
  /** Segundos ate o token expirar. */
  expires_in: number;
}

export interface Credentials {
  email: string;
  password: string;
}

/* ----------------------------- Exames ------------------------------------- */

/**
 * Veredito do EXAMINADOR — os tres valores que a origem grava:
 *   'A' aprovado | 'R' reprovado | 'N' NAO AVALIADO.
 *
 * NULL e o QUARTO estado ("ainda sem informacao") e existe de verdade: a spec
 * original so citava A/R/N, mas ha linhas com NULL na producao. 'N' e NULL sao
 * estados NEUTROS, nunca erro nem reprovacao: 'N' e o exame cujo veredito real
 * chega depois (mediana de 36 h para migrar de 'N' para A ou R).
 *
 * Nao confundir com a coluna `resultado` da producao, que e saida da IA e NAO
 * entra no portal. Os dois eixos sao independentes: ha 393 exames com
 * resultado='APROVADO' e resultado_exame='R', e 477 o inverso.
 */
export type ResultadoExame = "A" | "R" | "N";

/** Um exame pratico, como o EXAMINADOR o registrou. */
export interface Exam {
  /** PK (uuid). 15 tabelas da producao tem FK nela. */
  id: string;
  /**
   * Chave natural, UNIQUE na origem (varchar(64), nao 32). E por `hash` que o
   * sync concilia e e ele o parametro das funcoes de escrita da aplicacao —
   * entao e ele, e nao `external_id`, que identifica um exame.
   */
  /**
   * ETAPAS já processadas por este portal: `{ audio: "pronto" }`.
   *
   * Dicionário e não um campo por etapa: hoje só existe áudio, e a próxima
   * entra como mais uma chave sem mudar o contrato. Chave ausente = nunca
   * processada, que é diferente de processada e vazia. O valor é o ESTADO, não
   * um booleano — "pronto", "erro" e "sem_audio" pedem cores diferentes.
   *
   * Uma tradução só (06/10/2026): a linha `bloqueado` com `etapa =
   * "audio_reprovado"` (anulada porque a régua do tempo de ruído reprovou o
   * áudio, ou o portão que fechou por isso) chega como `"audio_reprovado"`, o
   * estado do próprio áudio. O resto vai cru (`_estado_do_selo` em
   * backend/app/api/routes/exams.py).
   */
  etapas: Record<string, string>;
  hash: string;
  /**
   * idAgendamento do TechPratico. NAO e unico e nao serve de chave: 483 valores
   * se repetem (527 linhas excedentes) porque a mesma ingestao acontece de novo.
   */
  external_id: number | null;
  renach: string | null;
  processo: string | null;
  categoria: string | null;
  rubrica: string;
  candidato_nome: string | null;
  candidato_cpf: string | null;
  examinador: string | null;
  local_unidade: string | null;
  veiculo: string | null;
  auto_escola: string | null;
  /** Quatro estados — ver ResultadoExame. */
  resultado_exame: ResultadoExame | null;
  /**
   * Nota do EXAMINADOR (nao `pontuacao_total`, que e o score da IA).
   * ZERO e nota valida, nao dado faltando: aprovado sem nenhuma falta e o caso
   * majoritario (11.996 dos 18.351 'A'). So e "pendente" quando
   * resultado_exame e 'N' ou null.
   */
  pontuacao_oficial: number | null;
  /**
   * Quantas faltas o EXAMINADOR anotou na ficha oficial — a contagem dos itens
   * de `training_annotations` (jsonb), feita pelo backend.
   *
   * O NOME NAO E `num_infracoes` DE PROPOSITO. `num_infracoes` e a coluna da IA
   * na producao, proibida neste projeto e nem sequer copiada para o portal:
   * mesma familia de `pontuacao_total` (IA) contra `pontuacao_oficial`
   * (examinador) e de `resultado` (IA) contra `resultado_exame` (examinador).
   * Usar aquele numero aqui trocaria silenciosamente o julgamento do humano
   * pelo palpite do modelo — o acidente que este portal existe para evitar.
   *
   * Medido em 11/09/2026 sobre os 8.392 exames do portal: `training_annotations`
   * e SEMPRE array (8.392 de 8.392) e a soma dos `pontuacao` dos itens bate com
   * `pontuacao_oficial` em 8.392 de 8.392 linhas, zero divergencias — por isso a
   * UI toma os PONTOS de `pontuacao_oficial` (a coluna canonica) e so a
   * CONTAGEM daqui.
   *
   * ZERO e valor legitimo, nao ausencia: 3.214 dos 4.738 aprovados tem ficha
   * vazia porque o candidato nao cometeu falta nenhuma. Quem exibe decide o
   * significado do zero junto com `resultado_exame`, nunca sozinho.
   */
  infracoes_oficiais: number;
  /**
   * Caminho gs:// gravado no INSERT, ANTES do download: e INTENCAO, nao prova.
   * Serve para exibir/copiar a origem, jamais para decidir se ha video.
   */
  gs_video: string | null;
  /** Duracao medida do video. NUMERIC(8,2) na origem — pode chegar como string. */
  duration_s: number | null;
  /**
   * Unico teste valido de "o video chegou ao GCS": duration_s IS NOT NULL.
   * Nao se infere de `status` nem de `gs_video` — ha status avancado sem video
   * (failed tem 1.136 linhas 'N', so 247 com video).
   */
  video_disponivel: boolean;
  /**
   * Um dos 8 valores medidos (queued, recebido, processed, bloqueado_fase1,
   * failed, aguardando_validacao, uploading, processed_no_pdf). Fica como string
   * livre de proposito: um valor novo na producao nao pode quebrar a tela.
   */
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Um exame COM os dois JSONB que a listagem recusa — o que `GET /exams/{hash}`
 * devolve.
 *
 * Os dois sao `unknown` DE PROPOSITO: a pagina de detalhe ainda nao desenha
 * nenhum deles, e tipar agora a estrutura de dentro seria inventar um contrato
 * que ninguem le e que ninguem confere contra a origem — que, alem de tudo, nao
 * versiona o formato para nos. `unknown` (e nao `any`) obriga quem for desenhar
 * a estreitar o tipo no ponto de uso, no dia em que houver uso.
 *
 * `ingest_payload_raw` e NULL em 3.979 dos 8.407 exames do espelho: ausencia e o
 * caso COMUM, nao anomalia. `training_annotations` e NOT NULL na origem (ficha
 * sem falta chega como array vazio) e e dela que o backend deriva
 * `infracoes_oficiais` — o numero ja vem pronto em `Exam`, nao se conta aqui.
 */
/* ---------------- A cadeia de decisão como a PRODUÇÃO a registra ----------- */

/**
 * Uma infração como as tabelas da produção a gravam — `exam_infractions`,
 * `exam_comite_laudos`, `auditor_pareceres`.
 *
 * TUDO OPCIONAL, e não por preguiça: as três tabelas gravam jsonb com formas
 * parecidas mas não iguais (a do comitê tem `evidencia` onde a do monitoramento
 * tem `descricao`), e o portal LÊ um banco de terceiro que não versiona formato
 * para nós. Campo ausente vira ausência na tela, nunca `undefined` renderizado.
 */
export interface InfracaoDaProducao {
  regra_id?: string | null;
  gravidade?: string | null;
  pontos?: number | null;
  timestamp_s?: number | null;
  descricao?: string | null;
  confianca?: number | null;
  status?: string | null;
  /** Só o auditor escreve isto: a frase dele sobre ESTA falta, que é diferente
   *  da justificativa do parecer inteiro. */
  justificativa?: string | null;
  /**
   * Só o auditor: de onde ele ADOTOU o item — `examinador` (da ficha) ou `val`
   * (da IA de produção); ausente quando ele mesmo o inseriu. A regra é a de
   * `origem_do_auditor` (backend/app/infracoes/normalizacao.py). A análise
   * completa do exame (11/10/2026) mostra isto como "inserida / adotada".
   */
  origem_adocao?: string | null;
}

/**
 * O tronco comum das quatro etapas: ela aconteceu, e — quando não aconteceu —
 * POR QUE não.
 *
 * `motivo` é obrigação de contrato, não enfeite: ausência de parecer NUNCA vira
 * parecer vazio. As frases são as do legado (valbot_web/producao_v2.py:258-360),
 * e elas distinguem coisas que mandam o supervisor fazer ações opostas — "este
 * exame não gerou ordem de serviço" contra "a ordem está em andamento e ainda
 * não tem parecer".
 */
export interface EtapaDaProducao {
  disponivel: boolean;
  motivo?: string | null;
  /**
   * O rótulo CRU da produção. Não é o nosso enum e não deve ser traduzido para
   * ele: a produção usa a palavra INAPTO para dizer reprovação POR PONTOS da IA
   * DELA (963 exames de categoria B no escopo espelhado), que é o OPOSTO do
   * INAPTO do nosso veredito. Quem desenha mostra o valor cru e diz de quem é.
   */
  resultado?: string | null;
}

/** ② A IA da produção. LEITURA DE TERCEIRO — nunca desenhada como oficial. */
export interface MonitoramentoDaProducao extends EtapaDaProducao {
  pontos?: number | null;
  run_vigente?: number | string | null;
  infracoes?: InfracaoDaProducao[] | null;
}

/** ③ A comissão. Medida em 21/09/2026: ZERO laudos no escopo que o portal
 *  espelha (created_at >= 2026-08-01) — a etapa foi desligada em 13/08/2026.
 *  A coluna existe porque exames anteriores a isso continuam existindo. */
export interface ComiteDaProducao extends EtapaDaProducao {
  conclusao?: string | null;
  pontuacao_sustentada?: number | null;
  /** Um exame pode ter até 14 laudos. Quando são vários e discordam, a tela
   *  avisa: mostrar o último calado afirmaria um desfecho que a própria
   *  comissão contradisse. */
  total_laudos?: number | null;
  resultados_distintos?: number | null;
  fundamentacao?: string | null;
  recomendacao_para_auditor?: string | null;
  infracoes?: InfracaoDaProducao[] | null;
  quando?: string | null;
}

/** ④ O parecer humano. 1.028 no escopo espelhado. */
export interface AuditorDaProducao extends EtapaDaProducao {
  /**
   * "concorda"/"discorda" — a opinião sobre a IA, NÃO o veredito. Nunca vai
   * para a pílula de resultado: em 5 pareceres da produção isso desenharia
   * "CONCORDA" no lugar de um desfecho.
   */
  decisao?: string | null;
  justificativa?: string | null;
  auditor?: string | null;
  quando?: string | null;
  infracoes?: InfracaoDaProducao[] | null;
}

/** ⑤ A decisão final da cadeia. 752 no escopo espelhado. */
export interface SupervisorDaProducao extends EtapaDaProducao {
  decisao?: string | null;
  /** `false` em 941 das 942 decisões medidas — por isso só aparece quando é
   *  verdadeiro: um "não" repetido em toda tela vira ruído. */
  homologar_conduta?: boolean | null;
  justificativa?: string | null;
  supervisor?: string | null;
  quando?: string | null;
}

/**
 * A ordem de serviço. 49.000 no escopo (uma por exame) — e a esmagadora maioria
 * é TOCO: `tipo_divergencia='pendente'`, `status='criada'`. Ela não é uma etapa
 * da cadeia; é a legenda que diz por que as etapas humanas existiriam.
 */
export interface OrdemDeServicoDaProducao {
  disponivel: boolean;
  numero?: string | null;
  status?: string | null;
  tipo_divergencia?: string | null;
  /** `smallint` na origem (medido: 1 a 3), nao texto. */
  prioridade?: number | null;
  criada_em?: string | null;
  encerrada_em?: string | null;
}

/**
 * O que o sync leu da produção sobre a cadeia de decisão DESTE exame.
 *
 * `null` quando o sync nunca passou por este exame — que é diferente de
 * `disponivel: false` em cada etapa (o sync passou e não havia parecer). A tela
 * diz coisas diferentes nos dois casos.
 */
export interface ProducaoDoExame {
  sincronizado_em: string | null;
  /**
   * De onde vieram as colunas (19/10/2026). `espelho`: a cadeia sincronizada
   * (`exam_etapas`, o dev). `retorno_auditor`: só o parecer do auditor chegou,
   * pelo retorno do auditor (a central, docs/infra/RETORNO-AUDITOR.md) — ② e ③
   * não foram sincronizados, e a tela diz "não sincronizado", não "sem parecer"
   * nem "aguardando". Opcional: a API de antes não o publica.
   */
  fonte?: "espelho" | "retorno_auditor";
  monitoramento: MonitoramentoDaProducao;
  comite: ComiteDaProducao;
  auditor: AuditorDaProducao;
  supervisor: SupervisorDaProducao;
  ordem_servico: OrdemDeServicoDaProducao;
}

export interface ExamDetalhe extends Exam {
  training_annotations: unknown;
  ingest_payload_raw: unknown;
  /**
   * As etapas ②③④⑤ da cadeia, lidas da produção (projeto valbot-497920) pelo
   * mesmo processo que já traz os exames — decisão do dono em 21/09/2026.
   *
   * OPCIONAL NO TIPO porque o campo nasceu depois da tela: enquanto o backend
   * desta frente não publicar `producao`, a resposta simplesmente não o traz, e
   * um tipo obrigatório faria o TypeScript afirmar a presença de um objeto que
   * não existe em runtime. `null` = o sync nunca passou.
   */
  producao?: ProducaoDoExame | null;
  /**
   * Quando ESTA linha foi gravada no portal — a unica coluna de `exams` que nao
   * existe na producao, e a unica que so o detalhe carrega.
   *
   * NAO E `updated_at`, e a diferenca e a razao de ela existir: aquele e o
   * relogio da PRODUCAO (quando o exame mudou la) e serve de cursor do sync;
   * este e o relogio DAQUI (quando o sync gravou). Um exame parado ha uma semana
   * na origem tem `updated_at` antigo e `sincronizado_em` de minutos atras — as
   * duas coisas certas ao mesmo tempo.
   *
   * `null` quando o backend nao mandou o campo: a tela testa valor, nunca
   * presenca de chave.
   */
  sincronizado_em: string | null;
}

/**
 * Ancora da PROXIMA pagina, copiada da ULTIMA linha entregue na atual.
 *
 * As duas partes viajam sempre juntas porque a condicao do backend compara a
 * TUPLA `(created_at, id)` com `(valor, id)` — o `id` e o desempate que torna a
 * ordenacao total. Mandar so metade e erro 422 la, de proposito: um cursor pela
 * metade produziria um recorte silenciosamente errado.
 */
export interface ExamCursor {
  /** `created_at`/`updated_at` (ISO) da ultima linha da pagina atual. */
  valor: string;
  /** `id` (uuid) da ultima linha da pagina atual. */
  id: string;
}

/**
 * Uma pagina de exames + o total do COUNT do backend.
 *
 * `page`/`pages` SAIRAM: a paginacao e KEYSET (cursor), nao OFFSET. O motivo e o
 * worker de sync, que insere no portal exatamente os `created_at` mais novos a
 * cada 300 s (~1,2 linha por minuto, 765 nas ultimas 24 h). Cada insercao entra
 * no TOPO de `created_at DESC` e empurra a janela do OFFSET uma posicao: quem
 * leu a pagina 1 por dois minutos e rolou recebia na pagina 2 linhas que ja
 * estavam na tela — e, na borda oposta, PERDIA uma linha que nunca apareceria em
 * pagina nenhuma, sem aviso. Ancorado na ultima linha entregue, o que foi
 * inserido depois simplesmente nao entra ate a proxima consulta.
 *
 * Nao ha campo "tem_mais": `proximo_cursor === null` JA e o fim da lista, e dois
 * campos dizendo a mesma coisa divergem um dia.
 */
/**
 * ⑤ A decisão VIGENTE do supervisor, reduzida ao que a listagem compara.
 *
 * `infracoes`/`pontos` NÃO são dele: o supervisor não aponta infração. São os
 * da lista do AUDITOR que ele HOMOLOGOU. Na reforma vêm null — ele desfez o
 * parecer sem dizer quais faltas sustenta, e contagem nenhuma seria dele.
 */
export interface SupervisorNaListagem {
  /** Cru da produção, minúsculo: `aprovado` | `reprovado`. */
  resultado: string | null;
  /** `homologar` | `reformar` — o que ele fez com o parecer do auditor. */
  decisao: string | null;
  infracoes: number | null;
  pontos: number | null;
}

/**
 * ④ O parecer VIGENTE do AUDITOR (a etapa `auditor` da produção), reduzido ao
 * que a listagem compara. O auditor é quem lê o exame DEPOIS do examinador,
 * insere as infrações e audita; o supervisor vem depois dele e homologa ou
 * reforma (o dono, 14/10/2026). Desde então é ele, e não o supervisor, que a
 * lista põe embaixo do examinador e que os filtros da barra comparam.
 */
export interface AuditorNaListagem {
  /** Cru da produção, minúsculo e aparado: `aprovado` | `reprovado`. */
  resultado: string | null;
  /** Os itens da lista do auditor. */
  infracoes: number | null;
  /** A soma dos pontos (peso) da lista; lista vazia = 0. */
  pontos: number | null;
}

/** ⑥ O veredito DESTA esteira (`veredito_processamento`), em contagem. */
export interface NossaIaNaListagem {
  /** O da linha: `pronto`, `processando`, `erro`, `interrompido`… */
  estado: string;
  /** APROVADO | REPROVADO | INAPTO | NAO_CALCULAVEL; null fora de `pronto`. */
  resultado: string | null;
  /**
   * `n_ocorreu`: as faltas que a esteira afirma, inclusive as de peso 0. null
   * (com `pontos`) também no NAO_CALCULAVEL: sem material não há contagem, e a
   * tela escreve "—" (bloco E, 24/09).
   */
  infracoes: number | null;
  pontos: number | null;
  /** Calculado com um catálogo de regras que já mudou; recalcular é de graça. */
  regua_desatualizada: boolean;
  /**
   * A cobertura: de quantas regras em jogo a esteira leu alguma coisa. É ela
   * que separa um APROVADO desta esteira de uma afirmação falsa (regra do dono,
   * 21/09/2026). null = rodada sem o bloco.
   */
  cobertura_lidas: number | null;
  cobertura_em_jogo: number | null;
}

// Os dois eixos da triagem (AD-5): o nome vem do vocabulario comum.
import { EIXOS_DA_TRIAGEM } from "./vocabulario";
import type { PlanoDaEsteira } from "./categorias-esteira";

/** Um eixo da triagem (AD-5, 24/09): o score e o destino que a triagem gravou. */
export interface EixoNaTriagem {
  score: number | null;
  destino: string | null;
}

/**
 * Os dois eixos da AD-5 (`resultado`, `conduta`), pelo nome do vocabulario.
 * Eixo ausente = a triagem gravada nao o publicou (a `triagem/1`, de antes de
 * 24/09): nao e "sem alerta" e nao se desenha como tal.
 */
export type EixosDaTriagem = Partial<Record<(typeof EIXOS_DA_TRIAGEM)[number], EixoNaTriagem>>;

/**
 * O compliance DESTE portal, em resumo — a linha da Fila do supervisor dentro
 * do registro (a Fila deixou de ser tela própria em 24/09: "não quero dois
 * menus"). Os MESMOS campos que `GET /compliance/fila` publica por exame.
 *
 * Fora de `pronto` só `estado` e `atualizado_em` vêm: os estados e a triagem
 * da linha podem ainda ser os da rodada anterior.
 */
export interface ComplianceNaListagem {
  /** O da LINHA: `pronto`, `pendente`, `processando`, `interrompido`, `erro`… */
  estado: string;
  estado_principal: string | null;
  /** TODOS os estados do exame, na ordem de severidade. */
  estados: string[] | null;
  n_alertas: number | null;
  score: number | null;
  faixa: string | null;
  /** `SUPERVISOR` | `RECUPERACAO_TECNICA` | `SEM_ALERTA_EM_SOMBRA`. */
  destino: string | null;
  /**
   * Os dois eixos da AD-5. O `score`, a `faixa` e o `destino` de cima são o
   * LEGADO (a soma de tudo). null na triagem gravada antes dos eixos.
   */
  eixos: EixosDaTriagem | null;
  /** A régua em que ESTA triagem foi calculada; o GET não recalcula. */
  assinatura_regras: string | null;
  /** null = sem triagem pronta (não se sabe, e não se chuta). */
  regua_vigente: boolean | null;
  atualizado_em: string | null;
}

/** O que o payload do integrador traz de telemetria — disponibilidade, não qualidade. */
export interface TelemetriaNaListagem {
  /** `percurso` com ao menos um ponto (GPS, ~1 Hz). */
  gps: boolean;
  /** `telemetrias` com ao menos um evento (porta, ignição, cinto, marcha, freio de mão). */
  sensores: boolean;
}

/**
 * O que ESTE portal gastou processando o exame — os MESMOS números da tira fixa
 * da página do exame (`total` de `GET /exams/{hash}/consumo`).
 */
export interface ProcessamentoNaListagem {
  /** A soma do que as etapas pagaram (retentativas e descartes incluídos). */
  custo_usd: number | null;
  /** false = alguma etapa que chamou modelo não tem custo: a soma é PISO. */
  custo_completo: boolean;
  /** Relógio em que alguma etapa rodava (união dos intervalos). */
  tempo_ocupado_s: number | null;
  /**
   * A parede de cada etapa somada, com a visão UMA vez, pelo papel mais longo
   * (os quatro papéis correm juntos; C1.16 da auditoria de 23/09, orquestrador-6).
   */
  tempo_soma_s: number | null;
  /** Etapas prontas sem relógio (antes de 23/09/2026): acima de 0, os tempos são PISO. */
  etapas_sem_relogio: number;
  banco_bytes: number | null;
  /** A mídia local, por inode; a limpeza da mídia zera. */
  disco_bytes: number | null;
  /** banco + disco. */
  armazenamento_bytes: number | null;
}

/**
 * Os pares de veredito que a listagem filtra. `true` diverge, `false`
 * concorda, `null` = um dos lados não tem veredito comparável. A MESMA regra
 * dos filtros `*_diverge_*` do backend.
 *
 * Os dois do AUDITOR entraram em 14/10/2026: o backend anterior não os manda,
 * e ausente vira null (não se sabe), nunca false.
 *
 * Os da IA, no exame de categoria A (29/10/2026), leem a letra da IA da A (o
 * veredito da `a_infracoes`: só APROVADO e REPROVADO têm letra; o
 * NAO_CONCLUSIVO, o NAO_CALCULADO e o barrado dão null).
 */
export interface DivergenciasNaListagem {
  supervisor_examinador: boolean | null;
  ia_examinador: boolean | null;
  ia_supervisor: boolean | null;
  auditor_examinador: boolean | null;
  ia_auditor: boolean | null;
}

/**
 * A ÚLTIMA execução do "Rodar tudo" deste exame (maior id de
 * `esteira_execucoes`). É a execução, e não as etapas (essas vêm em `etapas`):
 * o que só ela sabe é o que a P6 do dono (24/09) acrescentou — a retomada
 * automática e o "parado para análise" depois de duas falhas.
 */
export interface EsteiraNaListagem {
  execucao_id: number;
  /** NA_FILA · EM_EXECUCAO · PRECISA_DE_DADO · CONCLUIDA · ERRO · INTERROMPIDA · CANCELADA · CANCELANDO. */
  estado: string;
  motivo: string | null;
  /** P6: a segunda falha parou o exame (estado ERRO) — espera uma pessoa. */
  para_analise: boolean;
  /** P6: quantas vezes o próprio laço reabriu um nó que caiu. */
  retomadas_auto: number;
  /** Quantas falhas a execução registrou. */
  n_falhas: number;
  criado_em: string | null;
  terminado_em: string | null;
}

/** O veredito cego da `a_infracoes` (grafo-a2/7), em contagem. */
export interface VereditoANaListagem {
  /** APROVADO | REPROVADO | NAO_CONCLUSIVO | NAO_CALCULADO, como a etapa gravou. */
  rotulo: string;
  /**
   * As três contagens são das infrações de ALTA confiança (as que somam). null
   * no NAO_CALCULADO: a etapa grava 0 porque não leu, e a tela escreve "—".
   */
  pontos: number | null;
  eliminatorias: number | null;
  n_infracoes: number | null;
}

/** Um motivo da decisão da qualidade do vídeo (`indice_abaixo_do_minimo`, `camera_inadequada`…). */
export interface MotivoDoComplianceA {
  codigo: string;
  frase: string;
}

/**
 * A decisão da `a_compliance` (a qualidade do vídeo de cada câmera). Só da
 * linha pronta: fora dela os números ainda podem ser os da rodada anterior.
 */
export interface ComplianceANaListagem {
  /** `avaliado` · `desligado` · `erro_modulo` · `modulo_indisponivel`. */
  estado: string | null;
  /** PROCESSAR · PROCESSAR_COM_RESTRICOES · NAO_PROCESSAR · INDETERMINADO. */
  decisao: string | null;
  /** O modo da decisão: `ativo` desde 29/10/2026 (a A é sempre ativo); `ensaio` é a regra antiga, que não barrava. */
  modo: string | null;
  /** Índice de qualidade e mínimo da célula, em pontos percentuais. */
  indice: number | null;
  indice_minimo: number | null;
  bloqueia: boolean;
  /** Em ensaio, NAO_PROCESSAR: com a célula em modo ativo, teria barrado. */
  teria_bloqueado: boolean;
  motivos: MotivoDoComplianceA[];
}

/**
 * A IA DA CATEGORIA A (29/10/2026): o veredito da `a_infracoes` e a decisão da
 * `a_compliance`. A A não grava em `veredito_processamento` (de onde vem
 * `nossa_ia`): sem este bloco, todo exame A dizia "não calculado".
 */
export interface IaCategoriaANaListagem {
  /** O da linha da `a_infracoes` (`pronto`, `processando`, `interrompido`…); null = nunca rodou. */
  estado: string | null;
  /** null fora de `pronto`, na etapa pulada ou desligada. */
  veredito: VereditoANaListagem | null;
  /** A `a_infracoes` concluiu desligada na configuração. */
  desligada: boolean;
  /** null = a `a_compliance` não rodou ou não está pronta. */
  compliance: ComplianceANaListagem | null;
  /**
   * O compliance do vídeo BARROU o exame (a `a_compliance` com `bloqueia`, ou
   * a `a_infracoes` pulada por ele): a IA não profere resultado.
   */
  barrado: boolean;
}

/**
 * Uma linha da LISTAGEM: o exame e os pareceres lado a lado.
 *
 * Tipo próprio, e não campos novos em `Exam`: o detalhe (`getExam`) também
 * parte de `Exam` e já traz a cadeia inteira em `producao` — um `supervisor`
 * vazio ao lado dela seria um segundo lugar dizendo a mesma coisa.
 */
export interface ExamListItem extends Exam {
  /** null = sem decisão da supervisão. */
  supervisor: SupervisorNaListagem | null;
  /**
   * null = sem parecer do auditor (ou o backend, anterior a 14/10/2026, não o
   * manda).
   */
  auditor: AuditorNaListagem | null;
  /** null = a nossa IA nunca calculou o veredito deste exame. */
  nossa_ia: NossaIaNaListagem | null;
  /** null = o compliance nunca rodou neste exame (ou o backend não o manda). */
  compliance: ComplianceNaListagem | null;
  /**
   * null = o servidor não informou (backend anterior aos pareceres) — o que
   * NÃO é "sem telemetria": isso é `{gps: false, sensores: false}`.
   */
  telemetria: TelemetriaNaListagem | null;
  /** null = este portal nunca processou o exame. */
  processamento: ProcessamentoNaListagem | null;
  divergencias: DivergenciasNaListagem;
  /** null = o exame nunca passou pelo "Rodar tudo" (ou o backend não o manda). */
  esteira: EsteiraNaListagem | null;
  /**
   * Só nos exames de categoria A; null = nenhuma das duas etapas rodou (ou o
   * backend, anterior a 29/10/2026, não o manda).
   */
  ia_categoria_a: IaCategoriaANaListagem | null;
}

export interface ExamPage {
  items: ExamListItem[];
  total: number;
  per_page: number;
  proximo_cursor: ExamCursor | null;
  /**
   * As leituras AO LADO que caíram nesta página, pelo nome do campo do item
   * (`supervisor`, `auditor`, `nossa_ia`, `ia_categoria_a`, `compliance`, `processamento`, `esteira`,
   * `etapas`). Vale
   * para TODAS as linhas da página: nelas, o campo vazio é "não lido", e não
   * ausência. Lista vazia = tudo foi lido; campo ausente (backend anterior a
   * ele) vira vazia. `indicadores` é o único nome que é da PÁGINA.
   */
  leituras_indisponiveis: readonly string[];
  /**
   * Os INDICADORES DA IA VALBOT sobre este recorte (07/10/2026, no lugar do
   * denominador da Fila do supervisor). Só a PRIMEIRA página os traz: nas
   * continuações vem null, e quem guarda é a tela. Na primeira página, null
   * com `indicadores` em `leituras_indisponiveis` é leitura que caiu.
   */
  indicadores: IndicadoresDaIa | null;
}

/** Um artigo entre os que a IA Valbot mais apontou no recorte. */
export interface InfracaoMaisApontada {
  /** `Art. 208` (sem inciso); null = regra sem artigo do CTB. */
  artigo: string | null;
  /** A regra mais apontada do artigo, de onde saem o nome e a descrição. */
  id_regra: string | null;
  /** O que a tela escreve: o artigo, senão o nome da regra. */
  rotulo: string;
  descricao: string | null;
  ocorrencias: number;
  exames: number;
  /**
   * O mesmo artigo na ficha do examinador, nos MESMOS exames (os processados
   * do recorte): itens e exames. null = linha de regra sem artigo (e não
   * zero): a da B a ficha não tem como anotar; a da A (`A:<tipo>`, a
   * suspensão da prova) a ficha anota sem artigo, e o confronto não é feito.
   */
  ocorrencias_do_examinador: number | null;
  exames_do_examinador: number | null;
}

/**
 * O custo da IA Valbot nos exames PROCESSADOS do recorte (`indicadores.custo`
 * de `GET /exams`; pedido do dono, 09/10/2026). O custo de cada exame é o
 * MESMO da coluna Processamento e da tira (`esteira.custo_total`): um preço só.
 */
export interface CustoDoRecorte {
  /** A soma do custo dos processados, em US$ (6 casas). */
  total_usd: number;
  /** `total_usd / exames` (6 casas); null = nenhum processado no recorte. */
  medio_usd: number | null;
  /** Quantos entraram na soma, contados pela própria leitura do custo. */
  exames: number;
  /**
   * Quantos têm etapa que chamou modelo sem custo gravado: o custo deles é
   * PISO, e então o total e a média também.
   */
  exames_com_piso: number;
}

/**
 * Os números da IA Valbot sobre o recorte da listagem (`indicadores` de
 * `GET /exams`). Cada um é o total da lista com o filtro que ele resume:
 * `processados` = `ia_valbot=processado`, `a_processar` = `pendente`,
 * as divergências = `ia_diverge_examinador`. `processados`, `a_processar` e
 * `sem_veredito_do_examinador` somam `exames`.
 *
 * A CATEGORIA A (29/10/2026) conta pela IA dela, e só por ela: processado é a
 * `a_infracoes` concluída (o barrado pelo compliance do vídeo inclusive; a
 * desligada não). O aprovado e o reprovado dela somam em `aprovado` e
 * `reprovado`, o NAO_CALCULADO em `nao_calculavel`, e o NAO_CONCLUSIVO e o
 * barrado têm campo próprio: `aprovado + reprovado + inapto + nao_calculavel
 * + nao_conclusivo + barrado_compliance` = `processados`. O custo de um exame
 * A é a soma das etapas A (o preço da tira); as infrações dela entram pelo
 * artigo do CTB ou, sem ele, pela regra (`id_regra` `A:<tipo>`).
 */
export interface IndicadoresDaIa {
  exames: number;
  processados: number;
  a_processar: number;
  /** Sem veredito da IA e com o examinador N ou vazio: fora das duas contas acima. */
  sem_veredito_do_examinador: number;
  aprovado: number;
  reprovado: number;
  inapto: number;
  nao_calculavel: number;
  /**
   * Categoria A: o veredito que pede conferência humana (mais de um candidato,
   * prova interrompida sem falta de alta, cobertura abaixo do mínimo). Sem
   * letra: não compara com o examinador.
   */
  nao_conclusivo: number;
  /** Categoria A: o compliance do vídeo barrou, e a IA não profere resultado (processado, sem letra). */
  barrado_compliance: number;
  comparaveis_com_examinador: number;
  divergem_do_examinador: number;
  examinador_aprovou_ia_reprovou: number;
  /** Dentro de `examinador_aprovou_ia_reprovou`: a IA disse inapto (pela fala). */
  examinador_aprovou_ia_inapto: number;
  examinador_reprovou_ia_aprovou: number;
  infracoes: number;
  exames_com_infracao: number;
  /** Os itens da ficha do examinador nos mesmos exames processados. */
  infracoes_do_examinador: number;
  exames_com_infracao_do_examinador: number;
  mais_apontadas: InfracaoMaisApontada[];
  /**
   * O custo dos processados, lido AO LADO dos outros números: null = a
   * leitura do custo caiu (`custo_do_recorte` em `leituras_indisponiveis`),
   * e o resto da faixa segue valendo.
   */
  custo: CustoDoRecorte | null;
}

/**
 * Filtros da listagem. Tudo opcional: filtro ausente = sem restricao.
 *
 * `status` e `resultado` sao `readonly` para aceitarem tanto um array de estado
 * do React quanto uma constante `as const` da propria tela.
 */
export interface ExamFilters {
  /**
   * Ancora devolvida pela carga anterior (`proximo_cursor`). Ausente/null = a
   * PRIMEIRA pagina do recorte.
   */
  cursor?: ExamCursor | null;
  per_page?: number;
  /** Busca textual (nome, CPF, renach, processo...). Vazia = nao enviada. */
  q?: string;
  status?: readonly string[];
  /** Valores de resultado_exame. "N" e um estado legitimo de filtro. */
  resultado?: readonly string[];
  /**
   * Etapa de áudio já processada. Várias valem como OU.
   *
   * `processado` · `processando` · `interrompido` · `sem_audio` · `erro` ·
   * `audio_reprovado` · `nao_processado`.
   *
   * ESTES NOMES NÃO SÃO os estados guardados no banco — há tradução, e ela é
   * deliberada (`pronto` vira `processado`, `pendente` e `processando` colapsam
   * num só, `nao_processado` é a ausência de linha). O de-para completo mora em
   * backend/app/api/routes/exams.py, junto da consulta que o usa.
   */
  etapa_audio?: readonly string[];
  com_video?: boolean;
  /**
   * Categoria de habilitação do exame (`A` · `B` · `C` · `D` · `E`). Várias
   * valem como OU; o servidor normaliza para maiúscula, e letra que não
   * existe não casa exame nenhum (o contrato do `status`).
   */
  categoria?: readonly string[];
  /**
   * A UF do exame, que é o prefixo do RENACH (`PI` · `SE` · `PE` · `MA`).
   * Várias valem como OU; o contrato é o do `uf` da Visão compartilhada
   * (`_condicao_uf`): ausente = todas, caixa indiferente, e presente sem
   * nenhuma UF válida = recorte VAZIO, e não "todas".
   */
  uf?: readonly string[];
  /**
   * O laudo do examinador: `com` = veredito A ou R (a regra "Só com laudo" do
   * processamento), `sem` = N ou vazio. Ausente = os dois. A tela /exames
   * manda `com` por padrão.
   */
  laudo?: "com" | "sem";
  /**
   * idAgendamento do TechPratico. NAO estreita uma busca: reconstitui um GRUPO.
   *
   * Diferente de todos os outros filtros daqui, este costuma devolver mais de
   * uma linha de proposito — `external_id` repete (483 valores na producao,
   * 527 linhas excedentes) porque a mesma ingestao acontece de novo ao longo do
   * tempo, e cada repeticao e uma linha com hash proprio. E o que a aba de
   * detalhes usa para listar as IRMAS de um exame.
   */
  external_id?: number;
  /**
   * Telemetria no payload do integrador: `com` | `sem`. Várias valem como OU —
   * as duas juntas não filtram nada.
   */
  telemetria?: readonly string[];
  /**
   * As divergências de veredito, CADA UMA um filtro próprio (E entre si):
   * `true` = os dois lados têm veredito e ele diverge; `false` = concorda.
   * Lado sem veredito comparável fica fora dos dois. As duas do AUDITOR
   * (parecer vigente da etapa `auditor`) entraram em 14/10/2026.
   */
  supervisor_diverge_examinador?: boolean;
  ia_diverge_examinador?: boolean;
  ia_diverge_supervisor?: boolean;
  auditor_diverge_examinador?: boolean;
  ia_diverge_auditor?: boolean;
  /**
   * A FILA DO SUPERVISOR como filtro (24/09). Os três grupos se combinam em E,
   * e as opções de um grupo valem como OU; opção desconhecida é ignorada.
   *
   * `compliance_estado`: um dos sete estados, casado contra TODOS os estados
   * do exame, e não só o principal.
   */
  compliance_estado?: readonly string[];
  /**
   * O destino da triagem (`SUPERVISOR` | `RECUPERACAO_TECNICA` |
   * `SEM_ALERTA_EM_SOMBRA`), ou `calculado` (qualquer destino) e
   * `nao_calculado` (sem compliance pronto).
   */
  compliance_destino?: readonly string[];
  /** Instante ISO: só compliance calculado a partir dele. */
  compliance_desde?: string;
  /**
   * A ÚLTIMA execução do "Rodar tudo" (P6, 24/09): `em_execucao` ·
   * `concluida` · `com_erro` (ERRO ou INTERROMPIDA) · `para_analise` (parado
   * depois de duas falhas). Várias valem como OU; opção desconhecida é
   * ignorada pelo servidor.
   */
  esteira?: readonly string[];
  /**
   * O veredito da IA Valbot (05/10/2026), valor ÚNICO e não repetível:
   * `processado` = há veredito pronto (qualquer resultado, inclusive o “não
   * calculável”: a IA rodou e concluiu); `pendente` = o examinador deu veredito
   * (A ou R — N e NULL não são veredito) e a IA Valbot ainda não;
   * `integrado` (14/10/2026) = veredito pronto e laudo levado à produção
   * legada (pedido de INTEGRAR que terminou INTEGRADO). Opção desconhecida é
   * ignorada pelo servidor (sem filtro, nunca 422).
   *
   * O exame de categoria A (29/10/2026) conta pela IA dela, e só por ela: o
   * "veredito pronto" é a `a_infracoes` concluída, qualquer rótulo, o barrado
   * pelo compliance do vídeo inclusive (a desligada e a pulada velha seguem
   * pendentes). A A integra (30/10/2026): no `integrado`, o exame A conta pelo
   * laudo da A (o pedido cujo envelope tem `meta.categoria` "A").
   */
  ia_valbot?: "processado" | "pendente" | "integrado";
  /**
   * O RETORNO DO AUDITOR da produção (19/10/2026, docs/infra/RETORNO-AUDITOR.md
   * §6), valor ÚNICO: `auditado` = integrado com parecer vigente;
   * `concorda`/`diverge` = o veredito do auditor contra o que a IA ENVIOU (o
   * envelope, não o veredito de agora); `aguardando` = integrado sem parecer
   * vigente.
   */
  retorno_auditor?: "auditado" | "concorda" | "diverge" | "aguardando";
  /**
   * A DATA DO EXAME (`created_at`, a que cada linha mostra e a da ordenação),
   * em instantes ISO e intervalo SEMI-ABERTO: `exame_desde` entra,
   * `exame_ate` NÃO entra — é o começo do dia SEGUINTE ao último dia pedido.
   * Instante sem fuso o backend lê em UTC.
   */
  exame_desde?: string;
  exame_ate?: string;
  /**
   * A data em que a IA Valbot concluiu o veredito, com a mesma regra
   * semi-aberta de `exame_desde`/`exame_ate`. Com qualquer um dos dois, só
   * entram exames processados pela IA Valbot. No exame A, a data da linha da
   * `a_infracoes` (o `atualizado_em` dela).
   */
  ia_processado_desde?: string;
  ia_processado_ate?: string;
  /**
   * `ia_processado_em` exige (o backend força) `ia_valbot=processado`:
   * pendente não tem veredito pronto, logo não tem essa data. O `integrado`
   * vale como veio (ele já exige o veredito pronto).
   */
  ordem?: "created_at" | "updated_at" | "ia_processado_em";
  direcao?: "asc" | "desc";
}

/** Saude da copia local: ate onde o sync leu e como terminou a ultima corrida. */
export interface SyncStatus {
  /** Cursor: maior updated_at ja copiado da producao. */
  ultimo_updated_at: string | null;
  /** Quando o portal respondeu esta pergunta. */
  atualizado_em: string | null;
  total_exames: number;
  /**
   * Piso do espelho: data PURA (YYYY-MM-DD, sem hora e sem fuso) a partir da
   * qual um exame e copiado, comparada com o created_at da producao.
   *
   * Esta aqui porque a copia e PARCIAL de proposito: o portal tem uma fatia das
   * ~65.000 linhas da origem, e a tela precisa poder dizer por que. Nao e o
   * mesmo que `ultimo_updated_at` — aquele e ate onde a leitura chegou (anda a
   * cada rodada), este e o recorte do que esta no escopo (fixo, vem do .env).
   *
   * null = o backend nao informou; nesse caso nao invente texto de escopo.
   */
  criado_desde: string | null;
  ultima_execucao: {
    terminado_em: string | null;
    lidos: number;
    inseridos: number;
    atualizados: number;
    modo: string;
    erro: string | null;
  } | null;
}

/** Erro de API com o status HTTP preservado (0 = falha de rede/CORS/offline). */
export class ApiError extends Error {
  readonly status: number;
  readonly url: string;
  /**
   * O corpo da resposta de erro, ja lido (JSON ou texto); `undefined` quando
   * nao houve resposta. Existe para o 409 que carrega dado: o `POST
   * /esteira/lotes` devolve a previa NOVA em `detail.previa` quando a
   * confirmada mudou, e a tela a mostra sem outra ida ao servidor.
   */
  readonly corpo: unknown;

  constructor(message: string, status: number, url: string, corpo?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
    this.corpo = corpo;
  }

  /** true quando nem chegamos a falar com o servidor. */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** true quando a sessao acabou, foi revogada, ou nunca existiu. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/* -------------------------------------------------------------------------- */
/* Infra                                                                       */
/* -------------------------------------------------------------------------- */

const DEFAULT_TIMEOUT_MS = 10_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Extrai a mensagem de erro do corpo (FastAPI usa {"detail": ...}). */
function extractErrorMessage(body: unknown, fallback: string): string {
  if (typeof body === "string" && body.trim() !== "") return body.trim();
  if (!isRecord(body)) return fallback;

  const detail = body.detail ?? body.message ?? body.error;
  if (typeof detail === "string" && detail.trim() !== "") return detail.trim();
  // `detail` objeto com a frase em `mensagem` (o 409 do lote da esteira, que
  // leva a previa nova junto).
  if (isRecord(detail) && typeof detail.mensagem === "string" && detail.mensagem.trim() !== "") {
    return detail.mensagem.trim();
  }
  if (Array.isArray(detail)) {
    const parts = detail
      .map((entry) =>
        isRecord(entry) && typeof entry.msg === "string" ? entry.msg : null,
      )
      .filter((msg): msg is string => msg !== null);
    if (parts.length > 0) return parts.join("; ");
  }
  return fallback;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /**
   * JSON por padrao. Um `Blob` vai CRU, com o `Content-Type` do proprio blob
   * (`audio/webm` do MediaRecorder; `application/octet-stream` se ele nao
   * disser) — o ditado dos ensinamentos (26/09) manda o audio assim, e
   * `JSON.stringify(blob)` daria `{}`.
   */
  body?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, signal, timeoutMs = DEFAULT_TIMEOUT_MS } = options;
  const url = `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`;

  // `typeof Blob` porque este modulo tambem e importado em codigo que roda no
  // servidor (Node): la o global existe desde o 18, mas o guarda custa nada.
  const blob = typeof Blob !== "undefined" && body instanceof Blob ? body : null;
  const headers: Record<string, string> = { Accept: "application/json" };
  if (blob !== null) headers["Content-Type"] = blob.type || "application/octet-stream";
  else if (body !== undefined) headers["Content-Type"] = "application/json";

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const onExternalAbort = () => controller.abort();
  signal?.addEventListener("abort", onExternalAbort, { once: true });

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      cache: "no-store",
      // OBRIGATORIO e facil de esquecer. Sem credentials:"include" o browser nao
      // ARMAZENA o Set-Cookie do login nem ENVIA o cookie nas chamadas
      // seguintes — porque app-valbot e api-valbot sao origens diferentes.
      // O sintoma e cruel: o CORS parece ok, o login responde 200, e todo o
      // resto responde 401 em loop.
      credentials: "include",
      headers,
      body: blob ?? (body !== undefined ? JSON.stringify(body) : undefined),
      signal: controller.signal,
    });
  } catch (error) {
    if (signal?.aborted) throw error;
    const reason =
      error instanceof DOMException && error.name === "AbortError"
        ? `Tempo esgotado (${timeoutMs / 1000}s) ao chamar a API.`
        : "Nao foi possivel falar com a API (rede, DNS, TLS ou CORS).";
    throw new ApiError(reason, 0, url);
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener("abort", onExternalAbort);
  }

  const rawText = await response.text();
  let parsed: unknown = null;
  if (rawText.trim() !== "") {
    try {
      parsed = JSON.parse(rawText) as unknown;
    } catch {
      parsed = rawText;
    }
  }

  if (!response.ok) {
    throw new ApiError(
      extractErrorMessage(parsed, `HTTP ${response.status} ${response.statusText}`),
      response.status,
      url,
      parsed,
    );
  }

  return parsed as T;
}

/* -------------------------------------------------------------------------- */
/* Normalizacao defensiva das respostas                                        */
/* -------------------------------------------------------------------------- */

function toUserOrThrow(raw: unknown): User {
  if (!isRecord(raw) || typeof raw.id !== "number" || typeof raw.email !== "string") {
    throw new ApiError("Resposta da API em formato inesperado.", 500, API_BASE_URL);
  }
  return { id: raw.id, email: raw.email, somente_leitura: raw.somente_leitura === true };
}

/**
 * Texto opcional: string vazia (ou so espacos) vira null.
 *
 * candidato_nome, candidato_cpf, examinador, local_unidade, veiculo e
 * auto_escola chegam '' na origem porque o integrador usa o shape de lote. O
 * sync ja normaliza na carga, e aqui se repete a defesa: se um '' escapar, todo
 * `!== null` na tela da true e a celula renderiza um vazio mudo em vez do
 * estado "sem informacao".
 */
function toTextoOuNull(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const limpo = value.trim();
  return limpo === "" ? null : limpo;
}

/**
 * Numero opcional, tolerante a numero em string.
 *
 * `duration_s` e NUMERIC(8,2) no Postgres e esse e exatamente o caso: numeric
 * vira Decimal no Python e o Pydantic serializa como string ("83.40") para nao
 * perder precisao. `Number("83.40")` resolve; qualquer lixo vira NaN e NaN e
 * tratado como null, porque NaN renderizado na tela aparece como "NaN".
 */
function toNumeroOuNull(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const numero = Number(value);
  return Number.isFinite(numero) ? numero : null;
}

/**
 * Veredito do examinador, ou null.
 *
 * A coluna e CHAR(1) na origem, entao qualquer valor fora de A/R/N (inclusive
 * '' e " ") cai em null — que nao e perda de informacao, e o quarto estado
 * legitimo. O `trim` existe por causa do CHAR: espaco a mais nao pode virar um
 * quinto estado fantasma na tela.
 */
function toResultadoExame(value: unknown): ResultadoExame | null {
  if (typeof value !== "string") return null;
  const limpo = value.trim();
  return limpo === "A" || limpo === "R" || limpo === "N" ? limpo : null;
}

/**
 * As etapas ②③④⑤ lidas da producao, ou `null` quando o sync nunca passou.
 *
 * ESTE PARSE EXISTE PORQUE `getExam` MONTA A RESPOSTA CAMPO A CAMPO, e essa e a
 * licao que ele registra. Declarar `ExamDetalhe.producao` no tipo nao traz nada:
 * sem uma linha aqui o backend publica o objeto inteiro, o TypeScript continua
 * satisfeito (o campo e opcional, e opcional cobre ausente) e as quatro colunas
 * do kanban desenham "nao sincronizado" com o dado na mao. Foi exatamente o que
 * aconteceu na primeira integracao, em 21/09/2026, com o exame 02f0be5e: a API
 * respondia `producao` completo e a tela dizia que nada tinha sido sincronizado.
 * Campo novo no backend precisa de DUAS mudancas aqui — o tipo e a copia.
 *
 * `disponivel` e lido como booleano ESTRITO, e qualquer outra coisa vira false.
 * Etapa que chega sem ele e etapa que nao aconteceu: o pior desfecho possivel
 * nesta funcao seria desenhar uma pilula de resultado a partir de campo ausente,
 * porque a pilula e lida como decisao de uma instancia humana.
 *
 * Numero tolerante a string (`toNumeroOuNull`) porque `pontuacao_sustentada` e
 * `prioridade` sao inteiros do Postgres e `pontos` vem de NUMERIC em parte da
 * cadeia — e texto por `toTextoOuNull` porque '' na tela vira vazio mudo em vez
 * de "sem informacao".
 */
/**
 * "186" → "Art. 186". O resto passa intacto.
 *
 * O campo `codigo` da produção às vezes traz só o número (medido no parecer do
 * exame c17f8666, onde um item vem "186" e os outros três vêm "Art. 196"). Um
 * número solto no cabeçalho do cartão fica ao lado de "4 pts" e de "00:18" e
 * não se lê como artigo — vira mais um número na linha.
 */
function toRotuloDeArtigo(valor: string | null): string | null {
  if (valor === null) return null;
  return /^\d{1,3}(?:-[A-Za-z])?$/.test(valor) ? `Art. ${valor}` : valor;
}

/** "01:13" → 73. A produção grava mm:ss no jsonb do auditor e segundos na
 *  coluna do monitoramento; só aqui as duas viram a mesma coisa. */
function toSegundosDeMmSs(valor: unknown): number | null {
  if (typeof valor !== "string") return null;
  const casado = /^\s*(\d{1,3}):([0-5]\d)\s*$/.exec(valor);
  if (casado === null) return null;
  return Number(casado[1]) * 60 + Number(casado[2]);
}

/**
 * As infrações das TRÊS fontes, numa forma só.
 *
 * AS FORMAS NÃO SÃO IGUAIS, e é por isso que a normalização mora aqui e não no
 * componente. O monitoramento grava `regra_id`/`descricao`/`timestamp_s`; o
 * parecer do auditor grava `codigo`/`artigo_ctb`, `titulo`, `timestamp` em mm:ss
 * e ainda uma `justificativa` por falta. Na primeira integração a coluna ④ saiu
 * com quatro cartões idênticos dizendo "sem regra · grave · 4 pts" e nenhum
 * texto — o dado estava no jsonb, e a tela lia o vocabulário da outra fonte.
 *
 * Ensinar as duas formas ao cartão espalharia o problema: a próxima fonte
 * (a comissão, que grava `evidencia`) teria de ser ensinada de novo, em outro
 * arquivo. Aqui é um lugar só, e o componente continua com um vocabulário.
 */
function toInfracoesDaProducao(valor: unknown): InfracaoDaProducao[] {
  if (!Array.isArray(valor)) return [];
  return valor.filter(isRecord).map((item) => ({
    regra_id: toRotuloDeArtigo(
      toTextoOuNull(item.regra_id) ??
        toTextoOuNull(item.codigo) ??
        toTextoOuNull(item.artigo_ctb),
    ),
    gravidade: toTextoOuNull(item.gravidade),
    // `peso` é como o auditor chama o que o monitoramento chama de `pontos`.
    pontos: toNumeroOuNull(item.pontos) ?? toNumeroOuNull(item.peso),
    timestamp_s:
      toNumeroOuNull(item.timestamp_s) ?? toSegundosDeMmSs(item.timestamp),
    descricao:
      toTextoOuNull(item.descricao) ??
      toTextoOuNull(item.titulo) ??
      toTextoOuNull(item.evidencia),
    confianca: toNumeroOuNull(item.confianca),
    status: toTextoOuNull(item.status),
    justificativa: toTextoOuNull(item.justificativa),
    origem_adocao: toTextoOuNull(item.origem_adocao),
  }));
}

function toEtapaDaProducao(valor: unknown): {
  base: EtapaDaProducao;
  bruto: Record<string, unknown>;
} {
  const bruto = isRecord(valor) ? valor : {};
  return {
    base: {
      disponivel: bruto.disponivel === true,
      motivo: toTextoOuNull(bruto.motivo),
      resultado: toTextoOuNull(bruto.resultado),
    },
    bruto,
  };
}

function toProducao(valor: unknown): ProducaoDoExame | null {
  if (!isRecord(valor)) return null;

  const monitor = toEtapaDaProducao(valor.monitoramento);
  const comite = toEtapaDaProducao(valor.comite);
  const auditor = toEtapaDaProducao(valor.auditor);
  const supervisor = toEtapaDaProducao(valor.supervisor);
  const os = isRecord(valor.ordem_servico) ? valor.ordem_servico : {};

  return {
    sincronizado_em: toTextoOuNull(valor.sincronizado_em),
    // Só o valor conhecido passa; o resto (a API de antes) é o espelho.
    fonte: valor.fonte === "retorno_auditor" ? "retorno_auditor" : "espelho",
    monitoramento: {
      ...monitor.base,
      pontos: toNumeroOuNull(monitor.bruto.pontos),
      run_vigente: toNumeroOuNull(monitor.bruto.run_vigente),
      infracoes: toInfracoesDaProducao(monitor.bruto.infracoes),
    },
    comite: {
      ...comite.base,
      conclusao: toTextoOuNull(comite.bruto.conclusao),
      pontuacao_sustentada: toNumeroOuNull(comite.bruto.pontuacao_sustentada),
      total_laudos: toNumeroOuNull(comite.bruto.total_laudos),
      resultados_distintos: toNumeroOuNull(comite.bruto.resultados_distintos),
      fundamentacao: toTextoOuNull(comite.bruto.fundamentacao),
      recomendacao_para_auditor: toTextoOuNull(
        comite.bruto.recomendacao_para_auditor,
      ),
      infracoes: toInfracoesDaProducao(comite.bruto.infracoes),
      quando: toTextoOuNull(comite.bruto.quando),
    },
    auditor: {
      ...auditor.base,
      decisao: toTextoOuNull(auditor.bruto.decisao),
      justificativa: toTextoOuNull(auditor.bruto.justificativa),
      auditor: toTextoOuNull(auditor.bruto.auditor),
      quando: toTextoOuNull(auditor.bruto.quando),
      infracoes: toInfracoesDaProducao(auditor.bruto.infracoes),
    },
    supervisor: {
      ...supervisor.base,
      decisao: toTextoOuNull(supervisor.bruto.decisao),
      // `=== true` e nao truthy: este campo so aparece na tela quando e
      // verdadeiro (false em 941 das 942 decisoes medidas), entao um valor
      // estranho vindo da origem nao pode virar um "sim" na coluna ⑤.
      homologar_conduta: supervisor.bruto.homologar_conduta === true,
      justificativa: toTextoOuNull(supervisor.bruto.justificativa),
      supervisor: toTextoOuNull(supervisor.bruto.supervisor),
      quando: toTextoOuNull(supervisor.bruto.quando),
    },
    ordem_servico: {
      disponivel: os.disponivel === true,
      numero: toTextoOuNull(os.numero),
      status: toTextoOuNull(os.status),
      tipo_divergencia: toTextoOuNull(os.tipo_divergencia),
      prioridade: toNumeroOuNull(os.prioridade),
      criada_em: toTextoOuNull(os.criada_em),
      encerrada_em: toTextoOuNull(os.encerrada_em),
    },
  };
}

/**
 * Um exame, ou null quando a linha nao da para usar.
 *
 * `id` e `hash` sao os unicos campos exigidos: sem eles a linha nao tem key
 * estavel para a lista nem endereco para a futura tela de detalhe. O resto
 * degrada para null/"" em vez de derrubar a pagina inteira — uma coluna nova
 * (ou renomeada) no backend nao pode apagar 50 exames da tela.
 */
function toExam(raw: unknown): Exam | null {
  if (!isRecord(raw)) return null;
  const { id, hash } = raw;
  if (typeof id !== "string" || id === "") return null;
  if (typeof hash !== "string" || hash === "") return null;

  const duration_s = toNumeroOuNull(raw.duration_s);

  return {
    id,
    hash,
    /*
      Normalizado a mao e nao repassado cru: `etapas` nasceu depois do resto do
      contrato, entao um backend mais antigo simplesmente nao o manda — e um
      `undefined` vazando para `Object.keys()` derrubaria a linha inteira. Cada
      valor tem de ser string; uma etapa com valor estranho e descartada em vez
      de virar `[object Object]` no selo.
    */
    etapas: isRecord(raw.etapas)
      ? Object.fromEntries(
          Object.entries(raw.etapas).filter(
            ([, valor]) => typeof valor === "string",
          ) as Array<[string, string]>,
        )
      : {},
    external_id: toNumeroOuNull(raw.external_id),
    renach: toTextoOuNull(raw.renach),
    processo: toTextoOuNull(raw.processo),
    categoria: toTextoOuNull(raw.categoria),
    rubrica: typeof raw.rubrica === "string" ? raw.rubrica : "",
    candidato_nome: toTextoOuNull(raw.candidato_nome),
    candidato_cpf: toTextoOuNull(raw.candidato_cpf),
    examinador: toTextoOuNull(raw.examinador),
    local_unidade: toTextoOuNull(raw.local_unidade),
    veiculo: toTextoOuNull(raw.veiculo),
    auto_escola: toTextoOuNull(raw.auto_escola),
    resultado_exame: toResultadoExame(raw.resultado_exame),
    pontuacao_oficial: toNumeroOuNull(raw.pontuacao_oficial),
    // CONTAGEM, e nao medida: nunca null e nunca undefined, porque a celula de
    // parecer decide o que exibir comparando este numero com zero — e
    // `undefined > 0` daria false por acidente, nao por dado. Truncado e preso
    // em zero: e um jsonb_array_length do outro lado, entao fracao ou negativo
    // so poderiam vir de payload corrompido, e "0,5 infracao" na tela e pior do
    // que o zero. Ausencia do campo (backend antigo) tambem cai em 0 — a tela
    // degrada para "sem falta anotada", nunca para NaN.
    infracoes_oficiais: Math.max(
      0,
      Math.trunc(toNumeroOuNull(raw.infracoes_oficiais) ?? 0),
    ),
    gs_video: toTextoOuNull(raw.gs_video),
    duration_s,
    // Prefere o que o backend calculou, mas cai no INVARIANTE quando o campo nao
    // vem: video existe <=> duration_s preenchida. O fallback nunca olha
    // gs_video (que e intencao de upload) nem status.
    video_disponivel:
      typeof raw.video_disponivel === "boolean"
        ? raw.video_disponivel
        : duration_s !== null,
    status: typeof raw.status === "string" ? raw.status : "",
    created_at: typeof raw.created_at === "string" ? raw.created_at : "",
    updated_at: typeof raw.updated_at === "string" ? raw.updated_at : "",
  };
}

/**
 * Cursor da resposta, ou null.
 *
 * INTEIRO ou NADA: um objeto com so uma das duas partes vira null aqui. Guardar
 * `{valor, id: ""}` seria pior do que nao ter cursor nenhum — a tela o mandaria
 * de volta no proximo pedido e receberia 422, transformando "acabou a lista" em
 * uma faixa de erro.
 */
function toExamCursor(raw: unknown): ExamCursor | null {
  if (!isRecord(raw)) return null;
  const valor = typeof raw.valor === "string" ? raw.valor.trim() : "";
  const id = typeof raw.id === "string" ? raw.id.trim() : "";
  if (valor === "" || id === "") return null;
  return { valor, id };
}

/**
 * Pagina de exames.
 *
 * `total` vem do COUNT do backend e NAO de items.length: items pode vir menor
 * (linhas descartadas por toExam) e, mesmo integro, e so a fatia da pagina —
 * deduzir o tamanho dele daria "100 exames" sobre ~8,4 mil.
 *
 * `proximo_cursor` tambem e do backend e NAO se deriva da ultima linha de
 * `items` pelo mesmo motivo, so que mais grave: se a ultima linha da pagina for
 * justamente uma das descartadas por toExam, a ancora derivada apontaria para a
 * PENULTIMA e a proxima carga devolveria de novo uma linha ja entregue.
 */
/** Contagem da listagem: inteiro >= 0, ou null (nao ha contagem, nao e zero). */
function toContagemOuNull(value: unknown): number | null {
  const numero = toNumeroOuNull(value);
  return numero === null ? null : Math.max(0, Math.trunc(numero));
}

/** Tri-estado da divergencia: so `true`/`false` ESTRITOS; o resto e null. */
function toDivergencia(value: unknown): boolean | null {
  return value === true || value === false ? value : null;
}

/** Lista de textos, ou null: item que nao e texto e descartado, e nao vira "[object Object]". */
function toTextosOuNull(value: unknown): string[] | null {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : null;
}

/**
 * O bloco de compliance do registro. Sem `estado` texto nao ha bloco: e o
 * campo que diz se ha triagem para mostrar, e adivinha-lo seria inventar um
 * "pronto".
 */
function toComplianceNaListagem(raw: unknown): ComplianceNaListagem | null {
  if (!isRecord(raw) || typeof raw.estado !== "string") return null;
  return {
    estado: raw.estado,
    estado_principal: toTextoOuNull(raw.estado_principal),
    estados: toTextosOuNull(raw.estados),
    n_alertas: toContagemOuNull(raw.n_alertas),
    score: toNumeroOuNull(raw.score),
    faixa: toTextoOuNull(raw.faixa),
    destino: toTextoOuNull(raw.destino),
    eixos: toEixosDaTriagem(raw.eixos),
    assinatura_regras: toTextoOuNull(raw.assinatura_regras),
    // Tri-estado, como a divergencia: so o booleano ESTRITO afirma alguma
    // coisa sobre a regua.
    regua_vigente: toDivergencia(raw.regua_vigente),
    atualizado_em: toTextoOuNull(raw.atualizado_em),
  };
}

/**
 * Os eixos da AD-5, ou null. So os eixos que o vocabulario conhece, e so com
 * o destino texto: eixo sem destino nao foi triado, e nao vira "sem alerta".
 */
function toEixosDaTriagem(raw: unknown): EixosDaTriagem | null {
  if (!isRecord(raw)) return null;
  const saida: EixosDaTriagem = {};
  for (const eixo of EIXOS_DA_TRIAGEM) {
    const bruto = raw[eixo];
    if (!isRecord(bruto)) continue;
    const destino = toTextoOuNull(bruto.destino);
    if (destino === null) continue;
    saida[eixo] = { score: toNumeroOuNull(bruto.score), destino };
  }
  return Object.keys(saida).length > 0 ? saida : null;
}

/**
 * Os indicadores da IA, ou null. Os contadores são todos obrigatórios: um que
 * faltasse viraria um "0" na faixa, e zero é um resultado — sem eles a faixa
 * não é desenhada, em vez de afirmar o que não chegou. (O aviso "não lido" é
 * o da leitura que caiu no backend: `indicadores` em `leituras_indisponiveis`.)
 */
function toIndicadoresDaIa(raw: unknown): IndicadoresDaIa | null {
  if (!isRecord(raw)) return null;
  const campos = [
    "exames",
    "processados",
    "a_processar",
    "sem_veredito_do_examinador",
    "aprovado",
    "reprovado",
    "inapto",
    "nao_calculavel",
    "comparaveis_com_examinador",
    "divergem_do_examinador",
    "examinador_aprovou_ia_reprovou",
    "examinador_aprovou_ia_inapto",
    "examinador_reprovou_ia_aprovou",
    "infracoes",
    "exames_com_infracao",
    "infracoes_do_examinador",
    "exames_com_infracao_do_examinador",
  ] as const;
  const contagens: Partial<Record<(typeof campos)[number], number>> = {};
  for (const campo of campos) {
    const valor = toContagemOuNull(raw[campo]);
    if (valor === null) return null;
    contagens[campo] = valor;
  }
  // Os dois da categoria A (29/10/2026) são a exceção ao "obrigatório": o
  // backend de antes não os manda porque não contava exame A nenhum, e o zero
  // dele é verdade. Valor fora do tipo também vira zero, e não derruba a faixa.
  const nao_conclusivo = toContagemOuNull(raw.nao_conclusivo) ?? 0;
  const barrado_compliance = toContagemOuNull(raw.barrado_compliance) ?? 0;
  const mais_apontadas = Array.isArray(raw.mais_apontadas)
    ? raw.mais_apontadas.flatMap((item): InfracaoMaisApontada[] => {
        if (!isRecord(item)) return [];
        const rotulo = toTextoOuNull(item.rotulo);
        const ocorrencias = toContagemOuNull(item.ocorrencias);
        if (rotulo === null || ocorrencias === null) return [];
        return [
          {
            artigo: toTextoOuNull(item.artigo),
            id_regra: toTextoOuNull(item.id_regra),
            rotulo,
            descricao: toTextoOuNull(item.descricao),
            ocorrencias,
            exames: toContagemOuNull(item.exames) ?? 0,
            ocorrencias_do_examinador: toContagemOuNull(item.ocorrencias_do_examinador),
            exames_do_examinador: toContagemOuNull(item.exames_do_examinador),
          },
        ];
      })
    : [];
  return {
    ...(contagens as Record<(typeof campos)[number], number>),
    nao_conclusivo,
    barrado_compliance,
    mais_apontadas,
    custo: toCustoDoRecorte(raw.custo),
  };
}

/**
 * O custo do recorte, ou null. Como nos contadores, o que falta NÃO vira zero
 * ("US$ 0" é um resultado): sem total ou sem contagem, o cartão diz que o
 * custo não foi lido. `medio_usd` null é legítimo (nenhum processado).
 */
function toCustoDoRecorte(raw: unknown): CustoDoRecorte | null {
  if (!isRecord(raw)) return null;
  const total_usd = toNumeroOuNull(raw.total_usd);
  const exames = toContagemOuNull(raw.exames);
  const exames_com_piso = toContagemOuNull(raw.exames_com_piso);
  if (total_usd === null || exames === null || exames_com_piso === null) return null;
  return { total_usd, medio_usd: toNumeroOuNull(raw.medio_usd), exames, exames_com_piso };
}

/**
 * Uma linha da listagem: o `Exam` e os pareceres lado a lado.
 *
 * Cada bloco novo degrada para "sem dado" (null) e NUNCA para zero: um backend
 * que ainda nao manda `supervisor` nao pode desenhar "0 infracoes" numa
 * coluna, porque zero e um parecer e ausencia nao e. Mesma licao de
 * `toProducao`: campo que nao passa por aqui chega descartado em silencio.
 */
function toExamListItem(raw: unknown): ExamListItem | null {
  const exame = toExam(raw);
  if (exame === null || !isRecord(raw)) return null;

  const sup = isRecord(raw.supervisor) ? raw.supervisor : null;
  const aud = isRecord(raw.auditor) ? raw.auditor : null;
  const ia = isRecord(raw.nossa_ia) ? raw.nossa_ia : null;
  const tel = isRecord(raw.telemetria) ? raw.telemetria : null;
  const proc = isRecord(raw.processamento) ? raw.processamento : null;
  const div = isRecord(raw.divergencias) ? raw.divergencias : {};

  return {
    ...exame,
    supervisor:
      sup === null
        ? null
        : {
            resultado: toTextoOuNull(sup.resultado)?.toLowerCase() ?? null,
            decisao: toTextoOuNull(sup.decisao)?.toLowerCase() ?? null,
            infracoes: toContagemOuNull(sup.infracoes),
            pontos: toNumeroOuNull(sup.pontos),
          },
    // Ausente (o backend de antes de 14/10/2026) e null: "sem parecer", e
    // nunca um auditor com zero infracoes.
    auditor:
      aud === null
        ? null
        : {
            resultado: toTextoOuNull(aud.resultado)?.toLowerCase() ?? null,
            infracoes: toContagemOuNull(aud.infracoes),
            pontos: toNumeroOuNull(aud.pontos),
          },
    nossa_ia:
      ia === null || typeof ia.estado !== "string"
        ? null
        : {
            estado: ia.estado,
            resultado: toTextoOuNull(ia.resultado),
            infracoes: toContagemOuNull(ia.infracoes),
            pontos: toNumeroOuNull(ia.pontos),
            regua_desatualizada: ia.regua_desatualizada === true,
            cobertura_lidas: toContagemOuNull(ia.cobertura_lidas),
            cobertura_em_jogo: toContagemOuNull(ia.cobertura_em_jogo),
          },
    compliance: toComplianceNaListagem(raw.compliance),
    // Ausente e null ("nao se sabe"), e nao false: `{gps: false}` afirma que
    // o payload nao traz telemetria, e isso so o servidor pode dizer.
    telemetria:
      tel === null
        ? null
        : { gps: tel.gps === true, sensores: tel.sensores === true },
    processamento:
      proc === null
        ? null
        : {
            custo_usd: toNumeroOuNull(proc.custo_usd),
            // `!== false`: so um false EXPLICITO rebaixa a soma a piso.
            custo_completo: proc.custo_completo !== false,
            tempo_ocupado_s: toNumeroOuNull(proc.tempo_ocupado_s),
            tempo_soma_s: toNumeroOuNull(proc.tempo_soma_s),
            etapas_sem_relogio: toContagemOuNull(proc.etapas_sem_relogio) ?? 0,
            banco_bytes: toNumeroOuNull(proc.banco_bytes),
            disco_bytes: toNumeroOuNull(proc.disco_bytes),
            armazenamento_bytes: toNumeroOuNull(proc.armazenamento_bytes),
          },
    divergencias: {
      supervisor_examinador: toDivergencia(div.supervisor_examinador),
      ia_examinador: toDivergencia(div.ia_examinador),
      ia_supervisor: toDivergencia(div.ia_supervisor),
      auditor_examinador: toDivergencia(div.auditor_examinador),
      ia_auditor: toDivergencia(div.ia_auditor),
    },
    esteira: toEsteiraNaListagem(raw.esteira),
    ia_categoria_a: toIaCategoriaANaListagem(raw.ia_categoria_a),
  };
}

/**
 * A IA da categoria A, ou null. Os booleanos só com o true EXPLÍCITO: um
 * `barrado` adivinhado diria "sem veredito" de um exame que a IA leu.
 */
function toIaCategoriaANaListagem(raw: unknown): IaCategoriaANaListagem | null {
  if (!isRecord(raw)) return null;
  const v = isRecord(raw.veredito) ? raw.veredito : null;
  const rotulo = v === null ? null : toTextoOuNull(v.rotulo);
  const c = isRecord(raw.compliance) ? raw.compliance : null;
  return {
    estado: toTextoOuNull(raw.estado),
    veredito:
      v === null || rotulo === null
        ? null
        : {
            rotulo,
            pontos: toNumeroOuNull(v.pontos),
            eliminatorias: toContagemOuNull(v.eliminatorias),
            n_infracoes: toContagemOuNull(v.n_infracoes),
          },
    desligada: raw.desligada === true,
    compliance:
      c === null
        ? null
        : {
            estado: toTextoOuNull(c.estado),
            decisao: toTextoOuNull(c.decisao),
            modo: toTextoOuNull(c.modo),
            indice: toNumeroOuNull(c.indice),
            indice_minimo: toNumeroOuNull(c.indice_minimo),
            bloqueia: c.bloqueia === true,
            teria_bloqueado: c.teria_bloqueado === true,
            motivos: Array.isArray(c.motivos)
              ? c.motivos.flatMap((m): MotivoDoComplianceA[] => {
                  if (!isRecord(m)) return [];
                  const codigo = toTextoOuNull(m.codigo);
                  const frase = toTextoOuNull(m.frase);
                  return codigo === null || frase === null ? [] : [{ codigo, frase }];
                })
              : [],
          },
    barrado: raw.barrado === true,
  };
}

/** A última execução do "Rodar tudo", ou null sem id e estado legíveis. */
function toEsteiraNaListagem(raw: unknown): EsteiraNaListagem | null {
  if (!isRecord(raw)) return null;
  const id = toNumeroOuNull(raw.execucao_id);
  const estado = toTextoOuNull(raw.estado);
  if (id === null || estado === null) return null;
  return {
    execucao_id: id,
    estado,
    motivo: toTextoOuNull(raw.motivo),
    // `=== true`: só um true EXPLÍCITO afirma o "parado para análise".
    para_analise: raw.para_analise === true,
    retomadas_auto: toContagemOuNull(raw.retomadas_auto) ?? 0,
    n_falhas: toContagemOuNull(raw.n_falhas) ?? 0,
    criado_em: toTextoOuNull(raw.criado_em),
    terminado_em: toTextoOuNull(raw.terminado_em),
  };
}

function toExamPage(raw: unknown, perPagePedido: number): ExamPage {
  // Anotado: sem o tipo explicito, o `{}` do else estreita a uniao e o acesso a
  // `bruto.items` vira erro de compilacao.
  const bruto: Record<string, unknown> = isRecord(raw) ? raw : {};
  const items = Array.isArray(bruto.items)
    ? bruto.items
        .map(toExamListItem)
        .filter((exam): exam is ExamListItem => exam !== null)
    : [];

  const total = toNumeroOuNull(bruto.total) ?? items.length;
  // Sem per_page na resposta, vale o que foi pedido; sem nada pedido, o
  // tamanho da fatia que chegou.
  const per_page =
    toNumeroOuNull(bruto.per_page) ??
    (perPagePedido > 0 ? perPagePedido : items.length);

  return {
    items,
    total,
    per_page,
    proximo_cursor: toExamCursor(bruto.proximo_cursor),
    leituras_indisponiveis: Array.isArray(bruto.leituras_indisponiveis)
      ? bruto.leituras_indisponiveis.filter(
          (campo): campo is string => typeof campo === "string",
        )
      : [],
    indicadores: toIndicadoresDaIa(bruto.indicadores),
  };
}

function toSyncStatus(raw: unknown): SyncStatus {
  if (!isRecord(raw)) {
    throw new ApiError("Resposta de sync em formato inesperado.", 500, API_BASE_URL);
  }
  const execucao = isRecord(raw.ultima_execucao) ? raw.ultima_execucao : null;

  return {
    ultimo_updated_at: toTextoOuNull(raw.ultimo_updated_at),
    atualizado_em: toTextoOuNull(raw.atualizado_em),
    total_exames: toNumeroOuNull(raw.total_exames) ?? 0,
    // Mesma defesa dos demais textos: '' vira null. Um escopo em branco na tela
    // ("exames criados a partir de ") seria pior do que nao mostrar escopo.
    criado_desde: toTextoOuNull(raw.criado_desde),
    // null aqui NAO e erro: e o portal que ainda nao rodou o sync nenhuma vez.
    // Quem exibe precisa distinguir isso de "rodou e falhou" (erro preenchido).
    ultima_execucao:
      execucao === null
        ? null
        : {
            terminado_em: toTextoOuNull(execucao.terminado_em),
            lidos: toNumeroOuNull(execucao.lidos) ?? 0,
            inseridos: toNumeroOuNull(execucao.inseridos) ?? 0,
            atualizados: toNumeroOuNull(execucao.atualizados) ?? 0,
            modo: typeof execucao.modo === "string" ? execucao.modo : "",
            erro: toTextoOuNull(execucao.erro),
          },
  };
}

/* -------------------------------------------------------------------------- */
/* Endpoints                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Mensagem de erro pronta para a tela, a partir de qualquer coisa lancada.
 *
 * Vive aqui (e nao em cada page) porque so este modulo sabe o que e um ApiError
 * e qual e a API_BASE_URL que aparece na dica de diagnostico.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    return error.isNetworkError
      ? `${error.message} Verifique se o backend esta no ar em ${API_BASE_URL}.`
      : `${error.message} (HTTP ${error.status})`;
  }
  if (error instanceof Error) return error.message;
  return "Erro desconhecido.";
}

/**
 * Autentica e abre a sessao.
 *
 * O token NAO volta no corpo da resposta e nao existe em lugar nenhum que o JS
 * alcance: ele vai num cookie HttpOnly que o browser guarda e reenvia sozinho.
 * Por isso nao ha nada a "salvar" aqui — e por isso o e-mail de quem entrou vem
 * de getMe(), nao de decodificar o token.
 */
export async function login(
  credentials: Credentials,
  signal?: AbortSignal,
): Promise<Session> {
  const raw = await request<unknown>("/auth/login", {
    method: "POST",
    body: credentials,
    signal,
    // O argon2 gasta ~50ms de proposito (encarecer forca bruta). Somado a
    // latencia do Caddy de borda, o default de 10s ainda sobra — mas o login e
    // a unica chamada com custo de CPU do lado do servidor, entao vale margem.
    timeoutMs: 15_000,
  });
  if (!isRecord(raw)) {
    throw new ApiError("Resposta de login em formato inesperado.", 500, API_BASE_URL);
  }
  return {
    user: toUserOrThrow(raw.user),
    expires_in: typeof raw.expires_in === "number" ? raw.expires_in : 0,
  };
}

/**
 * Encerra a sessao (apaga os cookies no servidor). Nunca lanca — devolve se
 * conseguiu.
 *
 * O booleano importa: os cookies sao `HttpOnly`, entao o JS NAO consegue
 * apaga-los por conta propria. Se esta chamada falhar, o cookie de gate continua
 * la, e navegar para /login faria o proxy do Next devolver o usuario para "/" —
 * que e o loop de redirect que `sessaoExpirada` existe para evitar. Quem chama
 * precisa saber que nao deu certo para mostrar um erro em vez de navegar.
 */
export async function logout(signal?: AbortSignal): Promise<boolean> {
  try {
    await request<unknown>("/auth/logout", { method: "POST", signal });
    return true;
  } catch {
    return false;
  }
}

/** Usuario da sessao atual. Lanca ApiError 401 quando nao ha sessao valida. */
export async function getMe(signal?: AbortSignal): Promise<User> {
  return toUserOrThrow(await request<unknown>("/auth/me", { signal }));
}

export async function getHealth(signal?: AbortSignal): Promise<Health> {
  const raw = await request<unknown>("/health", { signal, timeoutMs: 8_000 });
  if (!isRecord(raw)) {
    return { status: typeof raw === "string" ? raw : "ok" };
  }
  return {
    status: typeof raw.status === "string" ? raw.status : "ok",
    database: typeof raw.database === "string" ? raw.database : null,
    version: typeof raw.version === "string" ? raw.version : null,
  };
}

/* ----------------------------- Exames ------------------------------------- */

/**
 * Query string dos filtros.
 *
 * Duas regras que parecem detalhe e nao sao:
 *
 * 1. `status` e `resultado` sao REPETIVEIS — um append por valor
 *    (status=queued&status=failed). E assim que o FastAPI monta um
 *    `List[str]` de query param. Serializar como CSV ("status=queued,failed")
 *    devolve 422 com uma mensagem sobre o tipo do campo, que nao diz em lugar
 *    nenhum que o problema foi a virgula.
 * 2. Filtro vazio NAO vai. Um `q=` em branco vira `ILIKE '%%'` no backend: casa
 *    com tudo, nao filtra nada e ainda descarta o indice, pagando um seq scan
 *    nas ~8.370 linhas a cada tecla.
 */
function buildExamQuery(filtros: ExamFilters): string {
  const params = new URLSearchParams();

  const inteiro = (chave: string, valor: number | undefined) => {
    if (typeof valor !== "number" || !Number.isFinite(valor) || valor <= 0) return;
    params.set(chave, String(Math.trunc(valor)));
  };

  inteiro("per_page", filtros.per_page);

  // Os DOIS ou NENHUM. Enviar so um deles e 422 no backend, entao nao existe
  // caminho em que metade do cursor vire uma consulta — o "meio cursor" morre
  // aqui, antes de virar requisicao.
  const cursor = filtros.cursor;
  if (cursor && cursor.valor !== "" && cursor.id !== "") {
    params.set("cursor_valor", cursor.valor);
    params.set("cursor_id", cursor.id);
  }

  const q = filtros.q?.trim();
  if (q) params.set("q", q);

  for (const status of filtros.status ?? []) {
    const valor = status.trim();
    if (valor) params.append("status", valor);
  }
  for (const resultado of filtros.resultado ?? []) {
    const valor = resultado.trim();
    if (valor) params.append("resultado", valor);
  }
  // `append` e nao `set`: o parametro REPETE, e e assim que o FastAPI monta a
  // lista do outro lado. Com `set`, a segunda opcao apagaria a primeira e o
  // filtro multiplo viraria um filtro simples em silencio.
  for (const etapa of filtros.etapa_audio ?? []) {
    const valor = etapa.trim();
    if (valor) params.append("etapa_audio", valor);
  }
  // A categoria tambem repete (`append`), como `status`.
  for (const categoria of filtros.categoria ?? []) {
    const valor = categoria.trim();
    if (valor) params.append("categoria", valor);
  }
  // A UF tambem repete (`append`), como a categoria.
  for (const uf of filtros.uf ?? []) {
    const valor = uf.trim();
    if (valor) params.append("uf", valor);
  }

  // `false` e um filtro legitimo ("so os exames SEM video"), entao o teste e
  // pelo tipo e nao pela veracidade — `if (filtros.com_video)` perderia o caso.
  if (typeof filtros.com_video === "boolean") {
    params.set("com_video", String(filtros.com_video));
  }
  // Repetivel, como `etapa_audio`: `com` e `sem` juntos somam os recortes.
  for (const opcao of filtros.telemetria ?? []) {
    const valor = opcao.trim();
    if (valor) params.append("telemetria", valor);
  }
  // As divergencias sao filtros PROPRIOS, cada uma um booleano: pelo tipo,
  // e nao pela veracidade, pelo mesmo motivo do `com_video` (false = concorda).
  const divergencias = [
    ["supervisor_diverge_examinador", filtros.supervisor_diverge_examinador],
    ["ia_diverge_examinador", filtros.ia_diverge_examinador],
    ["ia_diverge_supervisor", filtros.ia_diverge_supervisor],
    ["auditor_diverge_examinador", filtros.auditor_diverge_examinador],
    ["ia_diverge_auditor", filtros.ia_diverge_auditor],
  ] as const;
  for (const [chave, diverge] of divergencias) {
    if (typeof diverge === "boolean") params.set(chave, String(diverge));
  }
  // A Fila do supervisor: estado e destino REPETEM (append), como
  // `etapa_audio`; a data e uma so.
  for (const estado of filtros.compliance_estado ?? []) {
    const valor = estado.trim();
    if (valor) params.append("compliance_estado", valor);
  }
  for (const destino of filtros.compliance_destino ?? []) {
    const valor = destino.trim();
    if (valor) params.append("compliance_destino", valor);
  }
  const desde = filtros.compliance_desde?.trim();
  if (desde) params.set("compliance_desde", desde);
  // A última execução da esteira (P6): repete, como `etapa_audio`.
  for (const opcao of filtros.esteira ?? []) {
    const valor = opcao.trim();
    if (valor) params.append("esteira", valor);
  }
  // O veredito da IA Valbot: um valor so (`set`, nao `append`).
  if (filtros.ia_valbot) params.set("ia_valbot", filtros.ia_valbot);
  // O retorno do auditor: um valor so, como a IA Valbot.
  if (filtros.retorno_auditor) params.set("retorno_auditor", filtros.retorno_auditor);
  if (filtros.laudo) params.set("laudo", filtros.laudo);
  // As duas datas da tela (do exame e do veredito da IA Valbot): instantes
  // ISO, um so por parametro, como `compliance_desde`. O `*_ate` ja chega
  // EXCLUSIVO (o comeco do dia seguinte) — o intervalo e semi-aberto do outro
  // lado tambem.
  const datas = [
    ["exame_desde", filtros.exame_desde],
    ["exame_ate", filtros.exame_ate],
    ["ia_processado_desde", filtros.ia_processado_desde],
    ["ia_processado_ate", filtros.ia_processado_ate],
  ] as const;
  for (const [chave, instante] of datas) {
    const valor = instante?.trim();
    if (valor) params.set(chave, valor);
  }
  // `typeof === "number"` e nao `if (filtros.external_id)`: existe external_id 0
  // valido em tese, e a versao curta o descartaria em silencio. Finite barra
  // NaN/Infinity, que virariam "NaN" na query string e 422 no backend.
  if (
    typeof filtros.external_id === "number" &&
    Number.isFinite(filtros.external_id)
  ) {
    params.set("external_id", String(filtros.external_id));
  }
  if (filtros.ordem) params.set("ordem", filtros.ordem);
  if (filtros.direcao) params.set("direcao", filtros.direcao);

  const query = params.toString();
  return query === "" ? "" : `?${query}`;
}

/**
 * Uma pagina de exames do portal (copia local), com filtros e busca.
 *
 * Sem `filtros.cursor` e a primeira pagina do recorte; com ele, a continuacao
 * ancorada na ultima linha ja entregue. Trocar busca ou filtro OBRIGA a soltar o
 * cursor: ele e a posicao dentro de UM recorte e nao significa nada em outro.
 *
 * O `signal` e para cancelar a busca anterior quando o usuario continua
 * digitando: sem ele, respostas fora de ordem pintam a tela com o resultado de
 * um termo antigo.
 */
export async function listExams(
  filtros: ExamFilters = {},
  signal?: AbortSignal,
): Promise<ExamPage> {
  const raw = await request<unknown>(`/exams${buildExamQuery(filtros)}`, {
    signal,
    // Folga sobre o default de 10s: com `q` preenchido a consulta vira ILIKE
    // sobre as ~8.370 linhas copiadas, e o COUNT da paginacao anda junto.
    timeoutMs: 20_000,
  });
  return toExamPage(raw, filtros.per_page ?? 0);
}

/**
 * UM exame, pelo `hash`.
 *
 * E `hash` e nao `id` porque essa e a chave natural do exame (UNIQUE na origem)
 * e e ela que as funcoes de escrita da producao recebem — a URL de hoje ja e a
 * chave do dia em que houver escrita de volta.
 *
 * 404 AQUI NAO E "erro generico": o espelho e PARCIAL de proposito (piso de
 * 01/09/2026, 8.407 das ~65.000 linhas da producao), entao "hash desconhecido" e
 * quase sempre "exame fora do recorte copiado", nao "exame que nao existe". Quem
 * chama distingue os dois casos por `error instanceof ApiError && error.status
 * === 404` — o status ja viaja no ApiError, entao nao ha tipo de erro novo a
 * inventar.
 *
 * Sem timeout proprio: o default de 10 s cobre com folga a resposta mais pesada
 * medida no espelho (129 KB somando os dois JSONB; media de ~10 KB).
 */
export async function getExam(
  hash: string,
  signal?: AbortSignal,
): Promise<ExamDetalhe> {
  // encodeURIComponent mesmo o hash sendo hexadecimal: o valor chega do segmento
  // de rota do Next e amanha pode vir de outro lugar (link colado, busca). Sem
  // escapar, uma '/' ou um '?' dentro do valor formam OUTRA URL — e a falha
  // aparece como um 404 confuso, nao como "identificador invalido". Custa nada.
  const raw = await request<unknown>(`/exams/${encodeURIComponent(hash)}`, {
    signal,
  });

  if (!isRecord(raw)) {
    throw new ApiError("Resposta da API em formato inesperado.", 500, API_BASE_URL);
  }

  // A MESMA toExam da listagem, e nao uma normalizacao propria: `resultado_exame`
  // com quatro estados, `duration_s` que pode chegar em string (NUMERIC) e
  // `infracoes_oficiais` preso em inteiro >= 0 sao regras de leitura do shape
  // Exam, nao da tela. Duas copias delas concordam so ate alguem corrigir uma.
  const exame = toExam(raw);
  if (exame === null) {
    // Mesmo criterio da listagem: sem `id` ou `hash` a linha nao tem identidade.
    // La ela e descartada (uma linha a menos na tabela); aqui ela E a resposta,
    // entao o unico desfecho honesto e o erro.
    throw new ApiError(
      "Exame em formato inesperado (sem id ou hash).",
      500,
      API_BASE_URL,
    );
  }

  return {
    ...exame,
    // `?? null` para que os campos EXISTAM sempre, mesmo que o backend omita um
    // deles: assim a tela testa valor (`=== null`) e nunca a presenca da chave.
    // `??` e nao `||` de proposito — array vazio e 0 sao conteudo legitimo aqui.
    training_annotations: raw.training_annotations ?? null,
    ingest_payload_raw: raw.ingest_payload_raw ?? null,
    // `null` aqui quer dizer "o sync nunca passou por este exame", e e diferente
    // de cada etapa vir com `disponivel: false` (o sync passou e nao havia
    // parecer). As duas frases que o kanban escreve sao opostas, entao esta
    // distincao nao pode se perder na leitura.
    producao: toProducao(raw.producao),
    // String ou null, nunca outra coisa: o campo nasceu depois do resto do
    // contrato, entao um backend mais antigo simplesmente nao o manda — e um
    // `undefined` vazando para a tela apareceria como "Invalid Date".
    sincronizado_em:
      typeof raw.sincronizado_em === "string" ? raw.sincronizado_em : null,
  };
}

/**
 * Ate onde a copia local esta em dia com a producao.
 *
 * Existe porque a tela mostra dados COPIADOS, nao ao vivo: sem o instante do
 * ultimo sync, um exame ausente e indistinguivel de um exame que ainda nao foi
 * copiado.
 */
export async function getSyncStatus(signal?: AbortSignal): Promise<SyncStatus> {
  return toSyncStatus(await request<unknown>("/exams/sync-status", { signal }));
}

/* -------------------------------------------------------------------------- */
/* Configuracao da extracao de audio                                          */
/* -------------------------------------------------------------------------- */

/**
 * O que roda no audio de um exame, por categoria de habilitacao.
 *
 * `configurada: false` NAO e o mesmo que "igual ao padrao": significa que
 * ninguem salvou esta categoria ainda, e que o que se ve na tela e o padrao de
 * fabrica. A diferenca importa no futuro — uma categoria nao configurada
 * acompanha uma mudanca de padrao, uma salva fica congelada no valor do dia.
 */
export interface ConfigAudioCategoria {
  categoria: string;
  rotulo: string;
  modelo: string;
  /** 0 = audio inteiro, uma chamada so. */
  janela_s: number;
  overlap_s: number;
  prompt_sistema: string;
  configurada: boolean;
  atualizado_em: string | null;
  atualizado_por: string | null;
  prompt_e_padrao: boolean;
  /**
   * O prompt de fábrica DESTA categoria (22/10/2026: C, D e E têm o seu).
   * Ausente num backend anterior: a tela restaura só a B (e a A) com o
   * `prompt_padrao` geral e desliga o "Restaurar" das cópias.
   */
  prompt_padrao?: string;
}

export interface OpcaoModelo {
  id: string;
  rotulo: string;
  nota: string;
}

export interface OpcaoJanela {
  valor: number;
  rotulo: string;
  nota: string;
}

export interface EtapaDoProcesso {
  chave: string;
  titulo: string;
  resumo: string;
  itens: string[];
}

export interface ConfigAudio {
  categorias: ConfigAudioCategoria[];
  modelos: OpcaoModelo[];
  janelas: OpcaoJanela[];
  prompt_padrao: string;
  processo: EtapaDoProcesso[];
  limites: string[];
}

/** Tudo que o modal de Config precisa, numa chamada so. */
export async function getConfigAudio(signal?: AbortSignal): Promise<ConfigAudio> {
  return request<ConfigAudio>("/config/audio", { signal });
}

/**
 * Salva a TABELA INTEIRA e devolve o estado que passou a valer.
 *
 * A tabela inteira e nao uma categoria por vez: e assim que a tela e lida e
 * salva, e salvar em partes abriria a janela em que metade das categorias ja
 * roda a configuracao nova e a outra metade ainda nao.
 */
export async function salvarConfigAudio(
  categorias: Array<
    Pick<
      ConfigAudioCategoria,
      "categoria" | "modelo" | "janela_s" | "overlap_s" | "prompt_sistema"
    >
  >,
  signal?: AbortSignal,
): Promise<ConfigAudio> {
  return request<ConfigAudio>("/config/audio", {
    method: "PUT",
    body: { categorias },
    signal,
    // O prompt de cada categoria viaja inteiro (quase 4 KB cada). Com as cinco
    // categorias o corpo passa de 20 KB, e o default de 10 s fica apertado numa
    // conexao ruim.
    timeoutMs: 20_000,
  });
}

/** A configuracao que vale para UM exame. Categoria desconhecida cai no padrao. */
export async function getConfigAudioDaCategoria(
  categoria: string,
  signal?: AbortSignal,
): Promise<ConfigAudioCategoria> {
  return request<ConfigAudioCategoria>(
    `/config/audio/exame/${encodeURIComponent(categoria || "-")}`,
    { signal },
  );
}

/* -------------------------------------------------------------------------- */
/* Processamento do audio de um exame                                         */
/* -------------------------------------------------------------------------- */

/**
 * O ESTADO DE UM INSTANTE. Espelha `app/audio/ancoragem.py`.
 *
 * O relógio do Gemini DERIVA dentro da janela de 60 s: acerta na cabeça e vai
 * antecipando o evento conforme a janela avança. Por isso o instante publicado
 * é medido de novo, no sinal, por um ASR local — e por isso ele vem com um
 * estado, que a tela é obrigada a mostrar.
 *
 *   MEDIDA     o instante saiu do sinal: um trecho do ASR casa com este texto.
 *   ESTIMADA   saiu do ajuste da deriva daquela janela, sustentado por âncoras
 *              VIZINHAS e não por este evento. É o melhor que um som consegue —
 *              som não tem texto para casar. NUNCA equivale a MEDIDA.
 *   NAO_MEDIDA não há instante medido. `inicio_s` é o que o modelo escreveu, e
 *              ele errou em média 4,86 s e até 13,50 s contra os 25 pontos de
 *              gabarito humano de 20/09/2026. Quem consome está PROIBIDO de
 *              criar vínculo temporal com este item.
 */
export type EstadoAncoragem = "MEDIDA" | "ESTIMADA" | "NAO_MEDIDA";

/** De onde veio o instante que está em `inicio_s`. */
export type FonteDoInstante = "asr" | "ajuste_da_janela" | "modelo";

/** A âncora de um item — `app/audio/execucao.py:_com_ancora`. */
export interface Ancoragem {
  estado: EstadoAncoragem;
  /**
   * 0..1. Em MEDIDA é o casamento aceito; em NAO_MEDIDA é o do melhor
   * candidato REJEITADO — ler esse número como qualidade seria ler ao contrário.
   * Som sai sempre 0: não há texto para comparar.
   */
  similaridade: number;
  /**
   * MEDIDA: o trecho do ASR que casou. NAO_MEDIDA: o que mais se pareceu e não
   * bastou. Som: string vazia.
   */
  evidencia: string;
  /**
   * `medido − modelo`, em segundos. `+` = o evento aconteceu DEPOIS do que o
   * modelo escreveu, que é o sentido da deriva. `null` quando não houve medida.
   */
  deslocamento_s: number | null;
  fonte_do_instante: FonteDoInstante;
  /**
   * O envelope do instante, em ms. `null` — e não 0 — quando NÃO houve medida:
   * um zero aqui seria lido como precisão perfeita por quem não abrisse o
   * `estado`, que é exatamente o defeito que esta etapa conserta.
   */
  precisao_ms: number | null;
  /** Só em NAO_MEDIDA: por quê. Slug de `execucao.MOTIVO_*`. */
  motivo?: string;
  /** Índices da primeira e da última palavra do ASR que este item ocupa. */
  trecho_asr?: [number, number];
  /** Item que TERIA casado no mesmo trecho e perdeu a disputa para a vizinha. */
  eco_de?: [number, number];
  eco_similaridade?: number;
  /** Índices (no array consolidado) de itens que ocupam o mesmo trecho do sinal. */
  gemea_de?: number[];
}

export interface SomOuvido {
  inicio_s: number;
  fim_s: number | null;
  categoria: string;
  subtipo?: string;
  descricao?: string;
  confianca?: number;
  /** Em quantas janelas o mesmo evento apareceu antes de consolidar. */
  vezes?: number;
  /** O que o modelo escreveu, antes da ancoragem. NUNCA se apaga. */
  inicio_modelo_s?: number | null;
  fim_modelo_s?: number | null;
  ancoragem?: Ancoragem;
}

export interface FalaOuvida {
  inicio_s: number;
  fim_s: number | null;
  texto: string;
  falante?: string;
  confianca?: number;
  vezes?: number;
  inicio_modelo_s?: number | null;
  fim_modelo_s?: number | null;
  ancoragem?: Ancoragem;
}

/** Quantos itens em cada estado. Item sem bloco de ancoragem conta como NAO_MEDIDA. */
export interface ContagemPorEstado {
  MEDIDA: number;
  ESTIMADA: number;
  NAO_MEDIDA: number;
}

/** O ajuste da deriva de UMA janela, por mínimos quadrados sobre as âncoras dela. */
export interface DerivaDaJanela {
  intercepto_s: number;
  inclinacao: number;
  residuo_s: number;
  n_ancoras: number;
  rel_min: number;
  rel_max: number;
}

export interface JanelaDoAsr {
  janela_indice?: number;
  janela_inicio_s?: number;
  janela_fim_s?: number;
  n_falas?: number;
  n_sons?: number;
  n_palavras?: number;
  falas_medidas?: number;
  sons_estimados?: number;
  /** `null` = a janela não teve âncoras bastantes; nenhum som dela foi estimado. */
  deriva?: DerivaDaJanela | null;
  motivo_sem_deriva?: string | null;
  n_disputas?: number;
  erro?: string;
}

/**
 * O que o ASR LOCAL fez nesta rodada — `app/audio/execucao.bloco_do_asr`.
 *
 * Viaja com o resultado pelo mesmo motivo que `modelo`/`janela_s`: trocar o
 * modelo de ASR ou desligar a etapa faria todo resultado antigo passar a mentir
 * sobre como foi produzido, e a mentira seria invisível — os instantes
 * continuariam parecendo instantes.
 *
 * NÃO CUSTA DINHEIRO: roda na CPU da máquina do backend, e o áudio não sai dela.
 */
export interface BlocoAsr {
  ligado: boolean;
  modelo: string | null;
  threads: number | null;
  /** `false` = não rodou. Então NENHUM item desta rodada tem instante medido. */
  disponivel: boolean;
  n_palavras: number;
  /** Relógio de parede da transcrição inteira, carga do modelo incluída. */
  segundos_de_cpu: number | null;
  aviso: string | null;
  /** Por que não rodou, quando não rodou. */
  motivo: string | null;
  estados: { falas: ContagemPorEstado; sons: ContagemPorEstado };
  deslocamento_s: { n: number; mediana: number | null; maximo: number | null };
  /** Pares de falas que ocupam o MESMO trecho do sinal — a mesma frase, duas vezes. */
  gemeas?: number;
  janelas?: JanelaDoAsr[];
  /** Presente quando a linha foi reancorada sem nova chamada paga. */
  reancorado_em?: string;
}

export interface ResultadoAudio {
  falas: FalaOuvida[];
  sons: SomOuvido[];
  /** Quantos eventos havia ANTES de consolidar as janelas. */
  brutos?: { falas: number; sons: number };
  resumo?: { falas: number; setas: number; motor_ligando: number; sons: number };
  /** Ausente em rodada anterior à etapa de ancoragem: lá nada foi medido. */
  asr?: BlocoAsr;
}

/**
 * `ausente` = nunca processado; `interrompido` = a tarefa morreu com o reload do
 * backend. Nenhum dos dois existe como linha no banco — sao estados que a API
 * deriva, e por isso o campo e string e nao um enum fechado.
 */
export type EstadoAudio =
  | "ausente"
  | "pendente"
  | "processando"
  | "pronto"
  /** O vídeo existe e foi gravado SEM trilha de áudio. Não é erro. */
  | "sem_audio"
  | "erro"
  | "interrompido"
  /**
   * O áudio parado pela régua do tempo de ruído na extração, antes do ASR
   * (06/10/2026, SPEC-ATIVO §2.6): `etapa` = a ação (`parar_audio` ou
   * `parar_exame`), `erro` = a frase da régua, custo 0. Não é erro.
   */
  | "audio_reprovado";

export interface AudioDoExame {
  exame_hash: string;
  estado: EstadoAudio;
  progresso: number;
  etapa: string | null;
  /** A configuração que produziu ESTE resultado — não a vigente. */
  modelo: string | null;
  janela_s: number | null;
  overlap_s: number | null;
  categoria: string | null;
  duracao_s: number | null;
  n_janelas: number;
  n_janelas_falhas: number;
  n_falas: number;
  n_setas: number;
  n_motor_ligando: number;
  tokens_entrada: number;
  tokens_saida: number;
  custo_usd: number | null;
  custo_tabela: string | null;
  // O ledger da E0 (compliance): GRAVADO na rodada, não calculado na leitura.
  // `null` numa linha anterior a 23/09/2026 quer dizer "não medido", não zero.
  // Opcionais porque a tela ainda não os mostra; o significado de cada um está
  // em `backend/app/models/audio_processamento.py`.
  /** A parcela de raciocínio DENTRO de `tokens_saida`. */
  tokens_raciocinio?: number | null;
  /** Envios HTTP ao Vertex, contando retentativas. */
  envios?: number | null;
  /** Tentativas que falharam, por classe: 429, 5xx, timeout, json, outro. */
  erro_classe?: Record<string, number> | null;
  /** O custo com a tabela do dia da rodada, e a data dessa tabela. */
  custo_usd_gravado?: number | null;
  custo_tabela_gravada?: string | null;
  /** Respostas cobradas e descartadas. Custo real = gravado + retentativas. */
  custo_retentativas_usd?: number | null;
  /** MP4 + WAV + FLAC guardados para este exame. O Limpar leva tudo. */
  artefatos_bytes: number;
  resultado: ResultadoAudio | null;
  midia: Record<string, unknown> | null;
  erro: string | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
}

export interface LimpezaAudio {
  exame_hash: string;
  removeu_resultado: boolean;
  artefatos_bytes: number;
}

export async function getAudioDoExame(
  hash: string,
  signal?: AbortSignal,
): Promise<AudioDoExame> {
  return request<AudioDoExame>(`/audio/exames/${encodeURIComponent(hash)}`, { signal });
}

/**
 * Dispara o processamento. Responde 202 e volta na hora — quem acompanha e o
 * polling. Cada janela e uma chamada PAGA ao modelo.
 */
export async function processarAudio(
  hash: string,
  signal?: AbortSignal,
): Promise<AudioDoExame> {
  return request<AudioDoExame>(`/audio/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    signal,
  });
}

/** Apaga o resultado e os artefatos. Recusa enquanto houver rodada em andamento. */
export async function limparAudio(
  hash: string,
  signal?: AbortSignal,
): Promise<LimpezaAudio> {
  return request<LimpezaAudio>(`/audio/exames/${encodeURIComponent(hash)}`, {
    method: "DELETE",
    signal,
  });
}

/**
 * URL do VIDEO baixado durante o processamento, servido com Range pelo backend.
 *
 * `<video src>` e cross-origin (app-valbot -> api-valbot), entao o elemento
 * PRECISA de `crossOrigin="use-credentials"` — sem ele o cookie de sessao nao
 * viaja e o backend responde 401 com o player mudo, sem erro visivel.
 *
 * 404 antes de processar, e isso e correto: o video so existe depois.
 */
export function urlDoVideoDoExame(hash: string): string {
  return `${API_BASE_URL}/audio/exames/${encodeURIComponent(hash)}/video`;
}

/** O WAV master extraido — a alternativa quando o video nao abre. */
export function urlDoAudioDoExame(hash: string): string {
  return `${API_BASE_URL}/audio/exames/${encodeURIComponent(hash)}/audio`;
}

/* -------------------------------------------------------------------------- */
/* Mapa de cameras                                                             */
/* -------------------------------------------------------------------------- */

export type Posicao = "tl" | "tr" | "bl" | "br";
/** {tl: "interna", ...}; null = quadrante aberto (a apuracao nao decidiu). */
export type MapaQuadrantes = Record<Posicao, string | null>;

export interface VotoTrecho {
  trecho_n: number;
  /** A janela REALMENTE enviada: 10 s na etapa 1, ate JANELA_FOCO_S nas focadas. */
  inicio_s: number;
  fim_s: number;
  tentativas: number;
  /**
   * Letras A–D -> papel em MAIUSCULAS. Ausente/null = voto invalido ou perdido.
   * No voto FOCADO (cadeia/1, etapas 2 e 3) so as letras do `foco`: os outros
   * quadrantes foram dados como fixos e nao sao voto; a resposta inteira do
   * modelo fica em `mapa_resposta_completa`.
   */
  mapa: Record<string, string | null> | null;
  /*
    cadeia/1 (04/10/2026). Ausentes na linha antiga (5 + 2), que tem um voto
    por trecho e todos da mesma rodada de Flash. Na cadeia um mesmo `trecho_n`
    pode votar mais de uma vez (etapa 1 e etapa 3, por exemplo).
  */
  /** 1 = os 5 trechos; 2 = o foco do Flash nos quadrantes em duvida; 3 = o Pro, um por vez. */
  etapa?: number | null;
  modelo?: string | null;
  /** O peso do voto na maioria ponderada: 1,5 no modelo da escalada, 1 nos outros. */
  peso?: number | null;
  /** As letras em duvida que este voto decide; null = voto inteiro (etapa 1). */
  foco?: string[] | null;
  /** Os papeis que sobraram para as letras do foco (um cada). */
  papeis_em_foco?: string[] | null;
  /** O que o prompt deu como ja identificado, {letra: papel}. */
  fixos?: Record<string, string> | null;
  fps?: number | null;
  /** `MEDIA_RESOLUTION_HIGH` nas focadas; null = o padrao do modelo. */
  resolucao?: string | null;
  mapa_resposta_completa?: Record<string, string | null> | null;
  confiancas?: Record<string, number | null> | null;
  evidencias?: Record<string, string[] | null> | null;
  confianca_mapa?: number | null;
  valido: boolean;
  perdido: boolean;
  reparado_por_retentativa?: boolean;
  /** O que o PRÓPRIO modelo declarou como limitação daquele trecho. */
  ressalvas?: string[] | null;
  chamadas?: ChamadaDeTrecho[] | null;
  erro?: string | null;
}

/**
 * Uma tentativa isolada de ler um trecho. Um voto tem de uma a
 * TENTATIVAS_POR_TRECHO delas — e quando todas falham é aqui, e só aqui, que
 * fica escrito o porquê (cota estourada, saída malformada).
 */
export interface ChamadaDeTrecho {
  tentativa: number;
  valido: boolean;
  ms?: number | null;
  /** Falha de transporte: HTTP 4xx/5xx do Vertex, timeout. */
  erro?: string | null;
  /** Falha de conteúdo: o que o parser recusou na resposta. */
  problemas?: string[] | null;
  custo_usd?: number | null;
  razao_parada?: string | null;
}

export interface CamerasDoExame {
  exame_hash: string;
  /** ausente | pendente | processando | pronto | erro | interrompido | nao_compativel */
  estado: string;
  progresso: number;
  etapa: string | null;
  modelo: string | null;
  categoria: string | null;
  /**
   * MAPA_COMPLETO | MAPA_INCOMPLETO | MAPA_INVALIDO | MAPA_AMBIGUO | ERRO_INFRA |
   * MAPA_ILEGIVEL. Desde a cadeia/1 (04/10/2026) rodada nova so produz
   * COMPLETO, ILEGIVEL (mapa null, nenhuma chamada paga) ou ERRO_INFRA (estado
   * `erro`) — e, desde a cadeia/2 (13/10/2026), o AMBIGUO da guarda fisica
   * (motivo `sentido_do_fluxo`), que espera uma pessoa como o antigo;
   * INCOMPLETO/INVALIDO ficam nas linhas antigas.
   */
  status_mapa: string | null;
  /**
   * O status pela regra de HOJE (o que o portao da visao usa): igual a
   * `status_mapa` nas rodadas da E2; nas anteriores, relido do resumo gravado.
   */
  status_vigente?: string | null;
  /** Letras dos quadrantes decididos com margem < 2 (tambem nas rodadas antigas). */
  quadrantes_ambiguos?: string[];
  /** A resposta da MAQUINA — nunca sobrescrita pela confirmacao humana. */
  mapa: MapaQuadrantes | null;
  /** E2.2: o mapa que uma pessoa confirmou (a bijecao dos quatro), e quem/quando. */
  mapa_confirmado?: Record<Posicao, string> | null;
  confirmado_por?: string | null;
  confirmado_em?: string | null;
  confirmacao_nota?: string | null;
  /** O que a visao usa: o confirmado quando ha, senao o da maquina. */
  mapa_vigente?: MapaQuadrantes | null;
  /** `humano:<email>` | `ia:<modelo>` | null. */
  origem_do_mapa?: string | null;
  /** E2.7: as medidas por quadrante (sem rotulo). Tipo em `QuadrantesDoMapa`. */
  quadrantes?: QuadrantesDoMapa | null;
  assinatura_regras?: string | null;
  apuracao: Record<string, unknown> | null;
  votos: VotoTrecho[] | null;
  miniaturas: Array<{ n: number; t: number; url: string }>;
  amostragem: Record<string, unknown> | null;
  portao: Record<string, unknown> | null;
  /**
   * A sonda do MP4 gravada na rodada (ffprobe). A aba tira dela a proporcao
   * inicial dos players dos quadrantes; o resto vai junto, sem uso na tela.
   */
  midia?: {
    largura?: number | null;
    altura?: number | null;
    bytes?: number | null;
    duracao_video_s?: number | null;
    audio_presente?: boolean | null;
    [campo: string]: unknown;
  } | null;
  /**
   * 24/09: o MOSAICO pode ser servido (`/audio/exames/{h}/video`, do disco ou
   * do bucket) — `core.evidencia.video_servivel`. `false` e "nao provado",
   * nao "nao existe". Ausente num backend anterior: a aba tenta e cai no
   * texto se o player falhar.
   */
  video_disponivel?: boolean;
  /** `local` | `bucket` | null — de onde o mosaico sai. */
  video_origem?: string | null;
  n_trechos: number;
  n_votos_validos: number;
  tokens_entrada: number;
  tokens_saida: number;
  tokens_raciocinio: number;
  /**
   * So o dos votos APROVEITADOS (C1.4 da auditoria de 23/09). O voto pago e
   * invalido esta em `custo_descartado_usd`; o total cobrado e a soma das
   * duas, e e ela que a aba mostra. Linha anterior a coluna: `custo_usd` ja
   * traz tudo, e o descartado vem null.
   */
  custo_usd: number | null;
  /** Ausente num backend anterior a revisao da auditoria. */
  custo_descartado_usd?: number | null;
  artefatos_bytes: number;
  erro: string | null;
  atualizado_em: string | null;
}

export async function getCamerasDoExame(hash: string, signal?: AbortSignal): Promise<CamerasDoExame> {
  return request<CamerasDoExame>(`/cameras/exames/${encodeURIComponent(hash)}`, { signal });
}

/**
 * Dispara o mapa de cameras. 202; quem acompanha e o polling. De 5 a 11
 * chamadas PAGAS pela cadeia/1 (5 do Flash, +2 focadas se houver duvida, ate 4
 * do Pro se ela persistir); nenhuma se uma camera estiver ilegivel.
 */
export async function processarCameras(hash: string, signal?: AbortSignal): Promise<CamerasDoExame> {
  return request<CamerasDoExame>(`/cameras/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    signal,
  });
}

export async function limparCameras(
  hash: string,
  signal?: AbortSignal,
): Promise<{ exame_hash: string; removeu_resultado: boolean; artefatos_bytes: number }> {
  return request(`/cameras/exames/${encodeURIComponent(hash)}`, { method: "DELETE", signal });
}

/** As miniaturas vem com URL RELATIVA a API; `<img>` precisa da absoluta. */
export function urlAbsolutaDaApi(caminho: string): string {
  return `${API_BASE_URL}${caminho.startsWith("/") ? caminho : `/${caminho}`}`;
}

export interface ConfigCamerasCategoria {
  categoria: string;
  rotulo: string;
  /** O modelo das etapas 1 e 2 da cadeia. O da etapa 3 e fixo: `cadeia.modelo_escalada`. */
  modelo: string;
  prompt_sistema: string;
  prompt_texto: string;
  configurada: boolean;
  atualizado_em: string | null;
  atualizado_por: string | null;
  prompt_e_padrao: boolean;
  /**
   * O padrao DESTA categoria (22/10/2026: C, D e E tem os prompts adaptados ao
   * layout delas). Ausente num backend anterior: a tela so restaura a B, com o
   * padrao geral da resposta, e desliga o "Restaurar" das copias.
   */
  prompt_sistema_padrao?: string;
  prompt_texto_padrao?: string;
  /** As constantes da cadeia/1 (codigo, nao configuracao). Ausente num backend anterior. */
  cadeia?: CadeiaDaConfig | null;
}

/**
 * A CADEIA DO MAPA como o backend a roda (`app/cameras/padroes.py`), para a
 * tela descrever o gasto com os numeros de la e nao com uma copia.
 */
export interface CadeiaDaConfig {
  versao: string;
  modelo_escalada: string;
  peso_escalada: number;
  fps_foco: number;
  janela_foco_s: number;
  resolucao_foco: string;
  max_chamadas_escalada: number;
  trechos_etapa_1: number[];
  trechos_etapa_2: number[];
}

export interface ConfigCameras {
  categorias: ConfigCamerasCategoria[];
  modelos: OpcaoModelo[];
  prompt_sistema_padrao: string;
  prompt_texto_padrao: string;
  parametros: Record<string, unknown>;
  processo: EtapaDoProcesso[];
}

export async function getConfigCameras(signal?: AbortSignal): Promise<ConfigCameras> {
  return request<ConfigCameras>("/config/cameras", { signal });
}

export async function salvarConfigCameras(
  categorias: Array<Pick<ConfigCamerasCategoria, "categoria" | "modelo" | "prompt_sistema" | "prompt_texto">>,
  signal?: AbortSignal,
): Promise<ConfigCameras> {
  return request<ConfigCameras>("/config/cameras", { method: "PUT", body: { categorias }, signal, timeoutMs: 20_000 });
}

/* -------------------------------------------------------------------------- */
/* Seta — a janela em que o tique-taque conta como desta manobra              */
/* -------------------------------------------------------------------------- */

/**
 * A ÚNICA TOLERÂNCIA EDITÁVEL DO PORTAL, e a exceção é consciente:
 * `config_fusao` diz por escrito que tolerância não se configura, porque uma
 * folga editável permite "resolver" uma divergência alargando-a até sumir.
 *
 * O que torna esta exceção segura é a assinatura: os dois números entram na
 * assinatura das manobras, então mexer aqui faz cada exame já processado
 * mostrar "a régua mudou desde esta rodada" até ser reprocessado.
 */
export interface ConfigSetaCategoria {
  categoria: string;
  rotulo: string;
  /** Quanto ANTES do início da manobra o tique-taque ainda conta como dela. */
  janela_antes_ms: number;
  /** Quanto DEPOIS do FIM da manobra (sem fim, do início) — seta.v7, decisão
   * do dono de 27/09/2026; até a v6 a âncora era o início. */
  janela_depois_ms: number;
  /** `false` = ninguém configurou; os números são os de fábrica. */
  configurada: boolean;
  padrao_antes_ms: number;
  padrao_depois_ms: number;
  limite_antes_ms: number[];
  limite_depois_ms: number[];
  atualizado_em: string | null;
  atualizado_por: string | null;
  /** A linha que existe e NÃO vale: gravada sem a marca de semântica da
   * seta.v7 (até a v6, "depois" contava do início), que lida hoje poderia ser
   * uma janela que ninguém decidiu (`app.seta.config.resolve`). `null` = nada
   * ignorado. */
  linha_ignorada?: ConfigSetaLinhaIgnorada | null;
}

export interface ConfigSetaLinhaIgnorada {
  janela_antes_ms: number;
  janela_depois_ms: number;
  semantica: string | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
  motivo: string;
}

export interface ConfigSeta {
  categorias: ConfigSetaCategoria[];
  /** De onde veio cada número de fábrica (seta.v7: os dois, decisão do dono de 27/09/2026). */
  procedencia: Record<string, string>;
  aviso: string;
}

export async function getConfigSeta(signal?: AbortSignal): Promise<ConfigSeta> {
  return request<ConfigSeta>("/config/seta", { signal });
}

export async function salvarConfigSeta(
  categorias: Array<Pick<ConfigSetaCategoria, "categoria" | "janela_antes_ms" | "janela_depois_ms">>,
  signal?: AbortSignal,
): Promise<ConfigSeta> {
  return request<ConfigSeta>("/config/seta", { method: "PUT", body: { categorias }, signal, timeoutMs: 20_000 });
}

export async function getConfigCamerasDaCategoria(
  categoria: string,
  signal?: AbortSignal,
): Promise<ConfigCamerasCategoria> {
  return request<ConfigCamerasCategoria>(`/config/cameras/exame/${encodeURIComponent(categoria || "-")}`, { signal });
}

/* -------------------------------------------------------------------------- */
/* Visao — a extracao de eventos das QUATRO cameras                            */
/* -------------------------------------------------------------------------- */

/**
 * NAO E O MAPA. `CamerasDoExame`, acima, responde "qual quadrante e qual
 * camera"; daqui para baixo e o que cada camera VIU depois de recortada. O
 * mapa e o PORTAO das quatro: enquanto os quatro papeis nao fecharem em
 * quadrantes distintos, nenhuma extrai, e o POST responde 409 com a frase
 * pronta no `detail` (que `describeError` ja entrega em portugues).
 *
 * Espelha `backend/app/schemas/visao.py` campo a campo, com duas regras que
 * vem de defeito ja pago neste repositorio:
 *
 *   UNIAO FECHADA, NUNCA `string`. `CamerasDoExame.estado` ficou `string` com
 *   os valores num comentario: o `switch` que a consome perdeu a
 *   exaustividade, e o compilador deixou de avisar quando um valor novo
 *   apareceu — o caso novo caiu no `default` sem ninguem notar. Aqui cada
 *   uniao e DERIVADA de um array `as const`: a lista serve ao runtime (as
 *   abas, os filtros) e o tipo sai dela, entao as duas nao podem divergir.
 *
 *   `null` E "NAO MEDIDO" E SOBREVIVE ATE A TELA. Todo campo que o backend
 *   declara `| None` sem default 0 sai `| null` aqui, e nenhuma funcao deste
 *   arquivo o troca por 0 no caminho. Contador que a execucao de fato contou
 *   (`n_eventos`, `tokens_entrada`, `artefatos_bytes`) e `number`, porque
 *   nesses o zero foi medido.
 */

/**
 * A ordem canonica dos quatro papeis — `app/visao/padroes.PAPEIS`.
 *
 * E a ordem das abas E a ordem em que as quatro sao despachadas. `as const`
 * para o tipo abaixo sair daqui: uma lista e um `type` escritos separados
 * divergem no dia em que houver uma quinta camera.
 */
export const PAPEIS_CAMERA = ["interna", "frontal", "lateral", "traseira"] as const;

/** interna | frontal | lateral | traseira. */
export type PapelCamera = (typeof PAPEIS_CAMERA)[number];

/**
 * OS OITO ESTADOS de uma camera. Sete sao de `app/visao/padroes.ESTADOS`; o
 * oitavo e `ausente`, que NAO existe como linha no banco — e a resposta para
 * "esta camera nunca foi processada", e a tela precisa dela para desenhar o
 * botao Processar.
 *
 * `bloqueado` e `nao_compativel` NAO SAO ERRO: sao falta de uma decisao (o
 * mapa por montar, um quadrante em aberto) ou recusa de escopo (exame que nao
 * e categoria B). Pintar os dois de vermelho ensina quem olha a ignorar
 * vermelho, e no dia em que houver falha de verdade ninguem vai distinguir.
 */
export const ESTADOS_VISAO = [
  "ausente",
  "pendente",
  "processando",
  "pronto",
  "erro",
  "interrompido",
  "nao_compativel",
  "bloqueado",
  // E2.1: a configuracao diz que esta camera NAO roda no processamento
  // automatico (a traseira de B). Terminal e neutro: nem erro, nem em curso.
  "nao_solicitada",
] as const;

export type EstadoVisao = (typeof ESTADOS_VISAO)[number];

/**
 * Os dois estados em que a rodada esta VIVA — os mesmos `_VIVOS` da rota.
 *
 * E a unica condicao em que o polling deve continuar; `pronto`, `erro`,
 * `bloqueado`, `nao_compativel`, `interrompido` e `ausente` sao terminais
 * enquanto ninguem clicar em nada.
 */
export const ESTADOS_VISAO_EM_ANDAMENTO = [
  "pendente",
  "processando",
] as const satisfies readonly EstadoVisao[];

/** `true` enquanto ha trabalho — quem faz polling para quando isto vira false. */
export function visaoEmAndamento(estado: EstadoVisao): boolean {
  return (ESTADOS_VISAO_EM_ANDAMENTO as readonly EstadoVisao[]).includes(estado);
}

/** O estado de revisao de um evento. `nao_revisado` e o valor de nascimento. */
export const REVISOES_DE_EVENTO = [
  "nao_revisado",
  "confirmado",
  "rejeitado",
  "corrigido",
] as const;

export type RevisaoEvento = (typeof REVISOES_DE_EVENTO)[number];

/**
 * O que se pode FAZER. `nao_revisado` fica de fora de proposito: desfazer uma
 * revisao nao e um clique que exista hoje, e oferece-lo sem a trilha saber
 * desfazer seria mentir na tela.
 */
export const ACOES_DE_REVISAO = [
  "confirmado",
  "rejeitado",
  "corrigido",
] as const satisfies readonly RevisaoEvento[];

export type AcaoRevisao = (typeof ACOES_DE_REVISAO)[number];

/* ------------------------------ O portao ---------------------------------- */

/**
 * MAPA_COMPLETO e o unico que abre o portao sozinho (`app/cameras/apuracao.py`);
 * desde a E2 os outros abrem com a confirmacao de uma pessoa (`mapa_confirmado`).
 * Desde a cadeia/1 (04/10/2026) rodada nova nao produz mais AMBIGUO: a cadeia
 * decide sempre, e MAPA_ILEGIVEL nao abre — o exame vai a `nao_compativel` e o
 * compliance acusa a falha tecnica.
 */
export type StatusMapa =
  | "MAPA_COMPLETO"
  | "MAPA_INCOMPLETO"
  | "MAPA_INVALIDO"
  | "MAPA_AMBIGUO"
  | "ERRO_INFRA"
  | "MAPA_ILEGIVEL";

/**
 * A margem da votacao do mapa — NAO e acuracia. Mede quantos votos formaram o
 * vencedor, nada mais.
 */
export type DegrauDoMapa = "folgada" | "apertada" | "quase_empate";

/**
 * O portao, que e UM POR EXAME e nao por camera: a pergunta "a interna pode
 * extrair?" nao tem resposta propria. Vem de `EstadoDoPortao.para_api()`.
 */
export interface PortaoDaVisao {
  exame_hash: string;
  aberto: boolean;
  /** snake_case, para o codigo decidir. `null` quando aberto. */
  motivo: string | null;
  /** A FRASE que a tela mostra — sempre preenchida, inclusive quando aberto. */
  mensagem: string;
  /**
   * SEMPRE `false`, e e por isso que viaja. Portao fechado e falta de decisao,
   * nao falha: quem pintar isto de vermelho ensina a ignorar vermelho.
   */
  e_falha: boolean;
  /** O mapa normalizado: posicao -> papel, `null` = quadrante em aberto. */
  mapa: MapaQuadrantes;
  /** O inverso, so com o que fechou. Papel ausente = papel sem lugar. */
  posicoes: Partial<Record<PapelCamera, Posicao>>;
  categoria: string | null;
  status_mapa: StatusMapa | null;
  /** O estado da execucao do MAPA (`cameras_processamento`), nao o desta aba. */
  estado_mapa: string | null;
  /** Quem escolheu o quadrante: `ia:<modelo>` | `humano:<email>` | `medicao`. */
  origem: string | null;
  degrau: DegrauDoMapa | null;
  /** O que o mapa registrou de proprio punho (`cameras_processamento.erro`). */
  detalhe: string | null;
  /** O que gravar em `visao_execucoes.estado` quando fechado. Nunca `erro`. */
  estado_sugerido: EstadoVisao | null;
}

/** Uma camera publicada de um quadrante que o mapa de hoje contradiz. */
export interface DivergenciaDeQuadrante {
  papel: PapelCamera;
  /** O quadrante de onde a extracao anterior saiu. */
  publicado: Posicao;
  /** O que o mapa vigente diz. `null` = o mapa nao decidiu este papel. */
  vigente: Posicao | null;
  /** `humano:<email>` | `ia:<modelo>` | `medicao` | `null` (nao registrada). */
  origem: string | null;
  /**
   * So `humano...` vence a leitura da maquina. Origem NAO REGISTRADA conta
   * como maquina — presumi-la humana desfaria o portao inteiro.
   */
  por_humano: boolean;
}

/** `Conferencia.para_api()`: pode extrair, de que quadrante, e o que falta. */
export interface ConferenciaDaCamera {
  exame_hash: string;
  papel: PapelCamera;
  liberado: boolean;
  /** De onde recortar. So vem preenchido quando liberado. */
  quadrante: Posicao | null;
  motivo: string | null;
  mensagem: string;
  /** Sempre `false` — ver `PortaoDaVisao.e_falha`. */
  e_falha: boolean;
  estado_sugerido: EstadoVisao | null;
  /** A escolha de uma pessoa contradisse o mapa e venceu. */
  venceu_humano: boolean;
  /** TODAS as do exame, inclusive as dos outros tres papeis. */
  divergencias: DivergenciaDeQuadrante[];
  portao: PortaoDaVisao;
}

/* ---------------------------- O custo previsto ---------------------------- */

/**
 * Quanto ESTA camera custa neste exame SE rodar agora (`app/visao/custo.py`).
 *
 * `usd` e a expectativa e `usd_teto` e o pior caso honesto (toda janela
 * trunca e e repetida com o dobro de saida). A distancia entre os dois E a
 * informacao. Os dois sao `null` quando o modelo esta fora da tabela de
 * precos ou quando nao ha janela a prever — `null` ali e "nao da para
 * estimar", nunca "de graca".
 */
export interface EstimativaDaCamera {
  modelo: string;
  papel: PapelCamera;
  tabela: string;
  tabela_fonte: string;
  n_janelas: number;
  janela_s: number;
  fps_enviado: number;
  resolucao_midia: string;
  tokens_por_janela: { video: number; texto: number; entrada: number; saida: number };
  tokens: { entrada: number; saida: number };
  /** `false`: o texto do prompt entra por regra de bolso, nao por medicao. */
  texto_medido: boolean;
  nota: string;
  preco_conhecido: boolean;
  usd: number | null;
  usd_por_janela: number | null;
  usd_teto: number | null;
  /** So viaja no ramo com preco conhecido. */
  teto_raciocinio?: number | null;
}

/* ------------------------------- As metricas ------------------------------ */

/** O funil da execucao. `fecha` viaja junto para ninguem ter de refazer a conta. */
export interface FunilDaVisao {
  emitidos_pelo_modelo: number;
  recusados: number;
  aceitos: number;
  por_motivo: Record<string, number>;
  eventos_na_costura: number;
  costura_deduplicada: number;
  estado_repetido_suprimido: number;
  publicados: number;
  fecha: boolean;
  /** Ausente quando o funil fecha. */
  divergencias?: string[];
}

/**
 * A fila de prioridade da revisao — NAO e julgamento de acerto.
 *
 * `sem_confianca` e contado A PARTE e nao entra no `total`: confianca ausente
 * e ausencia de medicao, nao confianca baixa.
 */
export interface CasosCriticosDaVisao {
  total: number;
  com_marca_da_regua: number;
  subtipo_recusado: number;
  contato_rebaixado: number;
  confianca_baixa: number;
  sem_confianca: number;
  limiar_confianca: number;
}

/** O custo do run, somado das janelas (`app/visao/custo.somar_janelas`). */
export interface CustoDoRunDaVisao {
  /**
   * `null` quando NENHUMA janela soube se precificar. Janela sem preco nao
   * vira zero: ela e contada em `janelas_sem_preco`, e um total mostrado sem
   * olhar esse campo diz "custou X" onde o certo e "custou pelo menos X".
   */
  aproveitado_usd: number | null;
  descartado_usd: number | null;
  total_usd: number | null;
  janelas: number;
  janelas_sem_preco: number;
  preco_conhecido: boolean;
  tabela: string;
  tokens_cobrados: Record<string, number>;
  /** A estimativa de ANTES do clique, guardada ao lado do que se pagou. */
  estimado?: EstimativaDaCamera | null;
}

/**
 * OS TRES CAMPOS QUE SO EXISTEM DEPOIS QUE UMA PESSOA JULGA UMA AMOSTRA.
 *
 * `null` neles nao e zero: zero afirmaria acerto onde ninguem olhou, que e o
 * defeito mais grave que o legado documenta. Renderize a `ressalva`, nunca um
 * numero.
 */
export interface ValidacaoHumanaDaVisao {
  falsos_negativos: number | null;
  recall_amostral: number | null;
  precision_amostral: number | null;
  amostra_sorteada: boolean;
  nao_medido: string[];
  ressalva: string;
}

/** Uma linha por janela, para a tela ler sem carregar o RAW inteiro. */
export interface ResumoDeJanelaDaVisao {
  indice: number;
  inicio_ms: number;
  fim_ms: number;
  duracao_s: number;
  bytes: number;
  /** `null` = a janela coube sem nenhuma das tres defesas. */
  degradacao: Record<string, unknown> | null;
  emitidos: number;
  aceitos: number;
  recusas: Record<string, number>;
  tentativas: number;
  /** Qual tentativa foi publicada (1-based). `null` = nenhuma rendeu texto. */
  vencedora: number | null;
  razao_parada: string | null;
  truncada: boolean;
  reparos: string[];
  loop: string[] | null;
  cauda_perdida: boolean;
  duracao_chamada_s: number | null;
  custo_usd: number | null;
  custo_descartado_usd: number | null;
}

/** O recorte deste quadrante, como o ffmpeg o mediu. */
export interface RecorteDaCamera {
  posicao: Posicao;
  caminho: string;
  largura: number | null;
  altura: number | null;
  fps: number | null;
  duracao_ms: number | null;
  bytes: number;
  sha256: string | null;
  mtime_ns: number | null;
  /** `true` = o recorte ja existia em disco e nao foi cortado de novo. */
  reaproveitado: boolean;
  papel: PapelCamera;
}

/** O fatiamento inteiro, como `app/visao/janelas.resumo_do_fatiamento` o conta. */
export interface FatiamentoDaVisao {
  n_janelas: number;
  bytes_totais: number;
  mib_totais: number;
  duracao_coberta_s: number;
  janelas_degradadas: number;
  janelas_encurtadas: number;
  faixas_de_costura: number;
  costura_total_ms: number;
}

/**
 * `visao_execucoes.metricas` — tudo opcional porque o bloco e escrito de uma
 * vez no fim da execucao: run em andamento, bloqueado ou com erro tem parte
 * dele, ou so `portao`.
 */
export interface MetricasDaVisao {
  funil?: FunilDaVisao;
  por_tipo?: Record<string, number>;
  /** Quantos eventos cada marca pegou SEGUNDO A REGUA DAQUELE DIA. */
  marcas?: Record<string, number>;
  casos_criticos?: CasosCriticosDaVisao;
  fatiamento?: FatiamentoDaVisao;
  janelas?: ResumoDeJanelaDaVisao[];
  custo?: CustoDoRunDaVisao;
  recorte?: RecorteDaCamera;
  /** Onde a frase do bloqueio mora. NUNCA em `erro` — bloqueio nao e falha. */
  portao?: ConferenciaDaCamera;
  validacao_humana?: ValidacaoHumanaDaVisao;
}

/* -------------------------- O estado de uma camera ------------------------ */

/** UMA camera de UM exame: onde ela esta, o que custou, e se pode rodar. */
export interface EstadoDaCamera {
  exame_hash: string;
  papel: PapelCamera;
  /** Do PERFIL, nao do banco: a aba se desenha antes de existir execucao. */
  rotulo: string;
  resumo: string;

  estado: EstadoVisao;
  progresso: number;
  etapa: string | null;

  /**
   * A receita que produziu ESTE resultado — nao a vigente. Trocar o prompt na
   * tela de configuracao nao reescreve a historia de um run ja pago.
   */
  modelo: string | null;
  categoria: string | null;
  prompt_sha1: string | null;
  assinatura_regras: string | null;
  configuracao: Record<string, unknown> | null;

  quadrante: Posicao | null;
  /** `tl` -> "A (superior esquerdo)" — ja pronto, nao remonte. */
  quadrante_rotulo: string | null;
  /** `humano:<email>` | `ia:<modelo>` | `medicao`. */
  origem_quadrante: string | null;
  recorte_sha256: string | null;

  metricas: MetricasDaVisao | null;
  /** O mesmo bloco de dentro de `metricas`, repetido no topo por conveniencia. */
  funil: FunilDaVisao | null;

  n_janelas: number;
  n_janelas_com_erro: number;
  n_eventos_brutos: number;
  n_eventos: number;
  /** `null` = a contagem nao veio; 0 = contou e ninguem revisou ainda. */
  n_eventos_revisados: number | null;

  tokens_entrada: number;
  tokens_saida: number;
  tokens_raciocinio: number;
  /** `null` = modelo fora da tabela de precos. NAO e zero. */
  custo_usd: number | null;
  custo_descartado_usd: number | null;
  /** `null` = nao da para estimar (exame sem duracao medida). */
  estimativa: EstimativaDaCamera | null;

  /** Recorte + fichas + janelas DESTA camera, medidos no disco a cada leitura. */
  artefatos_bytes: number;
  /**
   * E2: o MOSAICO do exame pode ser servido (disco ou bucket,
   * `core.evidencia.video_servivel`). Sempre `false` na `nao_solicitada`.
   */
  video_disponivel: boolean;
  /**
   * E2: relativa a API e aponta para o MOSAICO (`/audio/exames/{h}/video`); a
   * tela recorta o quadrante `recorte.posicao` por CSS. Use
   * `urlDoVideoDoExame` para o absoluto.
   */
  video_url: string | null;
  /** E2: `local` | `bucket` | `null` — de onde o mosaico sai. */
  video_origem?: string | null;
  /** E2: o quadrante que a tela recorta do mosaico. */
  recorte?: { posicao: Posicao | null } | null;
  /** E2.1: o modo que a configuracao VIGENTE da a esta camera. */
  modo?: ModoDaCamera | null;
  modo_padrao?: ModoDaCamera | null;
  /** E2.3: `interna` | `frontal` | `lateral` | `traseira` | `lateral_parada`. */
  perfil?: string | null;
  /** E2.1: a `nao_solicitada` que alguem mandou rodar pelo botao. */
  solicitada_manualmente?: boolean;
  /**
   * E2.1: o custo NO PROCESSAMENTO AUTOMATICO, pelo modo — 0 na
   * `nao_solicitada`, so as janelas do gatilho na condicional. `estimativa`
   * continua sendo a do botao desta aba (a integral).
   */
  custo_previsto_usd?: number | null;
  /** E2.3: a previsao da lateral condicional (as janelas do gatilho). */
  estimativa_no_modo?: EstimativaCondicional | null;

  conferencia: ConferenciaDaCamera | null;
  /** Atalho do que a tela pergunta primeiro: o botao Processar fica ativo? */
  liberado: boolean;

  erro: string | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
}

/** AS QUATRO de uma vez — que e como o portao decide e como a tela abre. */
export interface VisaoDoExame {
  exame_hash: string;
  categoria: string | null;
  /** A duracao do MOSAICO. `null` = o video nunca chegou ao bucket. */
  duracao_s: number | null;
  portao: PortaoDaVisao | null;
  aberto: boolean;
  /** Na ordem canonica de `PAPEIS_CAMERA`. */
  papeis: EstadoDaCamera[];
  /**
   * A soma das quatro estimativas, ou `null` quando ALGUMA falta: uma soma
   * parcial apresentada como total seria um numero mais barato que a verdade,
   * exatamente onde o numero existe para avisar do gasto.
   */
  custo_previsto_usd: number | null;
  artefatos_bytes: number;
}

/* --------------------------------- Eventos -------------------------------- */

/** O que o MODELO disse. Nunca muda, nem quando alguem corrige. */
export interface PrevisaoDoModelo {
  tipo: string;
  subtipo: string | null;
  ator: string | null;
  descricao: string;
}

/**
 * A regua de HOJE contra a regua da EXECUCAO.
 *
 * `divergiu` pode ser `null` — a execucao nao registrou regua, e ninguem sabe
 * se elas batem. Renderizar esse `null` como `false` desfaria o ponto inteiro:
 * um evento antigo pareceria comparavel com um de hoje.
 */
export interface ComparacaoDeRegua {
  de_hoje: string;
  da_execucao: string | null;
  conhecida: boolean;
  divergiu: boolean | null;
  marcas_da_execucao: Record<string, string>;
}

/**
 * `atributos` do evento, ja RECALCULADO na leitura.
 *
 * As chaves `alerta_*` sao as marcas da regua de hoje sobre a descricao
 * vigente (valor = o trecho que disparou). O resto do bloco e o que a
 * execucao gravou — `subtipo_recusado`, `contato_rebaixado_de` e o que mais o
 * perfil daquela camera produzir; por isso a assinatura de indice.
 */
export interface AtributosDoEvento {
  /** O digest da regua de hoje, repetido em todo evento da resposta. */
  regua_afirmacoes?: string;
  regua?: ComparacaoDeRegua;
  /** O rotulo que o modelo deu e a taxonomia recusou. O gesto continua sendo um fato. */
  subtipo_recusado?: string;
  [chave: string]: unknown;
}

/** Um evento como a LEITURA o entrega: vigente no topo, previsao ao lado. */
export interface EventoDeVisao {
  id: number;
  exame_hash: string;
  papel: PapelCamera;

  inicio_ms: number;
  fim_ms: number | null;
  /**
   * Onde o player deve abrir: `FOLGA_S` (3 s) ANTES do instante. Validar um
   * gesto exige ver o que veio antes dele — nao use `inicio_ms` para o seek.
   */
  abre_em_ms: number;

  /** O VIGENTE, com o COALESCE ja feito: a correcao humana quando existe. */
  tipo: string;
  /** `""` e legitimo: "tipo sem subtipo". `null` e "nunca teve". */
  subtipo: string | null;
  ator: string | null;
  descricao: string;
  /** `null` = o modelo nao disse. NAO e confianca zero. */
  confianca: number | null;

  previsao: PrevisaoDoModelo;
  revisao: RevisaoEvento;
  revisado_por: string | null;
  revisado_em: string | null;

  /** jNNN#NNN — liga o evento a resposta bruta da janela de onde veio. */
  raw_id: string | null;
  janela_indice: number | null;
  /** O instante que o modelo disse, antes do deslocamento da janela. */
  instante_modelo: string | null;

  atributos: AtributosDoEvento | null;
  /** Atalho: a regua de HOJE pegou alguma coisa nesta descricao? */
  marcado: boolean;
  criado_em: string | null;
}

/** A lista, mais o vocabulario que o select de correcao precisa. */
export interface EventosDaCamera {
  exame_hash: string;
  papel: PapelCamera;
  /**
   * Quantos casaram com o filtro. `eventos` pode trazer MENOS: a lista tem
   * teto, e `truncado` diz quando ele cortou.
   */
  total: number;
  truncado: boolean;
  eventos: EventoDeVisao[];

  /**
   * tipo -> subtipos. VEM DA MESMA FONTE QUE VALIDA A CORRECAO: montar o
   * select de outra lista faria a tela oferecer opcao que a API recusa. A
   * opcao "tipo sem subtipo" (`""`) e da tela — sem ela, salvar uma correcao
   * de descricao apaga o subtipo.
   */
  taxonomia: Record<string, string[]>;
  atores: string[];
  /** Digest das duas reguas de hoje; igual ao `atributos.regua.de_hoje`. */
  regua_de_hoje: string;
  /**
   * Quantos eventos cada marca pega HOJE, e quantos a execucao gravou. Somar
   * uma com a outra nao mede nada: sao perguntas diferentes.
   */
  marcas_de_hoje: Record<string, number>;
  marcas_da_execucao: Record<string, number>;
  contagem_por_tipo: Record<string, number>;
}

/** Os filtros da rota de eventos. Tudo opcional; o que nao for enviado, nao filtra. */
export interface FiltroDeEventos {
  revisao?: RevisaoEvento | null;
  /** Pelo tipo VIGENTE — inclui o que virou esse tipo numa correcao. */
  tipo?: string | null;
  /** Filtro de LEITURA: a marca e recalculada na resposta, nao ha indice. */
  marcados?: boolean;
  /** 1..20000. O default do backend e 5000. */
  limite?: number;
}

/* -------------------------------- A revisao ------------------------------- */

/**
 * O clique de quem revisa.
 *
 * `undefined`/`null` quer dizer "nao mexi neste campo"; `""` no subtipo ou no
 * ator quer dizer "este evento nao tem". Sao coisas diferentes, e e essa
 * distincao que impede que salvar uma correcao de descricao apague o subtipo.
 *
 * A API recusa (422) `tipo: ""`, `descricao: ""`, `corrigido` sem nenhum campo
 * e campo corrigido junto de `confirmado`/`rejeitado`.
 */
export interface CorpoDaRevisao {
  acao: AcaoRevisao;
  tipo?: string | null;
  subtipo?: string | null;
  ator?: string | null;
  descricao?: string | null;
  nota?: string | null;
}

/** Uma linha da trilha. Nunca sobrescrita, nunca apagada. */
export interface RevisaoDoEvento {
  id: number;
  evento_id: number;
  acao: AcaoRevisao;
  antes: Record<string, unknown> | null;
  depois: Record<string, unknown> | null;
  nota: string | null;
  autor: string | null;
  criado_em: string | null;
}

/** O que o Limpar tirou de fato — banco e disco contados separados. */
export interface LimpezaDaCamera {
  exame_hash: string;
  papel: PapelCamera;
  removeu_resultado: boolean;
  artefatos_bytes: number;
  /** `null` = o motor nao disse quantos. NAO e "apagou zero". */
  eventos_removidos: number | null;
  janelas_removidas: number | null;
  /** Sempre `true`: o mosaico serve as outras tres cameras e a aba de audio. */
  manteve_video_do_exame: boolean;
}

/* ------------------------------ A configuracao ---------------------------- */

/** As alavancas editaveis de UMA camera numa categoria. */
export interface ConfigVisaoCategoria {
  papel: PapelCamera;
  categoria: string;
  rotulo: string;
  modelo: string;
  /** Minimo de 40 caracteres — a API recusa menos. */
  prompt_sistema: string;
  /** Minimo de 200 caracteres. Tem marcadores ({duracao}, {fps}) obrigatorios. */
  prompt_pergunta: string;
  /** A taxa em que o ARQUIVO da janela e reamostrado. 0 < fps <= 30. */
  fps: number;
  /** A taxa DECLARADA ao modelo. Campo separado porque os dois ja divergiram. */
  fps_hint: number;
  /** 0 < janela_s <= 600. */
  janela_s: number;
  /** >= 0, e no maximo METADE da janela: acima disso a fatia sobe tres vezes. */
  sobreposicao_s: number;
  /** > 1900 e <= 65536. Abaixo do piso medido TODA janela trunca. */
  max_tokens: number;
  teto_raciocinio: number | null;
  /** `false` = nunca foi salva; o que esta na tela e o padrao de fabrica. */
  configurada: boolean;
  atualizado_em: string | null;
  atualizado_por: string | null;
  /** `true` quando os dois prompts sao byte a byte os de fabrica. */
  prompt_e_padrao: boolean;
}

/** O que a tela MANDA para uma categoria — sem os campos so de leitura. */
export type ConfigVisaoCategoriaEscrita = Pick<
  ConfigVisaoCategoria,
  | "categoria"
  | "modelo"
  | "prompt_sistema"
  | "prompt_pergunta"
  | "fps"
  | "fps_hint"
  | "janela_s"
  | "sobreposicao_s"
  | "max_tokens"
  | "teto_raciocinio"
>;

/** Uma aba da tela de configuracao: a camera e as categorias dela. */
export interface ConfigVisaoCamera {
  papel: PapelCamera;
  rotulo: string;
  resumo: string;
  categorias: ConfigVisaoCategoria[];
  /**
   * NAO CONFIGURAVEL, e viaja so para a tela mostrar o que esta em vigor: a
   * taxonomia entra no esquema que prende a resposta do modelo e na assinatura
   * das regras. Editavel, permitiria inventar um tipo que nenhum consumidor a
   * jusante sabe ler, sem nada quebrar no caminho.
   */
  taxonomia: Record<string, string[]>;
  atores: string[];
  /** Vazios na interna; preenchidos nas cameras de via. */
  proximidades: string[];
  estados_semaforo: string[];
  /** Digest das duas reguas de linguagem desta camera, hoje. */
  regua: string;
}

/** Tudo que o modal precisa, numa resposta so: as quatro abas montadas. */
export interface ConfigVisao {
  cameras: ConfigVisaoCamera[];
  /** Mesmo formato do audio e do mapa: {id, rotulo, nota}. */
  modelos: OpcaoModelo[];
  /**
   * Os numeros que NAO se configuram por camera (temperatura, resolucao de
   * midia, tentativas, teto de envio, as tres defesas). Vem do backend para a
   * tela nao redeclarar nenhum deles em TypeScript.
   */
  parametros: Record<string, unknown>;
  /** A explicacao do processo, na voz da tela. Mesmo formato do audio. */
  processo: EtapaDoProcesso[];
  limites: string[];
}

/* -------------------------------- As chamadas ----------------------------- */

/**
 * Estas rotas leem disco (medem artefatos), varrem eventos e carregam prompts
 * inteiros — o default de 10 s e curto. O RAW de um exame de 6 minutos passa
 * de 1 MB de texto.
 */
const TIMEOUT_VISAO_MS = 30_000;

/** `/visao/exames/{hash}` — as quatro, o portao e o custo previsto. */
export async function getVisaoDoExame(
  hash: string,
  signal?: AbortSignal,
): Promise<VisaoDoExame> {
  return request<VisaoDoExame>(`/visao/exames/${encodeURIComponent(hash)}`, {
    signal,
    timeoutMs: TIMEOUT_VISAO_MS,
  });
}

/**
 * `/visao/exames/{hash}/{papel}` — o mesmo item da rota acima, sozinho.
 *
 * A conferencia do portao continua olhando os QUATRO papeis: a divergencia de
 * um bloqueia os quatro.
 */
export async function getEstadoDaCamera(
  hash: string,
  papel: PapelCamera,
  signal?: AbortSignal,
): Promise<EstadoDaCamera> {
  return request<EstadoDaCamera>(
    `/visao/exames/${encodeURIComponent(hash)}/${papel}`,
    { signal, timeoutMs: TIMEOUT_VISAO_MS },
  );
}

/**
 * Dispara UMA camera. Responde 202 e volta na hora — quem acompanha e o
 * polling.
 *
 * GASTA DINHEIRO: uma chamada PAGA por janela. O botao tem de dizer quanto
 * (`estado.estimativa.usd`) ANTES do clique.
 *
 * 409 com frase legivel em tres casos, e NENHUM deles e falha: ja ha rodada em
 * andamento, o portao esta fechado, ou o exame nao e categoria B. Mostre o
 * `describeError` como aviso, nao como erro vermelho.
 */
export async function processarCameraDeVisao(
  hash: string,
  papel: PapelCamera,
  signal?: AbortSignal,
): Promise<EstadoDaCamera> {
  return request<EstadoDaCamera>(
    `/visao/exames/${encodeURIComponent(hash)}/${papel}/processar`,
    { method: "POST", signal, timeoutMs: TIMEOUT_VISAO_MS },
  );
}

/**
 * Dispara AS QUATRO, com um portao so. TODAS OU NENHUMA: se uma estiver
 * bloqueada, nenhuma e despachada.
 *
 * O custo e `visao.custo_previsto_usd` — quatro vezes o de uma camera, com a
 * interna rodando a 4 fps no modelo mais caro.
 */
export async function processarAsQuatroCameras(
  hash: string,
  signal?: AbortSignal,
): Promise<VisaoDoExame> {
  return request<VisaoDoExame>(
    `/visao/exames/${encodeURIComponent(hash)}/processar`,
    { method: "POST", signal, timeoutMs: TIMEOUT_VISAO_MS },
  );
}

/** Monta a query de `/eventos` sem mandar o que nao foi pedido. */
function queryDeEventos(filtro: FiltroDeEventos | undefined): string {
  if (!filtro) return "";
  const params = new URLSearchParams();
  if (filtro.revisao) params.set("revisao", filtro.revisao);
  if (filtro.tipo) params.set("tipo", filtro.tipo);
  // `marcados=false` e o default do backend; mandar so quando ligado mantem a
  // URL (e a chave de cache do browser) igual a de quem nao filtrou.
  if (filtro.marcados) params.set("marcados", "true");
  if (filtro.limite !== undefined) params.set("limite", String(filtro.limite));
  const texto = params.toString();
  return texto === "" ? "" : `?${texto}`;
}

/**
 * Os eventos publicados, com as MARCAS RECALCULADAS na leitura.
 *
 * A marca gravada envelhece: um run que terminou antes de `alerta_objeto`
 * existir continuaria "limpo" enquanto um run novo aparece marcado pela MESMA
 * frase. As duas viajam juntas em `atributos.regua`.
 */
export async function getEventosDaCamera(
  hash: string,
  papel: PapelCamera,
  filtro?: FiltroDeEventos,
  signal?: AbortSignal,
): Promise<EventosDaCamera> {
  return request<EventosDaCamera>(
    `/visao/exames/${encodeURIComponent(hash)}/${papel}/eventos${queryDeEventos(filtro)}`,
    { signal, timeoutMs: TIMEOUT_VISAO_MS },
  );
}

/**
 * Confirmar, rejeitar ou corrigir. A correcao NUNCA toca a previsao do modelo.
 *
 * A resposta traz o evento ja recalculado pela regua de hoje: corrigir a
 * descricao pode acender ou apagar uma marca, e a tela ve isso no mesmo
 * clique. Troque o item na lista pelo que voltou — nao remende o local.
 */
export async function revisarEvento(
  eventoId: number,
  corpo: CorpoDaRevisao,
  signal?: AbortSignal,
): Promise<EventoDeVisao> {
  return request<EventoDeVisao>(`/visao/eventos/${eventoId}/revisar`, {
    method: "POST",
    body: corpo,
    signal,
  });
}

/** A trilha deste evento, em ordem cronologica. Vazia = nunca revisado. */
export async function getRevisoesDoEvento(
  eventoId: number,
  signal?: AbortSignal,
): Promise<RevisaoDoEvento[]> {
  return request<RevisaoDoEvento[]>(`/visao/eventos/${eventoId}/revisoes`, { signal });
}

/**
 * Limpa o resultado DESTA camera. O video do exame (o mosaico) fica: ele serve
 * as outras tres e a aba de audio.
 *
 * Recusa com 409 enquanto houver rodada em andamento.
 */
export async function limparCameraDeVisao(
  hash: string,
  papel: PapelCamera,
  signal?: AbortSignal,
): Promise<LimpezaDaCamera> {
  return request<LimpezaDaCamera>(
    `/visao/exames/${encodeURIComponent(hash)}/${papel}`,
    { method: "DELETE", signal, timeoutMs: TIMEOUT_VISAO_MS },
  );
}

/** A configuracao das quatro cameras — tudo que o modal precisa, numa chamada. */
export async function getConfigVisao(signal?: AbortSignal): Promise<ConfigVisao> {
  return request<ConfigVisao>("/config/visao", { signal, timeoutMs: TIMEOUT_VISAO_MS });
}

/**
 * O RECORTE de UM quadrante, servido com Range pelo backend.
 *
 * NAO E `urlDoVideoDoExame`, que aponta para o mosaico inteiro guardado pela
 * aba de Audio. Esta serve o `<papel>.mp4` — o quadrante ja cortado, no MESMO
 * relogio do exame, e e ela que o player da aba de cada camera usa.
 *
 * O `<video>` PRECISA de `crossOrigin="use-credentials"`: sem ele o cookie de
 * sessao nao viaja, o backend responde 401, e o player fica mudo sem nenhum
 * erro na tela.
 *
 * 404 antes de processar, e isso e correto — o recorte so existe depois. Use
 * `estado.video_disponivel` para decidir se desenha o player.
 */
export function urlDoVideoDaCamera(hash: string, papel: PapelCamera): string {
  return `${API_BASE_URL}/visao/exames/${encodeURIComponent(hash)}/${papel}/video`;
}

/* -------------------------------------------------------------------------- */
/* Fusao Temporal — o que aconteceu no exame, em ordem cronologica             */
/* -------------------------------------------------------------------------- */

/**
 * A SEXTA ETAPA, e a unica que NAO chama modelo para olhar midia.
 *
 * Ela le o que as CINCO fontes ja publicaram — a aba Audio
 * (`audio_processamento.resultado`) e as quatro abas de camera
 * (`visao_eventos`) — e organiza aquilo no tempo. Nenhum quadro, nenhum audio,
 * nenhuma janela de video: o material ja foi pago. O unico gasto possivel aqui
 * e a leitura de uma DIVERGENCIA por texto, e ela e desligavel.
 *
 * Espelha `backend/app/schemas/fusao.py` campo a campo, com as duas regras que
 * este arquivo ja paga desde o bloco da visao:
 *
 *   UNIAO FECHADA, NUNCA `string`. `estado`, `especie`, `assunto`, `fonte` e
 *   `nivel` sao DERIVADOS de um array `as const`: a lista serve ao runtime (os
 *   filtros, a regua, os rotulos) e o tipo sai dela, entao as duas nao podem
 *   divergir. Do lado do backend as mesmas listas sao `Literal` conferido no
 *   import — um valor novo quebra a compilacao dos dois lados, e nao passa
 *   despercebido num `default`.
 *
 *   `null` E "NAO MEDIDO" E SOBREVIVE ATE A TELA. O caso que mais importa
 *   aqui: `fim_ms: null` quer dizer "nenhuma fonte fechou o fim", NAO "durou
 *   zero" — a tela escreve "fim não determinado". Nenhuma funcao deste arquivo
 *   troca esse `null` por 0 no caminho. Contador que a execucao de fato contou
 *   (`n_momentos`, `n_origens`, `tokens_entrada`) e `number`, porque ali o zero
 *   foi medido.
 *
 * OS TRES NIVEIS, que sao o desenho inteiro da aba:
 *
 *   `MomentoFusao`  "o que estava acontecendo em 01:58"   <- a aba LISTA isto
 *      -> `FatoFusao`    "o que um conjunto de fontes afirma"  (expandir 1x)
 *          -> `OrigemFusao` "qual evento bruto, de qual execucao" (expandir 2x)
 */

/**
 * As SEIS fontes, na ordem da tela — `app/fusao/padroes.FONTES`.
 *
 * O audio primeiro, a seta (o rele medido NO audio, que por isso nao o
 * corrobora — `regras.DERIVA_DE`) logo depois, e as quatro cameras na ordem
 * canonica de `PAPEIS_CAMERA`. Tres listas com a mesma ordem (esta, a da visao
 * e a do backend) e nenhuma delas pode reordenar sozinha.
 *
 * A "seta" FALTAVA AQUI ate 26/09/2026 (o backend publica seis desde a etapa
 * da seta): o filtro de fontes da aba escondia o momento so de seta, e a
 * ancora de seta caia em "fonte não publicada".
 */
export const FONTES_FUSAO = [
  "audio",
  "seta",
  "interna",
  "frontal",
  "lateral",
  "traseira",
] as const;

/** audio | seta | interna | frontal | lateral | traseira. */
export type FonteFusao = (typeof FONTES_FUSAO)[number];

/**
 * OS OITO ESTADOS da fusao de um exame. Sete sao de `app/fusao/padroes.ESTADOS`;
 * o oitavo e `ausente`, que NAO existe como linha no banco — e a resposta para
 * "este exame nunca foi fundido", e a tela precisa dela para desenhar o botao
 * Processar. Um 404 no lugar obrigaria a tratar erro para descrever o estado
 * mais normal que existe.
 *
 * `bloqueado` NAO E ERRO: e falta de etapa anterior (alguma das cinco fontes
 * ainda nao ficou pronta), e `nao_compativel` e recusa de escopo (categoria
 * fora de B). Pintar os dois de vermelho ensina quem olha a ignorar vermelho.
 */
export const ESTADOS_FUSAO = [
  "ausente",
  "pendente",
  "processando",
  "pronto",
  "erro",
  "interrompido",
  "nao_compativel",
  "bloqueado",
] as const;

export type EstadoFusao = (typeof ESTADOS_FUSAO)[number];

/** Os dois estados em que a rodada esta VIVA — o polling so continua neles. */
export const ESTADOS_FUSAO_EM_ANDAMENTO = [
  "pendente",
  "processando",
] as const satisfies readonly EstadoFusao[];

/** `true` enquanto ha trabalho — quem faz polling para quando isto vira false. */
export function fusaoEmAndamento(estado: EstadoFusao): boolean {
  return (ESTADOS_FUSAO_EM_ANDAMENTO as readonly EstadoFusao[]).includes(estado);
}

/**
 * O DESFECHO de um fato — `app/fusao/padroes.ESPECIES`.
 *
 * DIVERGENCIA e AMBIGUIDADE sao PRODUTO desta etapa, nao defeito dela: o
 * criterio de acerto da fusao e conseguir dizer "não sei". Uma timeline limpa e
 * falsa e pior que uma com buracos declarados, porque a primeira e usada e a
 * segunda e investigada — entao as duas aparecem na mesma lista dos fatos, com
 * MAIS peso visual e nao menos.
 */
export const ESPECIES_FUSAO = [
  "FATO_FUNDIDO",
  "HIPOTESE_CORROBORADA",
  "HIPOTESE_NAO_CORROBORADA",
  "OBSERVACAO_ISOLADA",
  "DIVERGENCIA_TEMPORAL",
  "AMBIGUIDADE_TEMPORAL",
] as const;

export type EspecieFusao = (typeof ESPECIES_FUSAO)[number];

/**
 * Os SETE grupos da tira de filtros, na ordem em que aparecem
 * (`app/fusao/padroes.ASSUNTOS`). Um fato tem UM assunto; um momento carrega a
 * lista dos assuntos que ha dentro dele.
 */
export const ASSUNTOS_FUSAO = [
  "fala",
  "seta",
  "motor",
  "movimento",
  "condutor",
  "via",
  "outros",
] as const;

export type AssuntoFusao = (typeof ASSUNTOS_FUSAO)[number];

/**
 * OBSERVACAO ou INTERPRETACAO — `app/fusao/regras.NIVEL_POR_EVIDENCIA`.
 *
 * "A roda está a X do meio-fio" e observacao contra referencia fixa; "o cenário
 * traseiro aproxima-se" e interpretacao de fluxo optico. Dar o mesmo peso aos
 * dois foi o que quase promoveu a fato, no legado, 43 s de marcha a re que nao
 * aconteceram. O que a tabela nao lista entra como `DERIVADA`.
 */
export const NIVEIS_FUSAO = ["DIRETA", "DERIVADA", "HIPOTESE"] as const;

export type NivelFusao = (typeof NIVEIS_FUSAO)[number];

/**
 * Quem PODERIA ter visto — `app/fusao/regras.MATRIZ_DE_CAPACIDADE`.
 *
 * Serve para UMA coisa so: qualificar o silencio de uma fonte. Nunca para
 * refutar nada.
 */
export const CAPACIDADES_FUSAO = ["sim", "parcial", "nao"] as const;

export type CapacidadeFusao = (typeof CAPACIDADES_FUSAO)[number];

/**
 * Os rotulos, copiados de `app/fusao/padroes` — e sao a MESMA grafia que o
 * backend usa no texto que ele monta (`texto`, `porque`, `FonteDaFusao.rotulo`).
 *
 * Use o `rotulo` que vier na resposta quando ele vier; estes mapas existem para
 * as listas que carregam so a chave (`fontes`, `ausentes`, `assuntos`), onde
 * nao ha rotulo do lado do backend para copiar.
 */
export const ROTULO_FONTE_FUSAO: Record<FonteFusao, string> = {
  audio: "Áudio",
  seta: "Seta",
  interna: "Interna",
  frontal: "Frontal",
  lateral: "Lateral",
  traseira: "Traseira",
};

export const ROTULO_ASSUNTO_FUSAO: Record<AssuntoFusao, string> = {
  fala: "Fala",
  seta: "Seta",
  motor: "Motor",
  movimento: "Movimento",
  condutor: "Condutor",
  via: "Via",
  outros: "Outros",
};

export const ROTULO_ESPECIE_FUSAO: Record<EspecieFusao, string> = {
  FATO_FUNDIDO: "Fato fundido",
  HIPOTESE_CORROBORADA: "Hipótese corroborada",
  HIPOTESE_NAO_CORROBORADA: "Hipótese não corroborada",
  OBSERVACAO_ISOLADA: "Observação isolada",
  DIVERGENCIA_TEMPORAL: "Divergência",
  AMBIGUIDADE_TEMPORAL: "Ambiguidade",
};

/* ------------------------- Nivel 3 — a observacao ------------------------- */

/**
 * DE ONDE VEIO: o evento de um extrator, como a fusao o leu.
 *
 * A fusao NAO altera, NAO apaga e NAO "corrige" a origem — `visao_eventos` e
 * `audio_processamento` saem daqui exatamente como entraram. Isto e uma copia
 * de leitura, e e o terceiro clique da aba.
 */
export interface OrigemFusao {
  id: number;
  fonte: FonteFusao;
  /** `visao_eventos.id` quando a fonte e camera; `null` no audio, que nao tem tabela. */
  origem_id: number | null;
  /** Sempre preenchida: `"1234"` (visao) ou `"fala:3"` / `"som:17"` (audio). */
  origem_chave: string;
  /** `jNNN#NNN` — liga a resposta CRUA da janela daquela camera. */
  raw_id: string | null;
  janela_indice: number | null;

  tipo: string;
  subtipo: string | null;
  ator: string | null;
  descricao: string | null;

  inicio_ms: number;
  /** `null` = a fonte nao fechou o fim. Nao e "instantâneo". */
  fim_ms: number | null;
  /** A incerteza do INSTRUMENTO, nao do fenomeno: 1.500 ms no audio, 2.000 nas cameras. */
  precisao_ms: number;
  nivel: NivelFusao;
  classe: string | null;
  /**
   * Metadado do detector sobre a propria frase. NAO DECIDE NADA nesta etapa —
   * viaja so para a tela. `null` = o modelo nao disse, e nao e confianca zero.
   */
  confianca: number | null;
  /**
   * O fim coincide com a borda da janela processada: ele nao foi OBSERVADO, foi
   * onde a janela acabou. Tratar isto como fim real e uma das cinco coisas que
   * a camada tem proibido.
   */
  limite_de_janela: boolean;
  fim_determinado: boolean;
  capacidade: CapacidadeFusao | null;
  /** Esta observacao entrou em mais de um fato. */
  reusada: boolean;
  /**
   * A classe do instante (E2.8): MEDIDO | ESTIMADO | SEM_SINCRONIA. `null` =
   * fusao anterior a E2. Opcional ate o backend novo estar no ar.
   */
  origem_tempo?: OrigemTempoFusao | null;
}

/* ----------------------------- Nivel 2 — o fato --------------------------- */

/** O QUE UM CONJUNTO DE OBSERVACOES AFIRMA, e por que este desfecho. */
export interface FatoFusao {
  id: number;
  ordem: number;
  especie: EspecieFusao;
  assunto: AssuntoFusao;
  classe: string | null;
  estado_movimento: string | null;
  subtipo: string | null;
  nivel: NivelFusao | null;

  inicio_ms: number;
  /** `null` = NENHUMA fonte fechou o fim. Nao e zero, nao e instantâneo. */
  fim_ms: number | null;
  fim_determinado: boolean;
  duracao_ms: number | null;
  precisao_ms: number | null;
  /** A observacao que deu o instante publicado — 12:08 e 12:11 nao viram 12:09,5. */
  ancora: Record<string, unknown> | null;
  coincidencia_ms: number[] | null;
  sobreposicao_observada: boolean;

  fontes: FonteFusao[];
  /** Quantas fontes realmente distintas ha aqui (`regras.fontes_independentes`). */
  fontes_independentes: number;
  /** Quem PODERIA ter visto, OLHOU e ficou calado. Silêncio nao refuta. */
  ausentes: FonteFusao[];
  /**
   * Quem poderia ter visto e NAO OLHOU este trecho (E2.1): camera nao
   * solicitada, lateral sob demanda fora das janelas dela, camera tirada pela
   * ficha de qualidade. Nunca e silencio. Opcional ate o backend novo.
   */
  nao_consultadas?: FonteFusao[];
  /** A classe do instante do fato: a da ANCORA, nunca a media. */
  origem_tempo?: OrigemTempoFusao | null;
  /**
   * Quem publicou a MESMA classe aqui e o agrupamento nao uniu. Isto e falha de
   * AGRUPAMENTO, nao ausencia — e a tela tem de dizer a diferenca.
   */
  publicaram_perto: FonteFusao[];
  em_divergencia: boolean;

  titulo: string;
  texto: string;
  porque: string | null;

  /** So em DIVERGENCIA: os lados do conflito, como o motor os montou. */
  lados: Array<Record<string, unknown>>;
  confrontos: Array<Record<string, unknown>>;
  resolvida: boolean;
  lado_predominante: string | null;
  /**
   * Opiniao do modelo anexada ao conflito. NUNCA RESOLVE NADA: `execucao.py` nao
   * escreve `resolvida` a partir dela, e e isso que torna seguro desligar a IA.
   */
  leitura_ia: string | null;
  chamada_id: number | null;

  origens: OrigemFusao[];
}

/* --------------------------- Nivel 1 — o momento -------------------------- */

/**
 * O QUE ESTAVA ACONTECENDO — a unidade que a aba LISTA.
 *
 * E um agrupamento de APRESENTACAO: ele nao afirma nada alem do que os fatos
 * dentro dele afirmam, e a duracao publicada continua vindo deles. Existe
 * porque listar fato a fato devolve ao humano o trabalho que a fusao existia
 * para fazer — 345 linhas num exame de 6 minutos, no legado.
 */
export interface MomentoFusao {
  id: number;
  ordem: number;
  inicio_ms: number;
  fim_ms: number | null;
  fim_determinado: boolean;
  duracao_ms: number | null;
  precisao_ms: number | null;

  titulo: string;
  resumo: string | null;
  especie: EspecieFusao;
  assunto: AssuntoFusao;
  assuntos: AssuntoFusao[];

  fontes: FonteFusao[];
  /**
   * Quantas fontes INDEPENDENTES sustentam o momento. A tela chama isto de
   * CORROBORACAO e nao de confianca, porque sao coisas diferentes: confianca e
   * o detector falando de si, corroboracao e outra fonte tendo visto o mesmo.
   */
  corroboracao: number;
  n_fatos: number;
  n_origens: number;
  tem_divergencia: boolean;
  ausentes: FonteFusao[];
  /** `{ texto, falante }` para fala; vazio no resto. */
  detalhe: Record<string, unknown> | null;

  fatos: FatoFusao[];
}

/**
 * TRECHO EM QUE NINGUEM PUBLICOU NADA — a unica linha da timeline sem fonte.
 *
 * Nao e "nada aconteceu": e um pedaco do exame que cinco fontes atravessaram
 * sem descrever. Entra entre os momentos porque buraco declarado se investiga e
 * buraco implicito nao.
 */
export interface LacunaFusao {
  inicio_ms: number;
  fim_ms: number;
  duracao_ms: number;
  texto: string;
}

/**
 * UM VINCULO ENTRE MODALIDADES, com a conta inteira da tolerancia.
 *
 * Correlacao NAO funde nada e nao cria momento: ela diz "esta seta e aquela
 * conversão estão ligadas pela regra X". `conta` traz a tolerancia do FENOMENO
 * e a precisao das duas fontes SEPARADAS — "±2 s" sem dizer de onde saiu nao e
 * auditavel, e foi assim que o legado produziu achado falso.
 */
export interface CorrelacaoFusao {
  id: number;
  ordem: number;
  /** O nome da regra em `app/fusao/regras.REGRAS_TEMPORAIS`. */
  regra: string;
  versao_regra: string;
  so_hipotese: boolean;
  inicio_ms: number;
  fim_ms: number | null;
  nivel: NivelFusao | null;
  distancia_ms: number | null;
  distancia_do_fim_ms: number | null;
  de: Record<string, unknown> | null;
  para: Record<string, unknown> | null;
  conta: Record<string, unknown> | null;
  texto: string | null;
  porque: string | null;
}

/**
 * A PERGUNTA EXATA feita ao modelo sobre uma divergencia, e a resposta.
 *
 * E a resposta para "isto o modelo disse ou vocês derivaram?". A chamada mais
 * barata do portal: texto puro, sem video.
 */
export interface ChamadaFusao {
  id: number;
  ordem: number;
  alvo: string | null;
  modelo: string | null;
  prompt_id: string | null;
  prompt_versao: string | null;
  prompt_sistema: string | null;
  prompt_pergunta: string | null;
  esquema: Record<string, unknown> | null;
  temperatura: number | null;
  max_tokens: number | null;
  resposta: string | null;
  razao_parada: string | null;
  tentativas: number;
  tokens_entrada: number;
  tokens_saida: number;
  tokens_raciocinio: number;
  /** `null` = modelo fora da tabela de precos. NAO e o mesmo que zero. */
  custo_usd: number | null;
  custo_descartado_usd: number | null;
  duracao_chamada_s: number | null;
  erro: string | null;
  criado_em: string | null;
}

/**
 * UMA DAS CINCO, como a fusao a encontrou.
 *
 * `atualizado_em` existe para a tela poder dizer que a camera foi reprocessada
 * DEPOIS da fusao — a timeline na tela estaria velha, e sem esta data ninguem
 * descobriria.
 */
export interface FonteDaFusao {
  fonte: FonteFusao;
  rotulo: string;
  /** O estado da etapa de ORIGEM (audio ou visao), nao o desta aba. */
  estado: string;
  pronta: boolean;
  /** `null` = nao contado. Zero e "rodou e nao publicou nada", que e diferente. */
  n_eventos: number | null;
  atualizado_em: string | null;
  /** Quando a fonte nao esta pronta: a frase que diz o que fazer. */
  nota: string | null;
}

/* ------------------------ O estado (leve — polling) ----------------------- */

/** Onde a fusao deste exame esta, o que ela produziu e se pode rodar. */
export interface FusaoDoExame {
  exame_hash: string;
  estado: EstadoFusao;
  progresso: number;
  etapa: string | null;

  versao: string | null;
  /** A assinatura com que ESTE resultado foi produzido. */
  assinatura_regras: string | null;
  /**
   * A assinatura de HOJE. Diferente da de cima = a regua mudou desde a rodada,
   * e comparar os dois numeros compara reguas diferentes. Isso e o mecanismo
   * funcionando, nao falha.
   */
  assinatura_vigente: string | null;
  categoria: string | null;
  duracao_s: number | null;

  escolhas: Record<string, unknown> | null;
  sincronia: Record<string, unknown> | null;
  configuracao: Record<string, unknown> | null;
  /**
   * `EstadoDoPortao.para_api()`. Carrega `e_falha`, que e SEMPRE `false`: portao
   * fechado e falta de trabalho feito, nao falha.
   */
  portao: Record<string, unknown> | null;
  /** Atalho do que a tela pergunta primeiro: o botao Processar fica ativo? */
  liberado: boolean;
  fontes: FonteDaFusao[];
  /** Alguma fonte foi reprocessada depois desta fusao? */
  fontes_mudaram: boolean;
  /** As fontes CORROBORATIVAS (T4) e o estado de cada uma. Nunca fecham. */
  fontes_corroborativas?: Record<string, string>;
  /** A faixa corroborativa gravada (`corroboracao.vincula`); `null` = sem faixa. */
  corroborativas?: CorroborativasDaFusao | null;
  /** A telemetria foi reprocessada depois desta fusao? */
  corroborativas_mudaram?: boolean;
  /** A regua de HOJE da faixa corroborativa — propria, nao move a da fusao. */
  assinatura_corroboracao_vigente?: string | null;

  funil: Array<Record<string, unknown>> | null;
  metricas: Record<string, unknown> | null;
  qualidade: Record<string, unknown> | null;
  /** A frase final do run — `app/fusao/padroes.RESULTADOS`. */
  resultado: string | null;

  n_observacoes: number;
  n_momentos: number;
  n_fatos: number;
  n_fatos_fundidos: number;
  n_hipoteses: number;
  n_isoladas: number;
  n_divergencias: number;
  n_ambiguidades: number;
  n_correlacoes: number;
  n_lacunas: number;

  usa_ia: boolean;
  modelo_ia: string | null;
  n_chamadas: number;
  tokens_entrada: number;
  tokens_saida: number;
  tokens_raciocinio: number;
  /** `null` = modelo fora da tabela de precos. NAO e zero. */
  custo_usd: number | null;
  custo_descartado_usd: number | null;
  /** O que ESTA rodada custa se rodar agora. `null` = nao da para estimar. */
  estimativa: Record<string, unknown> | null;

  /** O mosaico 2x2 que a aba Audio ja baixou. `false` = esconda o player. */
  video_disponivel: boolean;
  video_url: string | null;

  erro: string | null;
  atualizado_em: string | null;
  atualizado_por: string | null;
}

/* --------------------- A timeline (pesada — uma vez) ---------------------- */

/** TUDO O QUE A ABA DESENHA, numa resposta so. */
export interface TimelineDaFusao {
  exame_hash: string;
  estado: EstadoFusao;
  duracao_s: number | null;
  assinatura_regras: string | null;

  momentos: MomentoFusao[];
  lacunas: LacunaFusao[];
  correlacoes: CorrelacaoFusao[];

  /**
   * Os numeros da tira de filtros. VEM DO BACKEND porque a tela filtra sobre o
   * mesmo criterio, e duas contas do mesmo numero divergem.
   */
  por_assunto: Record<string, number>;
  por_especie: Record<string, number>;
  /** Observacoes BRUTAS por fonte — o "186 eventos de origem" do cabecalho. */
  por_fonte: Record<string, number>;

  /** As tabelas da doutrina, para a secao "Regras da fusão". */
  regras_temporais: Array<Record<string, unknown>>;
  matriz_de_capacidade: Record<string, Record<string, string>>;
  capacidades_sem_lastro: Record<string, string[]>;
  /** PRIMARIA | CORROBORATIVA | CONTEXTUAL por fonte (E2.8). */
  papel_da_fonte?: Record<string, string>;
  /** A coluna da telemetria, FORA da matriz da fusao (T4). Nunca `sim`. */
  capacidade_corroborativa?: Record<string, Record<string, string>>;
}

/** O que a limpeza levou. O VIDEO NUNCA: ele e da aba Audio. */
export interface LimpezaFusao {
  exame_hash: string;
  removeu_execucao: boolean;
  momentos: number;
  fatos: number;
  origens: number;
  correlacoes: number;
  chamadas: number;
}

/* ------------------------------ A configuracao ---------------------------- */

/**
 * A configuracao de UMA categoria, como a tela a edita.
 *
 * SO A LEITURA DAS DIVERGENCIAS E CONFIGURAVEL. A doutrina — niveis de
 * evidencia, matriz de capacidade, regras temporais, tolerancias — mora em
 * `app/fusao/regras.py`, em codigo, e entra na assinatura que viaja com cada
 * rodada. Editavel na tela, permitiria "resolver" uma divergencia inconveniente
 * alargando a folga ate ela sumir, sem nada quebrar e sem ninguem saber depois.
 */
export interface ConfigFusaoItem {
  categoria: string;
  usa_ia: boolean;
  modelo: string;
  /** 0..60. Cada unidade e uma chamada paga a mais por exame. */
  teto_chamadas: number;
  /** 1.024..32.768. O raciocinio do Gemini 3 conta contra este teto. */
  max_tokens: number;
  prompt_sistema: string;
  /** Precisa dos marcadores `{inicio}`, `{fim}` e `{lados}` — a API recusa sem. */
  prompt_pergunta: string;

  /**
   * Alguem SALVOU esta categoria alguma vez? Distingue "ninguém mexeu" de
   * "alguém escolheu exatamente o padrão" — sem isso as duas sao
   * indistinguiveis na tela, e a segunda e uma decisao.
   */
  configurada: boolean;
  /** Os dois prompts sao byte a byte os de `app/fusao/prompts/`? */
  prompt_e_padrao: boolean;
  /** O que diverge do padrao, campo a campo (prompts por digest). */
  divergencias: string[];
  atualizado_em: string | null;
  atualizado_por: string | null;
}

/** O PUT. So o que se edita — o resto e derivado na leitura. */
export interface ConfigFusaoCorpo {
  usa_ia: boolean;
  modelo: string;
  teto_chamadas: number;
  max_tokens: number;
  prompt_sistema: string;
  prompt_pergunta: string;
}

/**
 * O GET de `/config/fusao` — tudo o que a secao do modal desenha.
 *
 * `parametros`, `processo` e `limites` sao listas/dicionarios OPACOS de
 * proposito: os textos explicativos vem do backend para a tela nao redeclarar
 * constante em TypeScript. Duas copias do mesmo numero divergem, e a que fica
 * errada e sempre a da tela.
 */
export interface ConfigFusao {
  categorias: ConfigFusaoItem[];
  parametros: Array<Record<string, unknown>>;
  processo: Array<Record<string, unknown>>;
  limites: Record<string, unknown>;
  /** IDs crus (`app/fusao/padroes.MODELOS`), sem rotulo nem nota. */
  modelos: string[];
  assinatura_vigente: string | null;
}

/* -------------------------------- As chamadas ----------------------------- */

/**
 * A TIMELINE E PESADA e o default de 10 s nao a cobre: sao centenas de momentos
 * com os fatos e as origens aninhados dentro, mais a regua com uma marca por
 * observacao bruta. Ela e buscada UMA vez por rodada, e nunca no polling — que
 * usa `getFusaoDoExame`, leve de proposito.
 */
const TIMEOUT_FUSAO_MS = 30_000;

/** `/fusao/exames/{hash}` — o estado leve. E este que entra no polling de 3 s. */
export async function getFusaoDoExame(
  hash: string,
  signal?: AbortSignal,
): Promise<FusaoDoExame> {
  return request<FusaoDoExame>(`/fusao/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Momentos, lacunas, regua, correlacoes e as tabelas da doutrina, de uma vez. */
export async function getTimelineDaFusao(
  hash: string,
  signal?: AbortSignal,
): Promise<TimelineDaFusao> {
  return request<TimelineDaFusao>(
    `/fusao/exames/${encodeURIComponent(hash)}/timeline`,
    { signal, timeoutMs: TIMEOUT_FUSAO_MS },
  );
}

/* ------------------ A janela (leve — um trecho do exame) ------------------ */

/**
 * UM FATO DA JANELA: o `FatoFusao` da timeline SEM as origens (o backend nem as
 * envia) e com a frase pronta. `schemas/fusao.FatoDaJanela`.
 */
export interface FatoDaJanela extends Omit<FatoFusao, "origens"> {
  /**
   * As frases das origens, sem repetição, unidas por " · " — a mesma conta da
   * `LinhaDoFato` da aba de fusão, feita no backend. `null` = nenhuma origem
   * escreveu frase.
   */
  descricao: string | null;
  /** O fato começou antes do início da janela. */
  comeca_antes: boolean;
  /**
   * O fim OBSERVADO cai depois da janela. Fim não determinado é `false` — para
   * ele existe `fim_determinado` ("fim não determinado", nunca "continuou").
   */
  segue_depois: boolean;
}

/**
 * `/fusao/exames/{hash}/janela` — os fatos e as correlações que tocam um trecho
 * de até 120 s, recortados POR FATO (`inicio_ms <= ate` e `fim_ms ?? inicio_ms
 * >= de`). É o painel "Fusão deste momento" da análise de infrações; a aba de
 * fusão continua com a timeline inteira. 26/09/2026.
 */
export interface JanelaDaFusao {
  exame_hash: string;
  /** `ausente` = o exame nunca foi fundido; as listas vêm vazias. Não é erro. */
  estado: EstadoFusao;
  de_ms: number;
  ate_ms: number;
  /** Em ordem de tempo (`inicio_ms`, depois `id`). */
  fatos: FatoDaJanela[];
  correlacoes: CorrelacaoFusao[];
  /**
   * Quantos FATOS da janela cada fonte assina — as seis, com zero (silêncio
   * também é informação). Não é o `por_fonte` da timeline, que conta
   * observações brutas.
   */
  por_fonte: Record<FonteFusao, number>;
  /**
   * Itens de áudio do EXAME INTEIRO que ficaram fora da timeline por não terem
   * instante medido. Maior que zero = "áudio 0" na janela NÃO é silêncio.
   */
  audio_fora: number;
}

/**
 * Um trecho da fusão, em segundos do vídeo original. O backend recusa com 422
 * `deS < 0`, `ateS <= deS` e janela maior que 120 s — quem chama limita antes.
 */
export async function getJanelaDaFusao(
  hash: string,
  deS: number,
  ateS: number,
  signal?: AbortSignal,
): Promise<JanelaDaFusao> {
  const consulta = new URLSearchParams({ de_s: String(deS), ate_s: String(ateS) });
  return request<JanelaDaFusao>(
    `/fusao/exames/${encodeURIComponent(hash)}/janela?${consulta.toString()}`,
    { signal, timeoutMs: TIMEOUT_FUSAO_MS },
  );
}

/**
 * UMA arbitragem inteira: os prompts exatos, a resposta crua e a conta de
 * tokens. Buscada sob demanda, ao abrir a divergencia — nao vem na timeline.
 */
export async function getChamadaDaFusao(
  hash: string,
  id: number,
  signal?: AbortSignal,
): Promise<ChamadaFusao> {
  return request<ChamadaFusao>(
    `/fusao/exames/${encodeURIComponent(hash)}/chamadas/${id}`,
    { signal },
  );
}

/**
 * Dispara a fusao. Responde 202 e volta na hora — quem acompanha e o polling.
 *
 * NAO CHAMA VIDEO: as cinco fontes ja foram pagas, e esta etapa so as le. O
 * unico gasto possivel e a leitura das divergencias por texto, que e
 * desligavel na configuracao e nao muda nenhum fato.
 *
 * 409 com frase legivel, e nenhum caso e falha: ja ha rodada em andamento, o
 * portao esta fechado (alguma das cinco fontes nao ficou pronta) ou o exame nao
 * e categoria B. Mostre o `describeError` como aviso, nao como erro vermelho.
 */
export async function processarFusao(
  hash: string,
  signal?: AbortSignal,
): Promise<FusaoDoExame> {
  return request<FusaoDoExame>(
    `/fusao/exames/${encodeURIComponent(hash)}/processar`,
    { method: "POST", signal, timeoutMs: TIMEOUT_FUSAO_MS },
  );
}

/**
 * Limpa o resultado da fusao deste exame. O VIDEO NUNCA SAI: ele e da aba
 * Audio, e as cinco fontes tambem ficam — a fusao e uma camada por cima, e
 * apagar a camada nao apaga o que ela leu.
 */
export async function limparFusao(
  hash: string,
  signal?: AbortSignal,
): Promise<LimpezaFusao> {
  return request<LimpezaFusao>(`/fusao/exames/${encodeURIComponent(hash)}`, {
    method: "DELETE",
    signal,
    timeoutMs: TIMEOUT_FUSAO_MS,
  });
}

/** A configuracao de todas as categorias — tudo que a secao do modal precisa. */
export async function getConfigFusao(signal?: AbortSignal): Promise<ConfigFusao> {
  return request<ConfigFusao>("/config/fusao", { signal, timeoutMs: TIMEOUT_FUSAO_MS });
}

/**
 * Salva UMA categoria e devolve o estado que passou a valer.
 *
 * A CATEGORIA VAI NO CAMINHO, como o papel em `PUT /config/visao/{papel}`: o
 * corpo (`ConfigFusaoCorpo`, em `app/schemas/fusao.py`) nao tem campo
 * `categoria`, entao a URL e o unico lugar em que ela cabe. Uma por vez e nao a
 * tabela inteira porque cada categoria e uma decisao propria, e o corpo leva
 * dois prompts inteiros — por isso o timeout maior.
 */
export async function salvarConfigFusao(
  categoria: string,
  corpo: ConfigFusaoCorpo,
  signal?: AbortSignal,
): Promise<ConfigFusao> {
  return request<ConfigFusao>(`/config/fusao/${encodeURIComponent(categoria || "-")}`, {
    method: "PUT",
    body: corpo,
    signal,
    timeoutMs: TIMEOUT_FUSAO_MS,
  });
}

/** A configuracao que vale para UM exame. Categoria desconhecida cai no padrao. */
export async function getConfigFusaoDaCategoria(
  categoria: string,
  signal?: AbortSignal,
): Promise<ConfigFusaoItem> {
  return request<ConfigFusaoItem>(
    `/config/fusao/exame/${encodeURIComponent(categoria || "-")}`,
    { signal },
  );
}

// ===========================================================================
// VEREDITO — as infrações, os pontos e o resultado
//
// A etapa que enquadra, pontua e decide. Três coisas desta API merecem aviso,
// porque quem as ignorar desenha a tela errada:
//
// 1. `resultado` tem CINCO valores, e `INDECIDIVEL` é o normal. A esteira soma
//    só o que verificou, e o que ela não verificou não pontua — publicar
//    "APROVADO" a partir disso seria afirmar que nada mais aconteceu.
// 2. `pontos` e `pontos_so_conferidas` podem divergir: 7 das 18 regras têm
//    gravidade herdada de norma revogada. Mostrar um número só afirmaria como
//    conferido um peso que não foi.
// 3. As assinaturas vêm em pares (`_catalogo`/`_catalogo_vigente`,
//    `_manobras`/`_manobras_atual`). Diferentes = a régua mudou desde a rodada,
//    e as citações `oc-man-N` podem apontar para outra reconstrução.
// ===========================================================================

/** O selo de um item humano na tela. */
export type SeloDoItem =
  | "VERIFICADA"
  | "CONTRADITA"
  | "NAO_VERIFICADA"
  | "NAO_OBSERVAVEL";

/**
 * Os desfechos. QUATRO estão vivos, e dois são entradas de linha antiga.
 *
 * Decisão do dono em 21/09/2026: `INDECIDIVEL` foi REVOGADO e `SEM_NOTA` foi
 * substituído por `INAPTO` — que sai da fala clara de que o candidato é inapto
 * (prova não concluída) e NUNCA da soma de pontos. O gatilho foi ligado em
 * 24/09/2026 (AD-6 "a", `core/inaptidao.py`): a fala de interrupção da prova,
 * publicada e com instante.
 *
 * Os dois revogados continuam no tipo porque linhas gravadas ANTES da decisão
 * ainda devolvem essas strings, e uma união que não as contemple faria a tela
 * receber em runtime um valor que o tipo jura não existir. Ver o mapa RESULTADO
 * em src/lib/vocabulario-veredito.ts, onde eles estão marcados `legado`.
 */
export type VereditoDoExame =
  | "APROVADO"
  | "REPROVADO"
  | "INAPTO"
  | "NAO_CALCULAVEL"
  | "INDECIDIVEL"
  | "SEM_NOTA";

export interface ItemVerificado {
  fonte_humana: "examinador" | "auditor";
  /**
   * A POSIÇÃO deste item no array `training_annotations` do exame — o elo com a
   * ficha desenhada na coluna ①.
   *
   * É POR ELE QUE SE CASA, e por mais nada. Por ARTIGO não dá: as duas pontas
   * formatam diferente ("Art. 196, II" contra "Art. 196"; "Art. 44" contra
   * "Art. 44-A"), e o mesmo artigo repete na mesma ficha (o caso comum do
   * Art. 169). Por POSIÇÃO NA LISTA também não: o backend pula item que não é
   * dicionário e a tela não pula, então os índices deslizam em silêncio a
   * partir do primeiro item torto.
   *
   * Opcional porque rodadas gravadas antes de 21/09/2026 não o têm. Item sem
   * `indice` fica sem selo, calado — um selo casado por chute seria pior.
   */
  indice?: number;
  artigo: string | null;
  t_s: number | null;
  pontos: number | null;
  texto: string;
  selo: SeloDoItem;
  porque: string;
  regras?: string[];
  casou_por?: string;
  fora_da_janela?: boolean;
  faltas?: Array<Record<string, unknown>>;
  base_da_ausencia?: { id_da_medida?: string; porque?: string } | null;
  /**
   * Só no item "SUSPENSÃO DA PROVA" da ficha (11 pontos, sem artigo) com selo
   * VERIFICADA (17/10/2026, `veredito/verificacao.py` 1.1): o INAPTO pela fala
   * que o verificou. `causa` é `conduta_do_candidato` (o indício de oferta ao
   * examinador, nível ALTA) ou `suspensao_dita`; `ref` é a fala decisiva.
   */
  inapto_pela_fala?: { causa: string; ref: string | null; instante_ms: number | null } | null;
}

/**
 * AS TRÊS PERNAS DO CONJUNTO DA SETA, pela regra do dono (21/09/2026): "a seta
 * por si só não quer dizer nada, mas em conjunto com a manobra e com a
 * comprovação visual — frontal identificando a conversão e interna (secundária,
 * de menos peso) confirmando o volante — esse conjunto é via de regra".
 *
 * A força NÃO bloqueia acusação: a interna enquadra o giro em 21% das
 * conversões da frota, e exigi-la custaria quatro de cada cinco.
 */
export interface ConjuntoDaSeta {
  forca: "COMPLETO" | "SEM_O_VOLANTE" | "SO_A_MANOBRA" | "CONTRADITO";
  pernas: Record<string, { estado: string | null; quem: string[]; porque: string }>;
  porque: string;
}

export interface AchadoSoDaEsteira {
  ocasiao_id: string;
  id_regra: string;
  artigo: string | null;
  peso: number;
  inicio_ms: number | null;
  fim_ms: number | null;
  tipo_de_manobra: string | null;
  porque: string;
  base_da_ausencia?: { id_da_medida?: string; porque?: string } | null;
  conjunto?: ConjuntoDaSeta | null;
  /**
   * A leitura promovida que pôs a falta (`pare_com_fala`,
   * `semaforo_vermelho_sem_parada`, `motor_mortes_reiteradas`…). Opcional: a
   * rodada antiga não a manda na lista.
   */
  promovida_por?: string | null;
}

/**
 * UMA infração que a NOSSA IA identificou — todas as faltas com veredito
 * OCORREU, e não só as inéditas.
 *
 * A DIFERENÇA PARA `so_a_esteira_viu` É O RECORTE, não o formato: aquela lista
 * é a das faltas que nenhum humano apontou, e continua existindo inalterada.
 * Esta é o laudo inteiro da nossa IA — é o que a coluna ⑥ publica desde a
 * decisão do dono em 21/09/2026, porque "Infrações: 14 pt" ao lado de uma lista
 * de 2 itens é um número sem lista.
 */
export interface InfracaoDaNossaIa extends AchadoSoDaEsteira {
  /**
   * LIDA DO CATÁLOGO, nunca derivada do peso. Derivar erra: o mapa medido é
   * Leves 1 pt, Médias 2, Graves 4, Gravíssimas 11 *ou* 6, Eliminatórias 6 — os
   * 6 pontos são ambíguos entre gravíssima e eliminatória (181 contra 61 itens
   * na ficha do examinador), e nenhuma tabela pontos → gravidade desfaz isso.
   */
  gravidade?: string | null;
  /**
   * O `indice` do item da ficha do examinador que esta infração sustenta, ou
   * `null` quando nenhum humano a apontou. É o que separa o chip "IA Valbot
   * Registrou" do "Também anotada pelo examinador".
   */
  item_do_examinador?: number | null;
}

export interface Verificacao {
  versao: string;
  /**
   * O laudo da NOSSA IA: todas as faltas com veredito OCORREU, o total delas e
   * os pontos que somam.
   *
   * Opcional porque rodadas anteriores a 21/09/2026 não trazem o bloco. Ausente
   * ≠ vazio: sem ele a coluna ⑥ não tem lista para publicar e diz isso, em vez
   * de desenhar "nenhuma infração" sobre um laudo que talvez tenha catorze.
   */
  nossa_ia?: { itens: InfracaoDaNossaIa[]; total: number; pontos: number };
  examinador: {
    itens: ItemVerificado[];
    placar: Record<SeloDoItem, number>;
    total: number;
  };
  auditor: {
    itens: ItemVerificado[];
    placar: Record<SeloDoItem, number>;
    total: number;
  };
  so_a_esteira_viu: { itens: AchadoSoDaEsteira[]; total: number };
}

/** UMA leitura do juiz, já passada pelo validador. */
export interface OcasiaoDoJuiz {
  ocasiao_id: string | null;
  id_regra: string | null;
  veredito: string | null;
  porque: string | null;
  o_que_mudaria_de_ideia: string | null;
  o_que_nao_vi: unknown;
  conviccao: string | null;
  base_da_ausencia: { id_da_medida?: string; porque?: string } | null;
  /**
   * OS MOTIVOS PELOS QUAIS A ACUSAÇÃO DO MODELO NÃO SUBIU. É o produto desta
   * seção, e não o refugo: sem eles, homologar seria concordar no escuro.
   */
  rebaixamentos: string[];
  passou_limpa: boolean;
}

/**
 * O que a chamada paga produziu — inclusive quando não produziu nada. `erro` e
 * `bloqueado` vêm junto de propósito: seção que só aparece quando deu certo
 * transforma falha em invisibilidade.
 */
export interface JuizDoVeredito {
  modelo: string | null;
  erro: string | null;
  bloqueado: string | null;
  triagem: string | null;
  limites: string | null;
  n_lidas: number;
  n_rebaixadas: number;
  duracao_s: number | null;
  tentativas: number;
  chars_do_dossie: number;
  tokens_raciocinio: number;
  custo_usd: number | null;
  /** A soma que o juiz sustentaria, AO LADO da do motor. Nunca somada a ela. */
  pontuacao_do_juiz: { pontos?: number; resultado?: string } | null;
  ocasioes: OcasiaoDoJuiz[];
}

/* -------------------------------------------------------------------------- */
/* Fala — quem falou, o examinador ou o candidato                             */
/* -------------------------------------------------------------------------- */

/** UMA fala, com o que esta etapa conseguiu dizer sobre ela. */
export interface AtribuicaoDeFala {
  /** O elo com `audio_processamento.resultado["falas"]`. Explícito de propósito. */
  indice: number;
  inicio_s: number | null;
  fim_s: number | null;
  /** LOCUTOR_1 | LOCUTOR_2 | INCERTO — MEDIÇÃO de timbre. */
  locutor: string;
  /** EXAMINADOR | CANDIDATO | INCERTO — HIPÓTESE, sempre. */
  papel: string;
  confianca: number | null;
  /** Os comandos do léxico reconhecidos nesta fala. */
  termos: string[];
}

export interface EstadoFala {
  exame_hash: string;
  estado: string;
  progresso: number;
  etapa: string | null;
  versao: string | null;
  modelo: string | null;
  categoria: string | null;
  duracao_s: number | null;
  n_falas: number;
  /** Quantas passaram do piso. As outras ficam INCERTO e continuam publicadas. */
  n_longas: number;
  n_com_locutor: number;
  n_com_papel: number;
  n_examinador: number;
  n_candidato: number;
  n_clusters: number;
  /** A separação de vozes se sustenta? É contraste entre grupos, não similaridade. */
  locutor_confiavel: boolean;
  similaridade_max: number | null;
  papel_publicado: boolean;
  papel_confianca: number | null;
  resultado: {
    atribuicoes?: AtribuicaoDeFala[];
    locutor?: {
      n_longas?: number;
      piso_s?: number;
      n_clusters?: number;
      qualidade?: {
        similaridade_max?: number | null;
        intra_grupo?: number | null;
        entre_grupos?: number | null;
        margem?: number | null;
        margem_minima?: number;
        confiavel?: boolean;
      };
      tempo_por_locutor?: Record<string, number>;
      aviso?: string;
    };
    papel?: {
      decidido?: boolean;
      publicado?: boolean;
      origem?: string;
      porque?: string;
      examinador?: string | null;
      confianca?: number | null;
      escore_por_minuto?: Record<string, number>;
      evidencias?: Record<string, string[]>;
      razao_sobre_o_segundo?: number;
      motivo?: string | null;
    };
  } | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  erro: string | null;
  atualizado_em: string | null;
}

export async function getFala(hash: string, signal?: AbortSignal): Promise<EstadoFala> {
  return request<EstadoFala>(`/fala/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Dispara a atribuição. Responde 202. NÃO custa dinheiro: é CPU. */
export async function processarFala(hash: string, signal?: AbortSignal): Promise<EstadoFala> {
  return request<EstadoFala>(`/fala/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    signal,
  });
}

export function falaEmAndamento(estado: EstadoFala | null): boolean {
  return estado !== null && (estado.estado === "pendente" || estado.estado === "processando");
}

/* -------------------------------------------------------------------------- */
/* O desfecho pela fala (veredito 1.5, categoria B, 14/10/2026)                */
/* -------------------------------------------------------------------------- */

/**
 * O degrau da escada que deu o rótulo (`core.inaptidao.escada`), na ordem:
 * sem material, conduta do candidato (INAPTO, desde 17/10/2026), suspensão
 * dita (INAPTO), limite dito (REPROVADO), soma de 11 ou mais, infração
 * confirmada pela fala no peso "reprova" (REPROVADO), e a soma que aprova.
 * `string` porque um motor mais novo pode mandar outro: a tela mostra o que
 * não conhece como veio, e não o esconde.
 */
export type MotivoDoRotulo =
  | "sem_material"
  | "conduta_do_candidato"
  | "suspensao_dita"
  | "limite_dito"
  | "soma"
  | "infracao_confirmada_pela_fala"
  | "soma_aprova";

/** A janela do botão VER/OUVIR de uma fala (`core.evidencia.ver_ouvir`). */
export interface VerOuvirDaFala {
  camera: string | null;
  inicio_ms: number;
  fim_ms: number;
  /** O caminho da aba (`/exame/{hash}/audio`). A tela monta o link pelo hash dela, não por este texto. */
  aba: string;
}

/**
 * `faltas.conta.desfecho_pela_fala` (`core.inaptidao.desfecho_pela_fala`): só
 * existe quando o rótulo veio da fala. A `citacao` é da transcrição (≤ 160
 * caracteres) e SÓ para esta tela interna: nunca vai ao laudo nem ao envio.
 */
export interface DesfechoPelaFala {
  regra: string | null;
  degrau: "CONDUTA_DO_CANDIDATO" | "SUSPENSAO_DITA" | "LIMITE_DITO" | "INFRACAO_CONFIRMADA_PELA_FALA" | string;
  rotulo: string | null;
  /** A fala decisiva, `fala:N` (na conduta do candidato, a 1ª fala forte do episódio). */
  motivo: string | null;
  /** Na conduta do candidato, as falas do episódio dos dois lados, na ordem do tempo. */
  refs: string[];
  citacao: string | null;
  instante_s: number | null;
  /** `MEDIDO` / `ESTIMADO` / `SEM_SINCRONIA`. */
  origem_tempo: string | null;
  /** `ALTA` na suspensão e no limite; null na confirmada (o vínculo de tempo da seta está perto do acaso). */
  nivel: string | null;
  /** A soma da IA, que fica ao lado e não muda. */
  soma_da_ia: number | null;
}

/**
 * `faltas.conta.eliminatoria` (`core.inaptidao.bloco_eliminatorio`): o item de
 * 11 pontos que o envio à produção leva (envelope/3). `fala_ref` é interno e
 * nunca vai ao corpo. Sem instante, o envio recusa o veredito.
 */
export interface BlocoEliminatorio {
  tipo: "inapto_pela_fala" | "limite_pela_fala" | "falta_confirmada" | string;
  regra: string | null;
  instante_ms: number | null;
  origem_tempo: string | null;
  fala_ref: string | null;
  /** Só na `falta_confirmada`: o item da Matriz daquela ocasião, que sai do corpo (não conta 6 + 11). */
  substitui: {
    id_regra: string | null;
    artigo: string | null;
    ocasiao_id: string | null;
    inicio_ms: number | null;
  } | null;
  /** Só na `falta_confirmada`: a distância da fala ao evento, em segundos. */
  distancia_s: number | null;
  /**
   * Só no `inapto_pela_fala` (17/10/2026, opção L1 do desenho da conduta do
   * candidato): por que o item vai. O id "SUSPENSAO DA PROVA" e o tipo ficam;
   * a montagem do laudo escolhe o modelo do texto pela causa. Ausente na linha
   * anterior, que é sempre a suspensão dita.
   */
  causa?: "conduta_do_candidato" | "suspensao_dita" | string | null;
  /** Na causa `conduta_do_candidato`: o instante da conduta (a 1ª fala forte do episódio). */
  conduta_instante_ms?: number | null;
  /** Na causa `conduta_do_candidato`: o início da 1ª imagem da câmera interna que a corroborou. */
  imagem_instante_ms?: number | null;
}

/**
 * UMA FALA do episódio da conduta do candidato (`core.conduta_candidato.avaliar`).
 * A `citacao` é da transcrição (≤ 160 caracteres) e SÓ para a tela interna:
 * nunca vai ao laudo nem ao envio. `contem_termo_vedado` marca a citação que
 * traz uma palavra que o sistema não escreve — ela sai literal, como foi dita.
 */
export interface FalaDaConduta {
  /** `fala:N`. */
  ref: string;
  indice: number | null;
  instante_s: number | null;
  /** `MEDIDO` / `ESTIMADO` / `SEM_SINCRONIA`. */
  origem_tempo: string | null;
  citacao: string | null;
  /** As famílias do léxico que casaram nesta fala (`nega_pedido`, `crime`, `aperto_de_mao`…). */
  familias: string[];
  contem_termo_vedado: boolean;
  ver_ouvir: VerOuvirDaFala | null;
}

/**
 * UMA IMAGEM da câmera interna que corrobora o episódio. Só corrobora: nunca
 * dispara nem muda o nível. A direção da entrega não se afirma (o modelo
 * troca o ator), e o objeto nunca é nomeado (`descricao` já vem rebaixada).
 */
export interface ImagemDaConduta {
  /** `visao_evento:<id>`. */
  ref: string;
  inicio_ms: number;
  fim_ms: number | null;
  /** O ator segundo o modelo da câmera (ou a correção humana). */
  ator: string | null;
  descricao: string | null;
}

/**
 * `faltas.conta.conduta_candidato` (`core.conduta_candidato.avaliar`,
 * `conduta_candidato_pela_fala/1`, 17/10/2026): o indício de oferta ao
 * examinador, lido no que o EXAMINADOR diz — o papel é presumido pela classe
 * da fala, nunca conferido. `ALTA` (duas famílias fortes do examinador a até
 * 120 s, com uma fala medida) dá INAPTO, pelo degrau `conduta_do_candidato`;
 * `MEDIA` é só revisão. Sem achado, `achou: false` e o resto vazio.
 */
export interface CondutaDoCandidato {
  regra: string | null;
  achou: boolean;
  nivel: "ALTA" | "MEDIA" | string | null;
  efeito: "INAPTO" | "A_REVISAR" | string | null;
  /** O instante da 1ª fala forte do episódio. */
  instante_ms: number | null;
  origem_tempo: string | null;
  episodio: { inicio_ms: number; fim_ms: number } | null;
  /** As famílias distintas do episódio, na ordem canônica (as fortes, as fracas e as do candidato). */
  familias: string[];
  falas_examinador: FalaDaConduta[];
  falas_candidato: FalaDaConduta[];
  imagens: ImagemDaConduta[];
  corroborada_por_imagem: boolean;
  /** `["audio"]` ou `["audio", "interna"]`. */
  testemunhas: string[];
  /** A 1ª fala de suspensão (ou cancelamento) da prova dentro do episódio. */
  suspensao_dita_ms: number | null;
}

/**
 * UMA fala que corroborou um evento da IA (`core.corroboracao.corroborar`):
 * seta, PARE ou semáforo. Em `faltas.conta.corroboracoes` vêm todas, com o
 * `evento` e a `manobra_ordem`; na falta (`faltas.itens[].corroboracao`), com o
 * `peso` ("norma" ou "reprova") e o `reprova` com que se julgou.
 */
export interface CorroboracaoPelaFala {
  evento?: string;
  manobra_ordem?: number | string | null;
  /** `fala:N`. */
  ref: string;
  /** `SETA` / `PARE` / `SEMAFORO`. */
  tipo: string;
  classe: string | null;
  alvo: string | null;
  origem_tempo: string | null;
  /** O instante da FALA, em segundos. */
  instante_s: number | null;
  /** A distância da fala ao evento: negativa antes, 0 dentro, positiva depois. */
  delta_s: number | null;
  /** A expressão do léxico que casou — interna. */
  marca: string | null;
  /** A transcrição cortada — interna. */
  citacao: string | null;
  ver_ouvir: VerOuvirDaFala | null;
  regra: string | null;
  peso?: string | null;
  reprova?: boolean;
}

/** Uma falta `OCORREU` que uma fala corroborou (`faltas.itens` com `corroboracao`). */
export interface FaltaCorroborada {
  ocasiao_id: string | null;
  manobra_ordem: number | null;
  id_regra: string | null;
  promovida_por: string | null;
  inicio_ms: number | null;
  corroboracao: CorroboracaoPelaFala | null;
}

/**
 * UMA porta de acusação do veredito de hoje (`app/veredito/portas.py`, C1.8 da
 * auditoria de 23/09): `ausencia_medida` (com `medida`) ou `sustentadora` (com
 * `id_regra`, `classe` e `peso`).
 */
export interface PortaDeAcusacao {
  /** `sustentadora`, `ausencia_medida` ou, desde 28/09/2026, `promovida` (com a `chave` da promoção). */
  via: string;
  /** A promoção (`pare_com_fala`…), na via `promovida`. */
  chave?: string | null;
  medida?: string | null;
  id_regra?: string | null;
  classe?: string | null;
  peso?: number | null;
}

export interface EstadoVeredito {
  exame_hash: string;
  estado: string;
  progresso: number;
  etapa: string | null;
  versao: string | null;
  versao_catalogo: string | null;
  assinatura_catalogo: string | null;
  assinatura_catalogo_vigente: string | null;
  assinatura_manobras: string | null;
  assinatura_manobras_atual: string | null;
  categoria: string | null;
  duracao_s: number | null;
  n_ocasioes: number;
  n_faltas_candidatas: number;
  n_ocorreu: number;
  n_nao_ocorreu: number;
  n_nao_observada: number;
  n_nao_medida: number;
  pontos: number;
  pontos_so_conferidas: number;
  resultado: VereditoDoExame | null;
  /**
   * AS PORTAS DE ACUSAÇÃO ABERTAS no catálogo VIGENTE (C1.8). Lista vazia =
   * nenhuma regra pode dar `OCORREU` nesta versão, e o `APROVADO` de um exame
   * com material é identidade, não leitura. OPCIONAL porque um backend anterior
   * a 23/09 não manda o campo — e aí a tela não afirma nada sobre as portas.
   */
  portas_de_acusacao_abertas?: PortaDeAcusacao[];
  /**
   * O gatilho do INAPTO: a fala de interrupção da prova, publicada e com
   * instante (AD-6 "a", ligada em 24/09/2026 — `core/inaptidao.py`). Nunca a
   * soma de pontos, que continua em `pontos`.
   */
  declarado_inapto?: boolean;
  /** A fala citada (`fala:<índice>`). Sem ela, um INAPTO não se audita. */
  motivo_inapto?: string | null;
  /**
   * O DESFECHO PELA FALA (veredito 1.5, 14/10/2026). Todos opcionais: um
   * backend anterior não os manda, e a linha gravada antes de 14/10 os traz
   * vazios — a tela cala sobre eles em vez de adivinhar.
   *
   * `motivo_do_rotulo` é o degrau da escada que deu o rótulo;
   * `reprovado_pela_fala`/`motivo_reprovado`, o limite de pontos dito (sem
   * palavra de suspensão); `desfecho_pela_fala`, o bloco da tela quando o
   * rótulo veio da fala; `eliminatoria`, o item que o envio leva;
   * `corroboracoes`, toda fala que corroborou um evento da IA;
   * `faltas_corroboradas`, as faltas `OCORREU` com a corroboração e o peso;
   * `pesos_da_confirmacao`, o peso de cada tipo com que se julgou;
   * `conduta_candidato` (17/10/2026), o bloco do indício de oferta ao
   * examinador, com ou sem achado (a linha anterior não o traz).
   */
  motivo_do_rotulo?: MotivoDoRotulo | string | null;
  reprovado_pela_fala?: boolean;
  motivo_reprovado?: string | null;
  desfecho_pela_fala?: DesfechoPelaFala | null;
  eliminatoria?: BlocoEliminatorio | null;
  corroboracoes?: CorroboracaoPelaFala[];
  faltas_corroboradas?: FaltaCorroborada[];
  pesos_da_confirmacao?: Record<string, string> | null;
  conduta_candidato?: CondutaDoCandidato | null;
  /**
   * A PARTIÇÃO DOS PONTOS: quanto do total sustenta um item que o examinador
   * anotou, e quanto só a esteira viu. As duas metades e o total vêm do
   * backend, nunca de subtração aqui — `particao_fecha` é o backend declarando
   * que `sustentados + so_a_esteira == pontos`. Quando ele diz que NÃO fecha, a
   * tela mostra os três números e o aviso; recalcular um deles aqui esconderia
   * exatamente a divergência que este campo existe para denunciar.
   */
  pontos_sustentados_examinador?: number;
  pontos_so_a_esteira_viu?: number;
  particao_fecha?: boolean;
  /**
   * O QUE SUSTENTA UM "APROVADO". Sem isto, aprovar é afirmar que nada mais
   * aconteceu — e a esteira só soma o que verificou. `regras_sem_fonte` nomeia
   * as regras do catálogo para as quais NÃO houve leitura neste exame.
   */
  cobertura?: {
    regras_em_jogo: number;
    regras_com_leitura: number;
    regras_sem_fonte: string[];
  } | null;
  faltam_para_reprovar: number;
  /** O nome antigo de `declarado_inapto`, com o mesmo valor. A tela não o lê
   * desde 24/09/2026; fica no tipo porque o backend ainda o manda. */
  houve_interrupcao: boolean;
  faltas: { itens?: Array<Record<string, unknown>>; conta?: Record<string, unknown> } | null;
  ocasioes: { itens?: Array<Record<string, unknown>> } | null;
  verificacao: Verificacao | null;
  n_itens_examinador: number;
  n_verificadas_examinador: number;
  n_itens_auditor: number;
  n_verificadas_auditor: number;
  n_so_a_esteira_viu: number;
  usa_ia: boolean;
  modelo: string | null;
  custo_usd: number | null;
  tokens_entrada: number;
  tokens_saida: number;
  dossie_sha1: string | null;
  /** `null` = o juiz não rodou nesta rodada. */
  juiz: JuizDoVeredito | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  /**
   * R19 (auditoria de 23/09): a Seta foi reprocessada DEPOIS das Manobras que
   * este veredito leu — o Art. 196 foi julgado sobre episódios que podem não
   * existir mais. Opcional pelo mesmo motivo de `portas_de_acusacao_abertas`.
   */
  seta_posterior_as_manobras?: boolean;
  /**
   * R3 (revisão de 24/09): as fontes que o veredito lê direto — `falas` (a
   * interrupção que liga o INAPTO) e `frontal` (o vídeo sem percurso) —
   * reprocessadas DEPOIS deste veredito. O portão fica aberto; recalcular é o
   * remédio. Opcional: um backend anterior não manda o campo.
   */
  fontes_posteriores_ao_veredito?: string[];
  erro: string | null;
  atualizado_em: string | null;
}

export async function getVeredito(
  hash: string,
  signal?: AbortSignal,
): Promise<EstadoVeredito> {
  return request<EstadoVeredito>(`/veredito/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Uma regra do catálogo vigente; a rota manda mais campos, o tipo declara só os lidos. */
export interface RegraDoCatalogo {
  descricao?: string | null;
  citacao?: string | null;
}

export interface CatalogoDoVeredito {
  versao: string;
  regras: Record<string, RegraDoCatalogo>;
}

export async function getCatalogoDoVeredito(signal?: AbortSignal): Promise<CatalogoDoVeredito> {
  return request<CatalogoDoVeredito>("/veredito/catalogo", { signal });
}

/**
 * Dispara o cálculo. Responde 202.
 *
 * **CUSTA DINHEIRO desde 21/09/2026.** O motor continua determinístico e de
 * graça, mas o juiz por IA está LIGADO e faz uma chamada paga por rodada — o
 * dossiê varia quase 2× entre exames, então o preço não é constante. O valor
 * cobrado volta em `custo_usd`.
 */
export async function processarVeredito(
  hash: string,
  signal?: AbortSignal,
): Promise<EstadoVeredito> {
  return request<EstadoVeredito>(
    `/veredito/exames/${encodeURIComponent(hash)}/processar`,
    { method: "POST", signal },
  );
}

/** `true` enquanto houver rodada viva — é o que mantém o polling ligado. */
export function vereditoEmAndamento(estado: EstadoVeredito): boolean {
  return estado.estado === "pendente" || estado.estado === "processando";
}

/**
 * A régua mudou desde que esta rodada foi calculada? Duas assinaturas, e as
 * duas importam: a do catálogo muda o peso, a das manobras muda o que
 * `oc-man-N` aponta.
 */
export function reguaMudouDesdeARodada(estado: EstadoVeredito): boolean {
  const cat =
    estado.assinatura_catalogo !== null &&
    estado.assinatura_catalogo_vigente !== null &&
    estado.assinatura_catalogo !== estado.assinatura_catalogo_vigente;
  const man =
    estado.assinatura_manobras !== null &&
    estado.assinatura_manobras_atual !== null &&
    estado.assinatura_manobras !== estado.assinatura_manobras_atual;
  return cat || man;
}

// ===========================================================================
// MANOBRAS — os atos de condução, e o que cada fonte registrou em torno
// ===========================================================================

export interface EstadoManobras {
  exame_hash: string;
  estado: string;
  progresso: number;
  etapa: string | null;
  versao: string | null;
  assinatura_regras: string | null;
  assinatura_vigente: string | null;
  categoria: string | null;
  modelo: string | null;
  duracao_s: number | null;
  n_manobras: number;
  n_ambiguas: number;
  n_com_conflito: number;
  n_sem_fim_medido: number;
  n_observacoes_sem_classe: number;
  manobras: { itens?: Array<Record<string, unknown>>; descartes?: unknown[] } | null;
  capacidade: Record<string, unknown> | null;
  funil: Record<string, unknown> | null;
  metricas: Record<string, unknown> | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  erro: string | null;
  atualizado_em: string | null;
}

export async function getManobras(
  hash: string,
  signal?: AbortSignal,
): Promise<EstadoManobras> {
  return request<EstadoManobras>(`/manobras/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Dispara a reconstrução. 202, e NÃO custa dinheiro: é CPU. */
export async function processarManobras(
  hash: string,
  signal?: AbortSignal,
): Promise<EstadoManobras> {
  return request<EstadoManobras>(
    `/manobras/exames/${encodeURIComponent(hash)}/processar`,
    { method: "POST", signal },
  );
}

export function manobrasEmAndamento(estado: EstadoManobras): boolean {
  return estado.estado === "pendente" || estado.estado === "processando";
}

// ===========================================================================
// ESTEIRA — as oito etapas de um exame numa leitura so (so le, nao dispara)
// ===========================================================================

/** Aberto ou fechado AGORA, e a frase — a mesma que o POST da etapa daria. */
export interface PortaoDaEtapaDaEsteira {
  aberto: boolean;
  motivo: string | null;
  mensagem: string;
  /**
   * Só em `sem_midia_local` da fala e da seta (L11): `true` = o "Rodar tudo"
   * traz a mídia de volta sozinho, a US$ 0 de modelo, antes da etapa — o
   * portão continua FECHADO para o botão da aba, mas não bloqueia a esteira.
   * `false` = não há como reidratar (sem a ficha da mídia do áudio). Ausente
   * nos outros motivos e em backend anterior.
   */
  reidrata?: boolean | null;
}

/** De onde veio o custo da linha. */
export type OrigemDoCusto = "gravado" | "calculado_na_leitura" | "sem_chamada_paga";

export interface LinhaDaEsteira {
  /** A etapa (o prefixo da rota): as oito da E0 e as seis da E2 (insumo,
   *  telemetria, motor, falas, conduta, compliance). */
  etapa: string;
  /** So a visao tem papel (interna, frontal, lateral, traseira). */
  papel: string | null;
  tabela: string;
  /** `NOS_OBRIGATORIOS` (vocabulario): a nao obrigatoria (telemetria) nao
   *  entra em `proxima_bloqueada`. Ausente em backend anterior a E2. */
  obrigatoria?: boolean;
  /** O da linha; `ausente` = nunca rodou; `interrompido` = a etapa acha que travou. */
  estado: string;
  progresso: number | null;
  /** O passo em curso ("baixando o vídeo", "janela 3 de 7"). */
  andamento: string | null;
  modelo: string | null;
  /** As planejadas, sem retentativa. */
  chamadas: number | null;
  /** O que se pagou. `null` = nao se sabe (nao e zero). */
  custo_usd: number | null;
  custo_origem: OrigemDoCusto | null;
  /** Gravados pelo gatilho da E0; `null` em rodada anterior a 23/09/2026. */
  iniciado_em: string | null;
  terminado_em: string | null;
  parede_s: number | null;
  erro: string | null;
  atualizado_em: string | null;
  portao: PortaoDaEtapaDaEsteira;
  detalhes: Record<string, unknown>;
  /** A PREVISAO do "Rodar tudo" (plano e2), pela regra de reaproveitamento
   *  do orquestrador: `refaz` = a etapa rodaria de novo (e `paga` diz se ela
   *  pode chamar o Vertex); `motivo_refazer` diz por que (`assinatura`,
   *  `opcao:ia`, `linha_ausente`...). Ausente/`null` = nao deu para prever
   *  (backend anterior a revisao da E2). */
  paga?: boolean | null;
  refaz?: boolean | null;
  motivo_refazer?: string | null;
  /** A prévia (TELA-02, 24/09): o laço dispararia a etapa e o portão dela,
   *  fechado hoje, a recusa. A frase é a de `portao.mensagem`. Ausente em
   *  backend anterior. */
  recusada?: boolean | null;
}

export interface EtapaBloqueadaDaEsteira {
  etapa: string;
  papel: string | null;
  estado: string;
  motivo: string | null;
  mensagem: string;
}

/** Um valor da ficha do insumo, sempre com o campo de onde veio. */
export interface CampoDoInsumo {
  valor: unknown;
  origem: string;
}

/** A ficha de qualidade do insumo, versao 0: so medida, SEM rotulo. */
export interface FichaDoInsumoV0 {
  versao: string;
  rotulo: null;
  rotulo_nota: string;
  fontes: Record<string, string>;
  video: Record<string, CampoDoInsumo>;
  mosaico: Record<string, CampoDoInsumo>;
  mapa: Record<string, CampoDoInsumo>;
  audio: Record<string, CampoDoInsumo>;
}

export interface EsteiraDoExame {
  plano?: string;
  exame_hash: string;
  no_espelho: boolean;
  categoria: string | null;
  duracao_video_s: number | null;
  /** Dezessete desde a E2, na ordem canonica do grafo (a visao por papel). */
  etapas: LinhaDaEsteira[];
  custo_total_usd: number;
  /** `false` quando alguma etapa que chamou modelo nao tem custo: a soma e piso. */
  custo_total_completo: boolean;
  proxima_bloqueada: EtapaBloqueadaDaEsteira | null;
  insumo_v0: FichaDoInsumoV0;
  /** A execucao mais recente do "Rodar tudo" deste exame (E2.9); `null` se nunca. */
  rodada?: RodadaDaEsteira | null;
  /**
   * A P6 da ULTIMA execução do "Rodar tudo", mesmo quando `rodada` é a do host
   * (F9, 24/09). `null` = nunca passou pelo "Rodar tudo"; ausente = backend
   * anterior (a aba lê a P6 da `rodada`, como antes).
   */
  ultima_execucao?: UltimaExecucaoDaEsteira | null;
  /**
   * P5 (24/09): o padrão EFETIVO de `limpar_midia` do "Rodar tudo" neste
   * servidor (`ESTEIRA_LIMPAR_MIDIA_PADRAO`) — o que a execução nova vai
   * usar. `null` = o servidor não conseguiu ler; ausente = backend anterior.
   * Nos dois casos a confirmação não afirma nada sobre a mídia.
   */
  limpar_midia_padrao?: boolean | null;
  /**
   * POR QUE A EXECUÇÃO VIVA NÃO ANDA (11/10/2026, `esteira/impedimentos.py`):
   * disco cheio, nenhum trabalhador, versão diferente, teto de gasto, fila na
   * frente... `bloqueia` primeiro. Vazia = nada impede (ou não há execução
   * viva); a leitura que cai vem como `nao_deu_para_saber`, nunca vazia.
   * Ausente = backend anterior.
   */
  impedimentos?: ImpedimentoDaEsteira[];
  /**
   * A limpeza mais recente deste exame (`esteira_limpezas`, 12/10/2026): o
   * "Limpar análise", o "Limpar tudo" e o "Reprocessar do zero" da aba
   * Processamento. `null` = nunca limpo; ausente = backend anterior (a aba
   * lê a mesma de `GET /esteira/exames/{hash}/acoes`).
   */
  ultima_limpeza?: UltimaLimpeza | null;
  lido_em: string;
}

/** `bloqueia`: nada anda até alguém agir; `espera`: anda sozinho, só demora;
 *  `atencao`: algo errado que não trava este exame, ou o motivo não foi achado. */
export type GravidadeDoImpedimento = "bloqueia" | "espera" | "atencao";

export interface ImpedimentoDaEsteira {
  /** `disco_baixo`, `sem_trabalhador`, `incompativel`, `teto_de_gasto`... */
  codigo: string;
  gravidade: GravidadeDoImpedimento;
  titulo: string;
  /** A explicação com os números. */
  frase: string;
  /** O que fazer; `null` quando não há o que fazer. */
  acao: string | null;
  /** A máquina (VM) do trabalhador envolvido, quando há uma. */
  quem: string | null;
  dados: Record<string, unknown>;
}

/** A esteira deste exame. So leitura: nenhuma etapa e disparada por aqui. */
export async function getEsteiraDoExame(
  hash: string,
  signal?: AbortSignal,
): Promise<EsteiraDoExame> {
  return request<EsteiraDoExame>(`/exams/${encodeURIComponent(hash)}/esteira`, { signal });
}

// ==== E2 · P8 · telemetria ====
/*
  A ETAPA DE TELEMETRIA (T4) e a trajetoria da aba (T3). Contrato §2.4.

  O GPS e os sensores do webhook como fonte CORROBORATIVA: a etapa roda
  sincrona, custa US$ 0 e devolve a linha pronta no proprio POST. Sem payload,
  a resposta e `pronto` com `SEM_TELEMETRIA` — nao e erro, e a tela nao pinta
  como erro.

  A TRAJETORIA E O UNICO LUGAR ONDE COORDENADA CHEGA AO NAVEGADOR. Ela vem de
  `GET /telemetria/exames/{h}/trajeto` com `Cache-Control: private, no-store`,
  e nada aqui a guarda fora da memoria da aba.
*/
import type {
  ORIGEM_TEMPO as _ORIGEM_TEMPO_P8,
  POSICOES_NO_PERCURSO as _POSICOES_P8,
  PROCEDENCIAS_TELEMETRIA as _PROCEDENCIAS_P8,
  ROTULOS_TELEMETRIA as _ROTULOS_TELEMETRIA_P8,
  SINCRONIA_GPS as _SINCRONIA_GPS_P8,
} from "./vocabulario";

export type RotuloTelemetria = (typeof _ROTULOS_TELEMETRIA_P8)[number];
export type OrigemTempoTelemetria = (typeof _ORIGEM_TEMPO_P8)[number];
export type SincroniaGps = (typeof _SINCRONIA_GPS_P8)[number];
export type ProcedenciaTelemetria = (typeof _PROCEDENCIAS_P8)[number];
export type PosicaoNoPercurso = (typeof _POSICOES_P8)[number];

/** Um fato da telemetria. Sem coordenada: o ponto e apontado por `seq`. */
export interface FatoTelemetria {
  id: string;
  tipo: "VELOCIDADE" | "SENSOR" | "GEOMETRIA";
  subtipo: string;
  /** `null` = sem relogio (nunca inventado). */
  inicio_s: number | null;
  fim_s: number | null;
  origem_tempo: OrigemTempoTelemetria;
  sincronia_gps: SincroniaGps;
  tolerancia_s: number | null;
  procedencia: ProcedenciaTelemetria;
  seq_inicio: number | null;
  seq_fim: number | null;
  atributos: Record<string, unknown> & {
    fora_do_video?: boolean | null;
    semantica?: string;
    posicao_no_percurso?: PosicaoNoPercurso;
    janela_possivel_s?: [number, number] | null;
    giro_graus?: number;
    v_max_kmh?: number | null;
    v_kmh?: number | null;
    n_pontos?: number;
  };
}

export interface RelogioTelemetria {
  delta_s: number;
  conferencia: "NAO_TENTADA" | "MEDIDA" | "FALHOU";
  motivo_conferencia: string | null;
  instrumento: string | null;
  mad_s: number | null;
  n_pares: number;
  n_ancoras: number;
  dispersao_s: number | null;
  cadencia: number | null;
  faixa_cadencia: string | null;
  eixo_valido: boolean;
  motivo_sem_eixo: string | null;
  sincronia_gps: SincroniaGps;
  origem_tempo: OrigemTempoTelemetria;
  tolerancia_s: number | null;
}

export interface FichaTelemetria {
  rotulo: RotuloTelemetria;
  motivos: string[];
  regra: string;
  medidas: Record<string, Record<string, unknown> | boolean>;
}

export interface EstadoTelemetria {
  exame_hash: string;
  /** ausente | pendente | processando | pronto | erro | interrompido | nao_compativel */
  estado: string;
  etapa: string | null;
  versao: string | null;
  assinatura_regras: string | null;
  assinatura_vigente: string | null;
  categoria: string | null;
  payload_sha1: string | null;
  fontes_mudaram: boolean;
  motivos_fontes_mudaram: string[];
  ficha_rotulo: RotuloTelemetria | null;
  ficha: FichaTelemetria | null;
  origem_tempo_max: OrigemTempoTelemetria | null;
  relogio: RelogioTelemetria | null;
  fatos: FatoTelemetria[] | null;
  n_pontos: number;
  n_eventos: number;
  n_fatos: number;
  n_fatos_medidos: number;
  n_fatos_estimados: number;
  n_fatos_sem_relogio: number;
  duracao_video_s: number | null;
  custo_usd: number | null;
  parede_s: number | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  erro: string | null;
  atualizado_em: string | null;
}

export interface EventoNoTrajeto {
  indice: number;
  id_sensor: string;
  lat: number | null;
  lon: number | null;
  v_kmh: number | null;
  posicao: PosicaoNoPercurso | string;
  seq_inicio: number | null;
  seq_fim: number | null;
  t_video: number | null;
  fora_do_video: boolean | null;
  origem_tempo: OrigemTempoTelemetria;
}

/** `[seq, lat, lon, v, t_video]` — `t_video` e `null` sem relogio valido. */
export type PontoDoTrajeto = [number, number, number, number | null, number | null];

export interface TrajetoTelemetria {
  exame_hash: string;
  bbox: [number, number, number, number] | null;
  pontos: PontoDoTrajeto[];
  origem_tempo_max: OrigemTempoTelemetria;
  sincronia_gps: SincroniaGps;
  tolerancia_s: number | null;
  delta_s: number | null;
  eventos: EventoNoTrajeto[];
  ficha_rotulo: RotuloTelemetria | null;
  sem_relogio_motivo: string | null;
}

export async function getTelemetria(hash: string, signal?: AbortSignal): Promise<EstadoTelemetria> {
  return request<EstadoTelemetria>(`/telemetria/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Roda SINCRONO (milissegundos, US$ 0) e devolve a linha pronta. */
export async function processarTelemetria(
  hash: string,
  signal?: AbortSignal,
): Promise<EstadoTelemetria> {
  return request<EstadoTelemetria>(
    `/telemetria/exames/${encodeURIComponent(hash)}/processar`,
    { method: "POST", signal },
  );
}

export async function getTrajetoTelemetria(
  hash: string,
  signal?: AbortSignal,
): Promise<TrajetoTelemetria> {
  return request<TrajetoTelemetria>(`/telemetria/exames/${encodeURIComponent(hash)}/trajeto`, {
    signal,
  });
}
// ==== fim P8 ====

// ==== E2 · P3 · mapa e medidas por quadrante ====
/*
  O MAPA 5 + 2 E A CONFIRMACAO HUMANA (E2.2) e as MEDIDAS POR QUADRANTE (E2.7).
  Contrato §2.8. `StatusMapa` e `CamerasDoExame`, acima, sao deste pacote.

  A confirmacao nao apaga a resposta do modelo: `mapa` continua o da maquina,
  `mapa_confirmado` e o da pessoa, e `mapa_vigente` diz qual vale. Nenhum
  rotulo de qualidade vem daqui — `quadrantes` e so medida; quem rotula e a
  ficha de qualidade do insumo.
*/
import type {
  CODIGOS_DIAGNOSTICO_QUADRANTE as _CODIGOS_DIAGNOSTICO_P3,
  STATUS_MAPA as _STATUS_MAPA_P3,
} from "./vocabulario";

/** O mesmo conjunto de `StatusMapa`, vindo do vocabulario comum. */
export type StatusMapaDoVocabulario = (typeof _STATUS_MAPA_P3)[number];
/** Trava de compilacao: `StatusMapa` e o vocabulario nao podem divergir. */
export const _STATUS_MAPA_CONFERE_P3: [StatusMapa] extends [StatusMapaDoVocabulario]
  ? [StatusMapaDoVocabulario] extends [StatusMapa] ? true : never
  : never = true;

/** `null` = o diagnostico nao achou defeito (e nao "a camera e boa"). */
export type CodigoDiagnosticoQuadrante = (typeof _CODIGOS_DIAGNOSTICO_P3)[number];

/** Uma rodada da escalada: 5 trechos e, se nao resolveu, mais 2. */
export interface RodadaDoMapa {
  rodada: number;
  trechos: number[];
  n_votos: number;
  status_mapa: StatusMapa;
  resolvido: boolean;
  motivo_escalada: string | null;
  margem: number | null;
  placar: Record<string, string>;
  n_validos: number;
  n_invalidos: number;
  n_perdidos: number;
}

/** O que a apuracao acrescenta em `CamerasDoExame.apuracao` (5 + 2 ate 04/10; cadeia/1 depois). */
export interface ApuracaoDoMapa {
  /** `apuracao-5mais2/1` na linha antiga; `cadeia/1` desde 04/10/2026; `cadeia/2` (+ a guarda fisica) desde 13/10. */
  regra?: string;
  status_mapa?: StatusMapa;
  motivo?: string | null;
  resolvido?: boolean;
  escalou?: boolean;
  motivo_escalada?: string | null;
  rodadas?: RodadaDoMapa[];
  quadrantes_ambiguos?: string[];
  placar?: Record<string, string>;
  margem?: number | null;
  degrau?: string | null;
  abertos?: string[];
  n_validos?: number;
  n_invalidos?: number;
  n_perdidos?: number;
  n_trechos?: number;
  confianca?: { acordo_empirico?: number | null; mediana_declarada?: number | null; n_discordantes?: number };
  /** Votos por papel em cada letra. Na cadeia/1 e PONDERADA (pode ser fracionaria). */
  urna?: Record<string, Record<string, number>>;
  margens?: Record<string, number | null>;
  /** cadeia/1: as etapas que rodaram, a decisao e o custo de cada uma. Ausente na linha antiga. */
  cadeia?: CadeiaDoMapa;
  /** cadeia/1: a etapa 0 (gratis), quadrante a quadrante. */
  legibilidade?: LegibilidadeDoMapa;
  /** cadeia/1, `ERRO_INFRA`: os quadrantes em duvida sem NENHUM voto valido ao fim da cadeia. */
  quadrantes_sem_voto?: string[];
  /** cadeia/2: a guarda fisica inteira (o resumo esta em `cadeia.decisao.guarda_fisica`). */
  guarda_fisica?: GuardaFisicaDoMapa | null;
}

/*
  A CADEIA DO MAPA (regra `cadeia/1`, decisao do dono de 04/10/2026). O mapa nao
  espera mais uma pessoa: etapa 0 mede a legibilidade (gratis), etapa 1 sao os
  5 trechos do Flash, etapa 2 foca os quadrantes em duvida (+2 trechos, mais
  quadros e resolucao alta), etapa 3 escala ao Pro um trecho por vez ate a
  certeza ou 4 chamadas. Com a certeza do Pro, o mapa e o DELE; sem ela, a
  maioria ponderada (Pro pesa 1,5) decide — e so ela deixa quadrante "sem
  certeza". Os campos sao opcionais na tela: ela degrada sem quebrar se faltar um.
*/

export type MetodoDaCadeia =
  | "consenso_flash"
  | "foco_flash"
  | "certeza_pro"
  | "maioria_ponderada"
  | "ilegivel"
  | "sem_votos";

export interface QuadranteIlegivel {
  letra: string;
  posicao?: string;
  /**
   * Os motivos medidos: a frase do diagnostico e/ou a da fracao de quadros
   * escuros. Linha antiga pode trazer o CODIGO cru (`escuro`, `diagnóstico
   * liso`): a tela o traduz (`cadeia.motivoDeIlegibilidade`).
   */
  motivos?: string[];
}

export interface LegibilidadeDoMapa {
  /** `legibilidade_mapa/1`. */
  regra?: string;
  /** false = nao havia medida; ausencia nao e defeito, e o mapa seguiu. */
  medido?: boolean;
  ok?: boolean;
  ilegiveis?: QuadranteIlegivel[];
}

export interface ResultadoDaEtapaDoMapa {
  /** {letra: papel} assentados ao fim desta etapa. */
  decididos?: Record<string, string>;
  duvida?: string[];
  /** {letra: "4×3"}. */
  placar?: Record<string, string>;
  resolveu?: boolean;
  /** `escalada_desligada`: a rota do exame desligou a etapa 3 (sem chamada, sem custo). */
  parou_por?: "certeza_do_pro" | "limite_de_chamadas" | "escalada_desligada" | null;
  /** So na etapa 0. */
  ilegiveis?: QuadranteIlegivel[];
  /** Uma frase em pt-BR, pronta para a tela. */
  frase?: string;
}

export interface EtapaDaCadeia {
  /** 0 = legibilidade (gratis); 1, 2, 3 = as pagas. */
  etapa: number;
  nome?: string;
  modelo?: string | null;
  paga?: boolean;
  foco?: string[] | null;
  papeis_em_foco?: string[] | null;
  fixos?: Record<string, string> | null;
  fps?: number | null;
  janela_s?: number | null;
  resolucao?: string | null;
  peso?: number | null;
  /** Na ordem de envio. */
  trechos?: number[];
  /** POSTs cobrados, retentativas incluidas. */
  chamadas?: number;
  votos_validos?: number;
  votos_invalidos?: number;
  votos_perdidos?: number;
  /** O COBRADO da etapa: aproveitado + descartado. */
  custo_usd?: number | null;
  custo_descartado_usd?: number | null;
  tokens?: { entrada?: number; saida?: number; raciocinio?: number };
  inicio?: string;
  fim?: string;
  duracao_ms?: number;
  resultado?: ResultadoDaEtapaDoMapa;
}

export interface DecisaoDaCadeia {
  metodo?: MetodoDaCadeia;
  /**
   * true = consenso, foco ou certeza do Pro; false SO na `maioria_ponderada`;
   * null sem mapa (`ilegivel`, `sem_votos`). Nunca leia `!certeza` como "sem
   * certeza": null nao e duvida, e falta de mapa.
   */
  certeza?: boolean | null;
  /** De onde saiu o mapa dos quadrantes em duvida (null sem duvida ou sem mapa). */
  origem?: "lideres_do_pro" | "maioria_ponderada" | null;
  /**
   * So vale com `metodo = maioria_ponderada` (`cadeia.quadrantesSemCerteza`).
   * A linha `sem_votos` gravada antes da correcao de 04/10 trazia aqui a
   * duvida inteira — que nao e "sem certeza", e falta de voto.
   */
  quadrantes_sem_certeza?: string[];
  /** `sem_votos` (ERRO_INFRA): os quadrantes em duvida sem nenhum voto valido. */
  quadrantes_sem_voto?: string[];
  /** `sem_votos`: o ultimo erro de transporte gravado. */
  ultimo_erro?: string | null;
  /** {modelo: peso}. */
  pesos?: Record<string, number>;
  /**
   * Os quadrantes da ultima duvida: {letra: {papel: soma ponderada}}; {} sem
   * duvida. Na `certeza_pro` e so referencia: o mapa e o do Pro.
   */
  placar_ponderado?: Record<string, Record<string, number>>;
  desempate?: "pro" | "confianca" | "etapa_1" | "ordem" | null;
  frase?: string;
  /**
   * cadeia/2 (13/10/2026): o resumo da guarda fisica do sentido do fluxo; o
   * detalhe (fracoes, trechos, regua) vem em `apuracao.guarda_fisica`. null
   * quando nao houve mapa a guardar; ausente na cadeia/1.
   */
  guarda_fisica?: GuardaFisicaDoMapa | null;
}

/**
 * A GUARDA FISICA (`cameras/cadeia.guarda_do_sentido`, regra
 * `sentido_do_fluxo/1`): andando, a frontal abre a cena e a traseira fecha —
 * na B. Em C, D e E (23/10/2026) a traseira e a camera do lado esquerdo, a
 * guarda so cobra a frontal e grava a `dispensa`.
 * `acao = portao_humano` (estado AMBIGUO) poe o mapa em `MAPA_AMBIGUO`, motivo
 * `sentido_do_fluxo`: o portao da visao nao abre sem uma pessoa confirmar.
 */
export interface GuardaFisicaDoMapa {
  /** `sentido_do_fluxo/1`. */
  regra?: string;
  estado?: "CONFERE" | "TROCADO" | "AMBIGUO" | "SEM_MEDIDA";
  /**
   * AMBIGUO: `frontal_nao_abre` | `traseira_nao_fecha` | `troca_sem_folga` |
   * `troca_sem_coerencia`; SEM_MEDIDA: o motivo da medida. null no CONFERE e no TROCADO.
   */
  motivo?: string | null;
  acao?: "nenhuma" | "trocou_frontal_e_traseira" | "portao_humano";
  /** `fisica:expansao` so na troca. */
  origem?: string | null;
  /** Uma frase em pt-BR, pronta para a tela. */
  frase?: string;
  /**
   * C/D/E desde 23/10/2026 (`cameras/categoria.TRASEIRA_DA_CATEGORIA`): a perna
   * "a traseira fecha a cena" dispensada, porque a traseira e a camera do lado
   * esquerdo. Ausente na B e na guarda gravada antes (que cobrou a traseira).
   */
  dispensa?: GuardaFisicaDispensa | null;
}

/** A perna da guarda que a categoria dispensa (`guarda_fisica.dispensa`). */
export interface GuardaFisicaDispensa {
  /** `traseira_fecha`. */
  perna?: string;
  /** `traseira_de_lado`. */
  motivo?: string;
  /** `lado_esquerdo`. */
  layout_da_traseira?: string;
  frase?: string;
}

export interface CadeiaDoMapa {
  versao?: string;
  /** A etapa 0 sempre; as pagas, so as que rodaram. */
  etapas?: EtapaDaCadeia[];
  decisao?: DecisaoDaCadeia;
  custo_total_usd?: number | null;
  custo_descartado_usd?: number | null;
  chamadas_total?: number;
  duracao_total_ms?: number;
}

export interface DiagnosticoDoQuadrante {
  n_quadros: number;
  contraste: number | null;
  nitidez: number | null;
  luminancia: number | null;
  invariancia: number | null;
  codigo: CodigoDiagnosticoQuadrante | null;
  /** O texto do legado `quadrantes_v2`, byte a byte. */
  motivo: string | null;
}

/** As medidas de UM quadrante. `null` = nao medido — nunca zero. */
export interface MedidasDoQuadrante {
  energia: { mediana: number | null; p10: number | null; p90: number | null; max: number | null };
  giro: { mediana_abs: number | null; p90_abs: number | null; corr_com_outros: number | null };
  luma: {
    media: number | null;
    p05: number | null;
    p95: number | null;
    min: number | null;
    max: number | null;
    frac_abaixo_16: number | null;
    frac_acima_235: number | null;
  };
  andando: { frac_parado_com_outros_andando: number | null; n_pares_andando: number };
  diagnostico: DiagnosticoDoQuadrante | null;
}

/** `cameras_processamento.quadrantes`, versao `quadrantes/1`. */
export interface QuadrantesDoMapa {
  versao: string;
  perfil: { fps: number | null; pares: number | null; quadros: number | null; dims: [number, number] };
  limiares: Record<string, number>;
  por_posicao: Record<Posicao, MedidasDoQuadrante>;
  diagnostico_meta: Record<string, unknown>;
}

/**
 * Uma pessoa confirma o mapa INTEIRO. 404 sem linha; 409 se a rodada nao
 * terminou; 422 se nao for a bijecao dos quatro. Nao chama modelo nenhum.
 */
export async function confirmarMapaDeCameras(
  hash: string,
  mapa: Record<Posicao, string>,
  nota: string | null,
  signal?: AbortSignal,
): Promise<CamerasDoExame> {
  return request<CamerasDoExame>(`/cameras/exames/${encodeURIComponent(hash)}/confirmar`, {
    method: "POST",
    body: { mapa, nota },
    signal,
  });
}

/** Desfaz a confirmacao: o mapa da maquina volta a ser o vigente. */
export async function desfazerConfirmacaoDoMapa(hash: string, signal?: AbortSignal): Promise<CamerasDoExame> {
  return request<CamerasDoExame>(`/cameras/exames/${encodeURIComponent(hash)}/confirmacao`, {
    method: "DELETE",
    signal,
  });
}
// ==== fim P3 ====

// ==== E2 · P2 · fusão por período e faixa corroborativa ====
/*
 * A classe do instante (decisão 4) e a faixa corroborativa da telemetria (T4).
 * Os literais vêm de `lib/vocabulario.ts` — o espelho de
 * `backend/app/core/vocabulario.py` —, e não são redeclarados aqui.
 */

/** MEDIDO | ESTIMADO | SEM_SINCRONIA. */
export type OrigemTempoFusao = (typeof import("./vocabulario").ORIGEM_TEMPO)[number];

/** CONCORDA | DIVERGE | INDETERMINADO | PROXIMO. */
export type RelacaoCorroborativa =
  (typeof import("./vocabulario").RELACOES_CORROBORATIVAS)[number];

/** Um fato da telemetria como a faixa o mostra — sem coordenada. */
export interface FatoDaTelemetriaNaFaixa {
  id: string;
  tipo: string;
  subtipo: string;
  inicio_s: number | null;
  fim_s: number | null;
  origem_tempo: OrigemTempoFusao;
  sincronia_gps: string | null;
  tolerancia_s: number | null;
  procedencia: string | null;
  atributos: Record<string, unknown>;
}

/** Um vínculo entre um fato da telemetria e uma observação da fusão. */
export interface VinculoCorroborativo {
  regra: string;
  versao_regra: string;
  telemetria: string;
  tipo_telemetria: string;
  observacao: {
    fonte: string;
    origem_chave: string;
    tipo: string | null;
    subtipo: string | null;
    inicio_ms: number;
    fim_ms: number | null;
    origem_tempo: OrigemTempoFusao | null;
  };
  relacao: RelacaoCorroborativa;
  modo: string;
  /** Só no modo "perto": a diferença entre os dois inícios, em segundos. */
  erro_s: number | null;
  tolerancia: Record<string, unknown>;
  evidencia: string;
  porque: string;
  /** Sempre `false` na v1: a telemetria corrobora, nunca é testemunha. */
  conta_como_testemunha: boolean;
  excluido_por_circularidade: boolean;
}

/** A faixa da telemetria dentro de `fusao_execucoes.corroborativas`. */
export interface FaixaDaTelemetria {
  papel: string;
  obrigatoria: boolean;
  ausencia_bloqueia: boolean;
  /** O estado da etapa de telemetria. `ausente` nunca é falha nem alerta. */
  estado: string;
  ficha_rotulo: string | null;
  origem_tempo_max: string | null;
  assinatura_regras: string | null;
  atualizado_em: string | null;
  /** Os fatos COM relógio (MEDIDO/ESTIMADO, tolerância no teto). */
  fatos: FatoDaTelemetriaNaFaixa[];
  /** Os fatos sem relógio: vão para o rodapé, nunca para um bloco. */
  sem_relogio: FatoDaTelemetriaNaFaixa[];
  fora_do_video: FatoDaTelemetriaNaFaixa[];
  vinculos: VinculoCorroborativo[];
  metricas: Record<string, number>;
}

export interface CorroborativasDaFusao {
  versao: string;
  assinatura: string;
  telemetria: FaixaDaTelemetria;
}
// ==== fim P2 ====

// ==== E2 · P1 · Visão: matriz, nao_solicitada e lateral sob demanda ====

/** E2.1: os tres modos de uma camera (`core.vocabulario.MODOS_CAMERA`). */
export type ModoDaCamera = (typeof import("./vocabulario").MODOS_CAMERA)[number];

/**
 * E2.3: a previsao da lateral CONDICIONAL — so as janelas do gatilho.
 * `previsao_pelo_teto` = a frontal ainda nao publicou, e a conta usa o teto de
 * janelas (um numero para cima, dito na `nota`).
 */
export interface EstimativaCondicional {
  modelo: string;
  papel: PapelCamera;
  perfil: string;
  modo: "condicional";
  n_janelas: number;
  janelas_s: number[];
  janela_s: number;
  fps_enviado: number;
  previsao_pelo_teto: boolean;
  preco_conhecido: boolean;
  usd: number | null;
  usd_por_janela: number | null;
  usd_teto: number | null;
  nota: string;
}

/** E2.3: uma janela do gatilho da lateral (`configuracao.gatilho.janelas`). */
export interface JanelaDoGatilho {
  indice?: number;
  inicio_ms: number;
  fim_ms: number;
  motivos: string[];
  ancoras: Array<{
    papel: string;
    evento_id: number | null;
    tipo: string;
    subtipo: string;
    inicio_ms: number;
  }>;
}

/** `metricas.cobertura` (E2.1/E2.3): o que a camera de fato olhou. */
export interface CoberturaDaCamera {
  /** `null` = integral (olhou o exame inteiro por desenho). */
  cobertura_ms: Array<[number, number]> | null;
  cobertura_fracao: number | null;
  duracao_coberta_s: number;
  janelas_lidas: number;
  janelas_nao_observaveis: number;
}

/** `metricas.gatilho`: as contagens do gatilho da lateral condicional. */
export interface ResumoDoGatilho {
  versao: string | null;
  n_janelas: number;
  n_descartadas_pelo_teto: number;
  por_motivo: Record<string, number>;
  contexto_ausente: boolean | null;
  n_paradas: number | null;
  n_paradas_sem_contexto: number | null;
  /**
   * E2-bis (D5, `lateral_parada.v2`): os `PARADO` que contaram como parada, e
   * os que se qualificavam mas continuam uma `PARADA` (uma testemunha por
   * raiz). Ausentes/`null` na linha de um gatilho v1, que não os contava.
   */
  n_parados?: number | null;
  n_parados_da_mesma_parada?: number | null;
  cobertura_ms: Array<[number, number]>;
}

/** O que a E2 acrescenta em `visao_execucoes.metricas`. */
export interface MetricasDaVisaoE2 {
  cobertura?: CoberturaDaCamera;
  gatilho?: ResumoDoGatilho;
  material?: Record<string, unknown>;
}

/** `/visao/exames/{hash}` com o que a E2 acrescenta: as SOLICITADAS. */
export type VisaoDoExameE2 = VisaoDoExame & {
  /** Os papeis que o botao "processar as solicitadas" dispara. */
  solicitadas?: PapelCamera[];
};

/** A configuracao de uma camera com o modo e o gatilho (E2.1). */
export type ConfigVisaoCategoriaE2 = ConfigVisaoCategoria & {
  /** O modo GRAVADO; `null` = o padrao do papel. */
  modo?: ModoDaCamera | null;
  gatilho?: Record<string, unknown> | null;
  modo_efetivo?: ModoDaCamera | null;
  modo_padrao?: ModoDaCamera | null;
  modos_permitidos?: ModoDaCamera[];
  gatilho_efetivo?: Record<string, unknown> | null;
  gatilho_padrao?: Record<string, unknown> | null;
  divergencias?: Record<string, unknown>;
  assinatura_regras?: string | null;
  problemas?: string[];
};

/** O que a tela manda com o modo e o gatilho. */
export type ConfigVisaoCategoriaEscritaE2 = ConfigVisaoCategoriaEscrita & {
  modo?: ModoDaCamera | null;
  gatilho?: Record<string, unknown> | null;
};

/**
 * Dispara as SOLICITADAS (E2.1): a integral roda, a condicional espera a
 * frontal e a interna, a `nao_solicitada` ganha a linha e nao roda. Mesma rota
 * de `processarAsQuatroCameras`, que continua existindo.
 */
export async function processarAsSolicitadas(hash: string): Promise<VisaoDoExameE2> {
  return request<VisaoDoExameE2>(`/visao/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    timeoutMs: TIMEOUT_VISAO_MS,
  });
}

/** Salva uma camera com o modo e o gatilho de cada categoria (E2.1). */
export async function salvarConfigVisaoE2(
  papel: PapelCamera,
  categorias: ConfigVisaoCategoriaEscritaE2[],
  signal?: AbortSignal,
): Promise<ConfigVisao> {
  return request<ConfigVisao>(`/config/visao/${papel}`, {
    method: "PUT",
    body: { categorias },
    signal,
    timeoutMs: TIMEOUT_VISAO_MS,
  });
}
// ==== fim P1 ====

// ==== E2 · P5 · Anexo K ====
/*
  As etapas `falas` e `conduta` não têm leitura própria no portal: os alertas
  delas chegam pela aba Compliance (P6), e as linhas pelo Processamento. Daqui
  sobra o Anexo K, na forma do contrato (§5.1).
*/
import type {
  ANEXO_K_RESPOSTAS as _ANEXO_K_RESPOSTAS_P5,
  PASSADAS as _PASSADAS_P5,
} from "./vocabulario";

export type RespostaAnexoK = (typeof _ANEXO_K_RESPOSTAS_P5)[number];
export type PassadaAnexoK = (typeof _PASSADAS_P5)[number];

/** Um item do Anexo K, na forma do contrato (§5.1), mais o `detalhe` legível. */
export interface ItemAnexoK {
  item: number;
  titulo: string;
  resposta: RespostaAnexoK;
  motivo: string;
  evidencias: string[];
  entra_no_estado: boolean;
  detalhe?: string;
}

export interface AnexoK {
  versao: string;
  passada: PassadaAnexoK;
  itens: ItemAnexoK[];
  resumo?: Record<RespostaAnexoK, number>;
  completo?: boolean;
}

// ==== fim P5 ====

// ==== E2 · P6 · insumo e compliance ====
// A ficha de qualidade (`/insumo`) e o compliance (`/compliance`), E2.7/E2.8.
// Os enums vêm de `lib/vocabulario.ts`; as seções saem como o backend grava
// (a grafia do §2.5/§2.6 do contrato), e a aba lê com guarda (`?? null`).

/** Uma fonte da ficha: mosaico, câmera ou áudio. */
export interface FonteDaFicha {
  rotulo: string | null;
  completo: boolean;
  motivos: string[];
  faltam: string[];
  medidas_usadas?: Record<string, unknown>;
  identificacao?: string | null;
  quadrante?: string | null;
  margem?: number | null;
  modo?: string | null;
  confirmado?: boolean;
}

export interface InsumoDoExame {
  exame_hash: string;
  estado: string;
  progresso: number | null;
  etapa: string | null;
  versao: string | null;
  assinatura_regras: string | null;
  assinatura_vigente: string | null;
  categoria: string | null;
  run_id: string | null;
  rotulo_exame: string | null;
  completo: boolean | null;
  fontes: Record<string, FonteDaFicha> | null;
  audio: Record<string, unknown> | null;
  origens: Record<string, string> | null;
  regra_camera: string | null;
  regra_audio: string | null;
  regra_exame: string | null;
  custo_usd: number | null;
  parede_s: number | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  erro: string | null;
  atualizado_em: string | null;
}

/** Um alerta do compliance (§2.6). `ver_ouvir.aba` pode vir com `{hash}`. */
export interface AlertaCompliance {
  id: string;
  tipo: string;
  familia: string;
  classe: string | null;
  abrangencia: "exame" | "trecho";
  inicio_ms: number | null;
  fim_ms: number | null;
  periodo: { indice: number; inicio_ms: number; fim_ms: number } | null;
  instante: { origem_tempo: string; precisao_ms: number | null; janela_ms: number[] } | null;
  descricao: string;
  fontes: string[];
  testemunhas: number;
  evidencias: string[];
  qualidade: { insumo_no_trecho?: Record<string, string | null> } | null;
  motivo: {
    regra: string;
    versao: string;
    classe_fato: string | null;
    condicoes_cumpridas: string[];
    condicoes_faltantes: string[];
    fundido_com?: string[];
    /** `ausencia_qualificada` na família que nasce do silêncio medido de uma
     *  fonte que olhou (a omissão da seta): zero testemunhas, sem
     *  `evidencia_positiva`. Ausente nas outras. */
    base?: string;
    detalhe?: Record<string, unknown>;
  };
  ver_ouvir: { camera: string | null; inicio_ms: number; fim_ms: number; aba: string } | null;
}

/** Uma entrada do checklist: todo número com a origem e a regra. */
export interface EntradaChecklist {
  valor: unknown;
  origem: string;
  regra: string;
  detalhe: Record<string, unknown>;
}

export interface FatorTriagem {
  fator: string;
  peso: number;
  n: number;
  contribuicao: number;
  evidencias: string[];
  rotulo?: string;
  porque?: string;
  /** O eixo da AD-5 (`padroes.EIXO_DO_FATOR`); null = fora dos dois (a baixa observabilidade). */
  eixo?: string | null;
}

/** Um eixo da triagem na aba (`triagem.eixos.<eixo>`, `triagem/2`). */
export interface EixoDaTriagemCompleto {
  score: number;
  faixa: string;
  destino: string;
  vai_para_o_supervisor: boolean;
  /** Os fatores que somaram neste eixo. */
  fatores: string[];
}

export interface TriagemCompliance {
  versao: string;
  score: number;
  faixa: string;
  destino: string;
  vai_para_o_supervisor?: boolean;
  fatores: FatorTriagem[];
  trava_falha_tecnica: boolean;
  corte_da_fila?: number;
  /** AD-5 (`triagem/2`): os campos de cima que são a soma de tudo, a fila de antes. */
  legado?: string[];
  /** AD-5 (`triagem/2`): os dois eixos. Ausente na triagem gravada antes. */
  eixos?: {
    regra: string;
    resultado?: EixoDaTriagemCompleto;
    conduta?: EixoDaTriagemCompleto;
    /** Fatores que não entram em eixo nenhum (a baixa observabilidade). */
    fora_dos_eixos?: string[];
  };
}

export interface FamiliaTransito {
  familia: string;
  classe: string;
  ocasioes: number;
  respostas: Record<string, number>;
  nao_avaliavel_motivo: string | null;
  regras?: string[];
  nota?: string;
  /** `OCORREU` do veredito por AUSÊNCIA (antes da decisão M): contado, sem alerta. */
  ocorreu_por_ausencia?: number;
  /** Só no `art_181` (sem régua): os tipos de manobra que contam as ocasiões. */
  manobras?: string[];
}

export interface CoberturaCompliance {
  etapas: Record<string, unknown>;
  fontes_solicitadas: string[];
  fontes_prontas: string[];
  fontes_nao_solicitadas: string[];
  fontes_com_falha: string[];
  janelas_perdidas: Array<Record<string, unknown>>;
  cobertura_temporal: Record<string, unknown>;
  regras_sem_fonte: { estruturais: string[]; por_nao_solicitada: string[]; deste_exame: string[] };
  rele_mensuravel: boolean | null;
  mapa: { status: string | null; degrau: string | null; confirmado_por: string | null };
  telemetria: string | null;
  completa: boolean;
  motivos_incompleta: string[];
}

export interface ComplianceDoExame {
  exame_hash: string;
  estado: string;
  progresso: number | null;
  etapa: string | null;
  versao: string | null;
  assinatura_regras: string | null;
  assinatura_vigente: string | null;
  fontes_mudaram: boolean;
  bloco_ms: number;
  categoria: string | null;
  run_id: string | null;
  commit: string | null;
  estados: string[] | null;
  estado_principal: string | null;
  n_alertas: number | null;
  n_por_estado: Record<string, number> | null;
  checklist: Record<string, EntradaChecklist> | null;
  alertas: AlertaCompliance[] | null;
  transito: { versao?: string; familias: FamiliaTransito[] } | null;
  cobertura: CoberturaCompliance | null;
  insumo: Record<string, unknown> | null;
  anexo_k: { versao: string; passada: string; itens: Array<Record<string, unknown>> } | null;
  seta: Record<string, unknown> | null;
  triagem: TriagemCompliance | null;
  fontes_lidas: Record<string, unknown> | null;
  /** A passada pós-cega (o confronto, E5): ler por `lerPosCega` (`lib/api-confronto.ts`). */
  pos_cega: Record<string, unknown> | null;
  /**
   * A `pos_cega` ainda é da passada cega desta linha (mesma assinatura, mesmo
   * `terminado_em`)? null sem confronto; ausente em backend anterior.
   */
  pos_cega_vigente?: boolean | null;
  n_seta_presente: number | null;
  n_seta_possivel_omissao: number | null;
  n_seta_nao_observavel: number | null;
  custo_usd: number | null;
  parede_s: number | null;
  video_disponivel: boolean;
  video_origem: string | null;
  portao_aberto: boolean;
  portao_motivo: string | null;
  portao_mensagem: string | null;
  erro: string | null;
  atualizado_em: string | null;
}

export function complianceEmAndamento(estado: { estado: string }): boolean {
  return estado.estado === "pendente" || estado.estado === "processando";
}

export async function getInsumoDoExame(hash: string, signal?: AbortSignal): Promise<InsumoDoExame> {
  return request<InsumoDoExame>(`/insumo/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Monta a ficha de qualidade: 202 e polling. US$ 0 (lê o master, não chama modelo). */
export async function processarInsumo(hash: string, signal?: AbortSignal): Promise<InsumoDoExame> {
  return request<InsumoDoExame>(`/insumo/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    signal,
  });
}

export async function getComplianceDoExame(hash: string, signal?: AbortSignal): Promise<ComplianceDoExame> {
  return request<ComplianceDoExame>(`/compliance/exames/${encodeURIComponent(hash)}`, { signal });
}

/** Calcula o compliance: 202 e polling. US$ 0, nenhum modelo. */
export async function processarCompliance(hash: string, signal?: AbortSignal): Promise<ComplianceDoExame> {
  return request<ComplianceDoExame>(`/compliance/exames/${encodeURIComponent(hash)}/processar`, {
    method: "POST",
    signal,
  });
}
// ==== fim P6 ====

// ==== E2 · P7 · orquestracao ====
/*
  O ORQUESTRADOR EM PROCESSO — o botao "Rodar tudo" (E2.9, §6 item 5). Contrato §3.4.

  Uma EXECUCAO percorre o grafo de `backend/app/esteira/grafo.py` (o MESMO do
  orquestrador de host) para um exame: dispara cada etapa pelo servico dela (os
  mesmos portoes das abas), le o estado de cada no da LINHA da etapa e grava a
  espera em vez de dormir. Os estados de no e de execucao vem de
  `@/lib/vocabulario` (ESTADOS_DE_NO, ESTADOS_DE_EXECUCAO).

  GASTA DINHEIRO: rodar dispara audio, mapa, visao, falas e motor (Vertex). O
  backend recusa (409) uma segunda execucao viva do mesmo exame.
*/

/**
 * Uma queda da execucao (P6 do dono, 24/09): a 1a reabre sozinha
 * (`acao = "retomada_automatica"`), a 2a para (`"para_analise"`, com o
 * `porque`: `teto_de_erros:2`, `nao_reabre:<no>:<motivo>`...).
 */
export interface FalhaDaExecucao {
  n: number;
  em: string | null;
  /** `laco` (o passo do agendador) ou `subida` (o processo que morreu). */
  origem: string;
  no: string | null;
  motivo: string | null;
  erro: string | null;
  nos: string[];
  acao: "retomada_automatica" | "para_analise";
  porque: string | null;
  /**
   * O nó e o motivo por trás de `porque = nao_reabre:<no>:<motivo>`, gravados
   * à parte desde 24/09: o nó da visão tem ":" (`visao:frontal`), e o motivo
   * pode ter (`preservada_pedido_manual:linha_erro`). Ausente na entrada
   * antiga — aí a tela lê do texto.
   */
  nao_reabre?: { no: string; motivo: string | null } | null;
  /** `porque = reabrir_falhou:<Excecao>`: a mensagem da exceção da reabertura. */
  erro_do_reabrir?: string | null;
}

/**
 * A P6 da ÚLTIMA execução do "Rodar tudo" deste exame (maior `id`) — a mesma
 * "última" e os mesmos nomes da listagem (`esteira` de `/exams`), com a lista
 * `falhas` inteira. Não depende da `rodada` que a aba mostra: com uma rodada do
 * host mais nova, é daqui que a aba tira o "parado para análise", as falhas e o
 * "Retomar". `retomadas_auto`/`para_analise`/`falhas` `null` = banco antes da
 * migração ("não se sabe").
 */
export interface UltimaExecucaoDaEsteira {
  execucao_id: number;
  run_id: string | null;
  estado: string | null;
  motivo: string | null;
  /** O que a execução GRAVOU (a frase da mídia no "Retomar"). */
  limpar_midia: boolean | null;
  retomadas_auto: number | null;
  para_analise: boolean | null;
  falhas: FalhaDaExecucao[] | null;
  criado_em: string | null;
  terminado_em: string | null;
}

/** O bloco `rodada` de `GET /exams/{hash}/esteira`. */
export interface RodadaDaEsteira {
  id: number;
  run_id: string;
  estado: string;
  motivo: string | null;
  plano: string;
  modo: string;
  limpar_midia: boolean;
  opcoes: Record<string, Record<string, unknown>>;
  no_corrente: string | null;
  criada_em: string | null;
  iniciada_em: string | null;
  terminada_em: string | null;
  custo_usd: number | null;
  /**
   * P6: quantas vezes o orquestrador reabriu sozinho; `para_analise` = parou
   * por falha e espera um humano; `falhas` = o historico. `null` na rodada do
   * host e no banco antes da migracao ("nao se sabe", nao zero).
   */
  retomadas_auto?: number | null;
  para_analise?: boolean | null;
  falhas?: FalhaDaExecucao[] | null;
  /** Por no do grafo (`visao:frontal`, `motor`...): o estado do no e o motivo. */
  etapas: Record<string, { estado: string; motivo: string | null }>;
}

/** Um no do grafo dentro de uma execucao. */
export interface EtapaDaExecucao {
  no: string;
  estado: string;
  tentativa: number;
  /** A espera GRAVADA (erro transitorio, recusa temporaria). */
  nao_antes_de: string | null;
  iniciado_em: string | null;
  terminado_em: string | null;
  parede_s: number | null;
  heartbeat_em: string | null;
  /** O estado da linha da etapa na ultima leitura. */
  estado_fonte: string | null;
  assinatura: string | null;
  reaproveitada: boolean | null;
  custo_usd: number | null;
  erro: string | null;
  erro_classe: Record<string, number> | null;
  esperas: Record<string, unknown>;
  /** `dependencia:<no>:<estado>`, `sem_trilha`, `midia_ainda_necessaria:<no>`... */
  motivo: string | null;
}

export interface ExecucaoDaEsteira {
  id: number;
  run_id: string;
  exame_hash: string;
  plano: string;
  modo: string;
  reusar: boolean;
  limpar_midia: boolean;
  opcoes: Record<string, Record<string, unknown>>;
  estado: string;
  motivo: string | null;
  dono: string | null;
  heartbeat_em: string | null;
  criado_em: string | null;
  iniciado_em: string | null;
  terminado_em: string | null;
  parede_s: number | null;
  custo_usd: number | null;
  commit: string | null;
  imagem: string | null;
  limpeza: Record<string, unknown> | null;
  criado_por: string | null;
  /** P6: as retomadas automaticas, a marca "para analise" e o historico das quedas. */
  retomadas_auto?: number;
  para_analise?: boolean;
  falhas?: FalhaDaExecucao[];
  no_corrente: string | null;
  etapas: EtapaDaExecucao[];
}

export interface PedidoDeRodada {
  run_id?: string;
  /**
   * O `a1` (Cronologia A) foi aposentado em 22/10/2026: o servidor o recusa.
   * `c_e2`/`d_e2`/`e_e2`: a esteira da B copiada para C, D e E (22/10/2026).
   */
  plano?: PlanoDaEsteira;
  modo?: "completo" | "analises";
  reusar?: boolean;
  /**
   * AUSENTE = o padrao do ambiente (`ESTEIRA_LIMPAR_MIDIA_PADRAO`, P5): ligado
   * na producao, desligado no dev. A midia local so sai com a execucao
   * concluida de fato e sem `para_analise`. `true`/`false` explicitos ganham.
   */
  limpar_midia?: boolean;
  opcoes?: Record<string, Record<string, unknown>>;
}

export interface RodadaAceita {
  execucao_id: number;
  estado: string;
  run_id: string;
}

/** Os estados de execucao em que ela ainda anda (ou espera gente). */
export function execucaoViva(estado: string | null | undefined): boolean {
  return estado === "NA_FILA" || estado === "EM_EXECUCAO" || estado === "CANCELANDO"
    || estado === "PRECISA_DE_DADO";
}

/** "Rodar tudo": cria a execucao (202). 409 se ja ha uma viva, ou se o examinador nao avaliou o exame (04/10/2026). PAGA. */
export async function rodarTudo(
  hash: string,
  pedido: PedidoDeRodada = {},
  signal?: AbortSignal,
): Promise<RodadaAceita> {
  return request<RodadaAceita>(`/esteira/exames/${encodeURIComponent(hash)}/rodar`, {
    method: "POST",
    body: pedido,
    signal,
  });
}

/** Colhe o que ficou pronto e reenfileira o resto (nao repaga o que ja fechou). */
export async function retomarExecucao(id: number, signal?: AbortSignal): Promise<ExecucaoDaEsteira> {
  return request<ExecucaoDaEsteira>(`/esteira/execucoes/${id}/retomar`, { method: "POST", signal });
}

/** Cooperativo; `forcar` cancela as tarefas e grava `interrompido` na linha. */
export async function cancelarExecucao(
  id: number,
  forcar = false,
  signal?: AbortSignal,
): Promise<ExecucaoDaEsteira> {
  return request<ExecucaoDaEsteira>(
    `/esteira/execucoes/${id}/cancelar${forcar ? "?forcar=true" : ""}`,
    { method: "POST", signal },
  );
}

/** O ledger da execucao em CSV, no por no (o link de download da aba). */
export function urlDoLedgerDaExecucao(id: number): string {
  return `${API_BASE_URL}/esteira/execucoes/${id}/ledger.csv`;
}

/*
  REPROCESSAR E LIMPAR UM EXAME (12/10/2026, pedido do dono): os quatro botoes
  da aba Processamento. Os grupos (`extracao`, `analise`) sao do backend
  (`esteira/acoes_do_exame.py`, derivados do grafo): a tela nao os repete.

    Reanalisar           execucao nova com refazer=ANALISE e manter=EXTRACAO:
                         a analise de novo sobre os dados ja extraidos (PAGA a
                         analise, nao a extracao). 409 se falta extrair.
    Reprocessar do zero  limpa tudo e cria o "Rodar tudo" (PAGA tudo). Pede
                         `confirmacao: "APAGAR"`.
    Limpar analise       apaga as linhas dos nos de analise; nada roda.
    Limpar tudo          apaga as linhas de todos os nos e as pastas de midia
                         do exame; nada roda. Pede `confirmacao: "APAGAR"`.

  O historico de execucao e de custo (`esteira_*`) nunca sai: o teto de gasto
  soma o custo dele, e apagar abriria orcamento. As quatro sao recusadas (409,
  `detail` = a frase) com execucao viva, linha em curso, reidratacao ou envio
  de laudo em andamento; `GET .../acoes` diz o mesmo ANTES, em `bloqueio`.
*/

/** As quatro acoes, nas chaves de `AcoesDoExame.acoes`. */
export type NomeDaAcaoDoExame = "reanalisar" | "do_zero" | "limpar_analise" | "limpar_tudo";

/** O registro de uma limpeza (`esteira_limpezas.acao`). */
export type AcaoDeLimpeza = "limpar_analise" | "limpar_tudo" | "do_zero";

export type GrupoDoNo = "extracao" | "analise";

/**
 * Um no na previa de uma acao. Os campos sao de dois tipos, e o outro vem
 * `null`: Reanalisar e Reprocessar do zero trazem a previa do orquestrador
 * (`refaz`, `paga`, `motivo`, `recusada`, `mantida_apesar_de`); as limpezas
 * trazem o que sai (`apaga`, `n_linhas`).
 */
export interface LinhaDaAcao {
  /** A chave do no no grafo (`audio`, `visao:frontal`): a de `custo.por_no`. */
  no: string;
  grupo: GrupoDoNo;
  /** O estado da linha da etapa hoje (`pronto`, `ausente`, `erro`...). */
  estado: string;
  refaz: boolean | null;
  paga: boolean | null;
  /** O porque de refazer (`assinatura`, `linha_ausente`, `refazer_pedido`...)
   *  ou, na recusada, o porque da recusa (`extracao_ausente`). */
  motivo: string | null;
  recusada: boolean | null;
  /** No mantido (extracao) que o "Rodar tudo" refaria: o motivo que valeria
   *  sem o `manter` (`assinatura`: a regra mudou). `null` = esta atualizado. */
  mantida_apesar_de: string | null;
  apaga: boolean | null;
  /** As linhas do banco que saem com este no (todas as tabelas dele). */
  n_linhas: number | null;
}

export interface BloqueioDaAcao {
  /** O codigo da trava (do backend): vai nos "Detalhes tecnicos", e a tela
   *  mostra a `frase`. */
  motivo: string;
  /** A frase para quem decide (pt-BR), a mesma do 409. */
  frase: string;
}

export interface AcaoPrevia {
  acao: NomeDaAcaoDoExame;
  disponivel: boolean;
  bloqueio: BloqueioDaAcao | null;
  /** `true` = o POST exige `confirmacao: "APAGAR"`. */
  exige_digitar: boolean;
  linhas: LinhaDaAcao[];
  /** O que ja se pagou pelas linhas que saem (a limpeza nao devolve nada:
   *  o historico de custo fica). `null` nas acoes que nao apagam. */
  custo_das_linhas_usd: number | null;
  /** `false` = alguma linha paga que sai nao tem custo gravado: a soma e piso. */
  custo_das_linhas_completo?: boolean | null;
  /** Os bytes da midia de trabalho do exame que saem do disco. `null` = a
   *  acao nao apaga midia; `0` = apaga, mas nao ha pasta. */
  disco_bytes: number | null;
  /** `{tabela: n}` de tudo o que sai, inclusive o que nao e linha de no (o
   *  checkpoint do audio, o alinhamento da telemetria): os "Detalhes tecnicos". */
  tabelas?: Record<string, number> | null;
  /** O trabalho HUMANO que sai junto (o mapa de cameras confirmado a mao,
   *  as revisoes da visao), em frase. Vazio = nada. */
  humanos_que_saem: string[];
}

export interface UltimaLimpeza {
  acao: AcaoDeLimpeza;
  /** Quem pediu (o e-mail do login); `null` = nao gravado. */
  por: string | null;
  em: string;
  /** `{tabela: linhas apagadas}`. */
  linhas: Record<string, number>;
  bytes_liberados: number | null;
  custo_das_linhas_usd: number | null;
  /** A execucao que o "Reprocessar do zero" criou depois da limpeza. */
  execucao_id?: number | null;
}

export interface AcoesDoExame {
  exame_hash: string;
  grupos: Record<GrupoDoNo, string[]>;
  ultima_limpeza: UltimaLimpeza | null;
  acoes: Record<NomeDaAcaoDoExame, AcaoPrevia>;
  lido_em?: string | null;
}

export type EscopoDaLimpeza = "analise" | "tudo";

export interface LimpezaDoExame {
  escopo: EscopoDaLimpeza;
  linhas_apagadas: Record<string, number>;
  /** `0` em `analise`; `null` = as linhas sairam, mas a midia nao (o `aviso` diz por que). */
  bytes_liberados: number | null;
  custo_das_linhas_usd: number | null;
  registro_id: number;
  /** So quando a midia nao saiu depois do commit das linhas. */
  aviso?: string | null;
}

/**
 * O que cada uma das quatro acoes faria AGORA. So le — mas roda a previa do
 * orquestrador duas vezes (Reanalisar e do zero) e mede o disco do exame: o
 * prazo e o dobro do padrao.
 */
export async function getAcoesDoExame(hash: string, signal?: AbortSignal): Promise<AcoesDoExame> {
  return request<AcoesDoExame>(`/esteira/exames/${encodeURIComponent(hash)}/acoes`, {
    signal,
    timeoutMs: 20_000,
  });
}

/** "Reanalisar": a analise de novo sobre os dados extraidos (202). PAGA a analise. */
export async function reanalisarExame(hash: string, signal?: AbortSignal): Promise<RodadaAceita> {
  return request<RodadaAceita>(`/esteira/exames/${encodeURIComponent(hash)}/reanalisar`, {
    method: "POST",
    signal,
    timeoutMs: 30_000,
  });
}

/**
 * "Reprocessar do zero": limpa tudo e cria o "Rodar tudo" (202). PAGA tudo.
 * `confirmacao` e a palavra digitada ("APAGAR"); outra = 422. A limpeza
 * apaga pastas de midia antes de criar a rodada: prazo largo.
 */
export async function reprocessarDoZero(
  hash: string,
  confirmacao: string,
  signal?: AbortSignal,
): Promise<RodadaAceita> {
  return request<RodadaAceita>(`/esteira/exames/${encodeURIComponent(hash)}/reprocessar-do-zero`, {
    method: "POST",
    body: { confirmacao },
    signal,
    timeoutMs: 60_000,
  });
}

/** "Limpar analise" / "Limpar tudo" (200). `confirmacao` obrigatoria com `tudo`. Nada roda, nada e pago. */
export async function limparProcessamento(
  hash: string,
  escopo: EscopoDaLimpeza,
  confirmacao?: string,
  signal?: AbortSignal,
): Promise<LimpezaDoExame> {
  return request<LimpezaDoExame>(`/esteira/exames/${encodeURIComponent(hash)}/limpar`, {
    method: "POST",
    body: confirmacao === undefined ? { escopo } : { escopo, confirmacao },
    signal,
    timeoutMs: 60_000,
  });
}
// ==== fim P7 ====

// ==== fila do cluster ====
/*
  A FILA DO CLUSTER (docs/esteira/CLUSTER.md §3.4 e §3.5; decisoes D2 e D6 do
  dono, 29/09/2026): a central (valbot-02) guarda a fila e as workers
  (valbot-w-NN) so processam. A tela "Esteira" le a fila, a frota e a
  recomendacao de quantas workers ligar; a listagem de /exames enfileira lotes.

  MEDIDO x ESTIMADO: todo instante e todo custo chega marcado (`tipo`). O que
  ja aconteceu e MEDIDO; a previsao de inicio e fim, o custo de um lote e a
  recomendacao sao ESTIMADOS, com faixa p10–p90 e a base citada — e a tela
  nunca os mostra como medida. O `p50` de um ESTIMADO e o CENTRO PELA MEDIA
  (`centro: "media"`), e nao a mediana: numa fila, a mediana errou o fim do
  lote em -21%.

  Enfileirar NAO paga nada por si: cria execucoes NA_FILA. Quem paga e o
  trabalhador que as admite — por isso o `POST /esteira/lotes` exige o eco da
  previa (`n`, `custo_p50`) e responde 409 com a previa nova se ela mudou.
*/

/** Um instante que ja aconteceu. */
export interface InstanteMedido {
  tipo: "MEDIDO";
  valor: string;
}

/** Um instante previsto: p10, centro (pela media) e p90, e a chave da base. */
export interface InstanteEstimado {
  tipo: "ESTIMADO";
  p10: string;
  p50: string;
  p90: string;
  /** `fallback:m7` ou `perfil:<hash>` — a chave de uma das `previsao.bases`. */
  base: string;
}

export type InstanteDaFila = InstanteMedido | InstanteEstimado;

/** O custo gravado da execucao; `parcial` = ela ainda roda. */
export interface CustoMedido {
  tipo: "MEDIDO";
  valor: number;
  parcial: boolean;
}

/** O custo ESTIMADO de um lote: US$ por minuto de video x os minutos do lote. */
export interface CustoEstimado {
  tipo: "ESTIMADO";
  p10: number;
  p50: number;
  p90: number;
  base: string;
  centro: string;
  n_exames: number;
  minutos_de_video: number;
  /** Exames sem duracao conhecida: entram pela mediana das conhecidas do lote. */
  sem_duracao: number;
  taxa_us_por_min: number;
  por_no: Record<string, number>;
  /** `medido` (as execucoes deste banco) ou `fallback_valbot02`. */
  fonte: string;
  n_base: number;
  cv: number;
  nota: string | null;
  /** Sempre `nao_descontado`: o que ja esta pronto e reaproveitado nao sai da conta. */
  reaproveitamento: string;
}

/** Um processo que admite exames (uma linha de `esteira_trabalhadores`). */
export interface TrabalhadorLido {
  /** `"{VALBOT_VM}/{hostname}#{encarnacao}:{pid}"`; no dev (`ambos`), `hostname:pid`. */
  dono: string;
  vm: string | null;
  hostname: string | null;
  papel: string | null;
  /** `ativo` · `drenando` · `drenado` · `encerrado` · `incompativel`. */
  estado: string;
  /** O que a central pediu: `ativo` ou `drenar`. */
  desejado: string;
  /** Batimento dentro do lease e nao `encerrado`. */
  vivo: boolean;
  /** false = o proprio processo da API no papel `ambos` (dev): sem linha, sem drenar. */
  registrado: boolean;
  /** Segundos desde o ultimo batimento, pelo relogio do banco. */
  idade_s: number | null;
  batimento_em: string | null;
  iniciado_em: string | null;
  admitindo: boolean | null;
  /** Conta na capacidade: vivo, admitindo, ativo, sem `drenar` e sem disco baixo. */
  admite: boolean;
  max_exames: number | null;
  ocupadas: number | null;
  voltas?: number | null;
  voltas_com_falha?: number | null;
  maior_volta_s?: number | null;
  commit: string | null;
  imagem: string | null;
  impressao_regras?: string | null;
  versao_schema?: string | null;
  /** Versao do schema e das regras iguais as da central; null = nao se sabe. */
  compativel?: boolean | null;
  maquina?: string | null;
  disco_livre_gb: number | null;
  disco_baixo: boolean;
  perfil: string | null;
  /** A chave da base da previsao que este trabalhador usa. */
  base: string | null;
  capacidade?: Record<string, unknown>;
  /** As vagas do Vertex EFETIVAS deste processo (ao vivo ou do ambiente; CLUSTER.md §6.2). */
  vertex_vagas_globais?: number | null;
  vertex_vagas_por_modelo?: number | null;
  /** O que o trabalhador aplicou da central (`esteira_controle_vagas`) e de qual alvo. */
  vagas_ao_vivo?: Record<string, unknown> | null;
  /** So em `/trabalhadores`: o que a central pede HOJE para este slot. */
  vagas_pedidas?: Record<string, unknown> | null;
  /** A ultima recusa do teto de gasto que este trabalhador publicou (null: admitindo). */
  teto_de_gasto?: Record<string, unknown> | null;
  midia_efemera?: boolean | null;
}

/**
 * O teto de gasto da campanha (`GET|PUT /esteira/teto-de-gasto` e o
 * `teto_de_gasto` de `/fila`; CLUSTER.md §6.1). `pausada`: ligado e o proximo
 * da fila passaria do teto — os trabalhadores nao admitem; as em voo seguem.
 * O gasto e MEDIDO; as projecoes (p90) sao ESTIMADAS.
 */
export interface TetoDeGastoLido {
  ligado: boolean;
  pausada: boolean;
  admite: boolean;
  teto_usd: number | null;
  desde: string | null;
  gasto_usd: number;
  projecao_em_voo_usd: number;
  projecao_do_proximo_usd: number;
  total_usd: number;
  em_voo: number;
  execucoes: number;
  proximo: number | null;
  tipo_gasto: "MEDIDO";
  tipo_projecao: "ESTIMADO";
  base: string | null;
  nota: string | null;
  atualizado_por: string | null;
  atualizado_em: string | null;
  historico: Record<string, unknown>[];
  /** A leitura do teto falhou (a fila veio sem ele). */
  erro: string | null;
}

export interface VagasDaFila {
  total: number;
  ocupadas: number;
  livres: number;
}

/** De onde sai a duracao de um exame numa vaga (a calibracao de um perfil, ou a m7). */
export interface BaseDaPrevisao {
  chave: string;
  /** `medido` ou `fallback_m7`. */
  fonte: string;
  n: number;
  perfil: string | null;
  centro: string;
  w_media_s: number;
  w_p10_s: number;
  w_p50_s: number;
  w_p90_s: number;
  cauda_media_s: number;
  cauda_p10_s: number;
  cauda_p50_s: number;
  cauda_p90_s: number;
  nota: string | null;
}

/** O erro MEDIDO de uma parte (inicio ou fim) das previsoes guardadas. */
export interface ErroDeUmaParte {
  n: number;
  /** Media de (real - centro): positivo = atrasou. */
  vies_s: number;
  mae_s: number;
  p90_abs_s: number;
  /** Fracao (0–1) que caiu entre o p10 e o p90. */
  na_faixa: number;
}

export interface ErroDasPrevisoes {
  tipo: "MEDIDO";
  n: number;
  inicio: ErroDeUmaParte | null;
  fim: ErroDeUmaParte | null;
}

/**
 * `ok` · `sem_base` (usa a m7) · `fila_vazia` · `sem_trabalhador_vivo` ·
 * `sem_trabalhador_admitindo` · `parada_por_disco`.
 */
export type EstadoDaPrevisao = string;

export interface PrevisaoDaFila {
  tipo: "ESTIMADO";
  estado: EstadoDaPrevisao;
  frase: string;
  vagas: number;
  vazao_h: number | null;
  fila_termina_em: InstanteEstimado | null;
  bases: BaseDaPrevisao[];
  erro: ErroDasPrevisoes | null;
}

/** A fila com a central sozinha, com +1, +2 workers, e com as ligadas agora. */
export interface CenarioDaFrota {
  /** `central` · `+1` · `+2` · `atual`. */
  nome: string;
  workers: number;
  vms: string[];
  vagas: number;
  fila_termina_em: InstanteEstimado | null;
  cabe_na_meta: boolean;
}

/** Um lote com meta propria de termino. */
export interface LoteNaMeta {
  run_id: string;
  meta_termino_em: string;
  necessarias: number | null;
  ligar: string[];
  reativar: string[];
  cabe_com_as_ligadas: boolean;
  /** A meta do lote ja passou: `necessarias` e o que o termina o quanto antes. */
  atrasado?: boolean;
  fim: InstanteEstimado | null;
}

/** VM viva que nem reativada admitiria: nao conta como vaga a reativar. */
export interface VmImpedida {
  vm: string;
  /** `disco_baixo` · `incompativel` · `sem_vaga`. */
  motivo: string;
}

/**
 * QUANTAS WORKERS LIGAR (D2): o menor numero com que o fim estimado (p90) de
 * cada exame da fila cabe no prazo dele — entrou na fila + `meta_h`. A conta
 * usa so as workers ligadas e vivas; quem liga e desliga as VMs e o dono.
 */
export interface RecomendacaoDaFrota {
  tipo: "ESTIMADO";
  /** `fila_vazia` · `cabe` · `ligar` · `nao_cabe`. */
  estado: string;
  /** A frase do backend (sem acento); a tela escreve a dela pelos campos. */
  frase: string;
  meta_h: number;
  criterio: string;
  frota: string[];
  ligadas: string[];
  /** Quantas workers (ao todo) bastam; null = nem com todas. */
  necessarias: number | null;
  ligar: string[];
  reativar: string[];
  cenarios: CenarioDaFrota[];
  lotes: LoteNaMeta[];
  pode_desligar: string[];
  pode_drenar: string[];
  /** Exames cujo prazo da meta ja venceu: ficam fora do "cabe". */
  atrasados?: number;
  impedidas?: VmImpedida[];
}

/** Uma execucao na fila (viva) ou terminada na janela da leitura. */
export interface ItemDaFila {
  execucao_id: number;
  run_id: string;
  lote: string | null;
  exame_hash: string;
  renach: string | null;
  categoria: string | null;
  duracao_video_s: number | null;
  plano: string;
  modo: string;
  estado: string;
  /** `disco_abaixo_do_minimo:8.2GB<10GB` e outros motivos do orquestrador. */
  motivo: string | null;
  para_analise: boolean;
  retomadas_auto: number;
  /** 1 = "passar a frente". */
  prioridade: number;
  /** So NA_FILA: 1..n na ordem em que o cluster admite. */
  posicao: number | null;
  entrou_em: InstanteMedido | null;
  inicio: InstanteDaFila | null;
  /** null numa viva = a fila nao anda. */
  fim: InstanteDaFila | null;
  passou_da_estimativa: boolean;
  rodando_ha_s: number | null;
  conduzida_por: string | null;
  conduzida_vm: string | null;
  no_corrente: string | null;
  /** O estado de cada no do grafo (ESTADOS_DE_NO). */
  etapas: Record<string, string>;
  custo_usd: CustoMedido | null;
  criado_por: string | null;
}

export interface FilaLida {
  lido_em: string;
  /** `ambos` (dev: a API tambem processa) · `api` · `trabalhador`. */
  papel: string;
  lease_s: number;
  trabalhadores: TrabalhadorLido[];
  vagas: VagasDaFila;
  /** Por estado de execucao, sobre os itens devolvidos. */
  resumo: Record<string, number>;
  previsao: PrevisaoDaFila;
  recomendacao: RecomendacaoDaFrota;
  /** Rodando, NA_FILA pela posicao, PRECISA_DE_DADO, terminadas (a mais nova primeiro). */
  itens: ItemDaFila[];
  /** As terminadas da janela passaram do teto e as mais antigas ficaram de fora. */
  terminadas_truncadas?: boolean;
  /** O teto de gasto da campanha (CLUSTER.md §6.1). */
  teto_de_gasto?: TetoDeGastoLido | null;
}

export interface VmDaFrota {
  vm: string;
  vivos: number;
  admitindo: number;
  vagas: number;
  ocupadas: number;
  estados: Record<string, number>;
  donos: string[];
}

export interface FrotaLida {
  lido_em: string;
  lease_s: number;
  schema_central: {
    versao: string;
    commit: string | null;
    impressao_regras: string | null;
    convergido_em: string | null;
  } | null;
  trabalhadores: TrabalhadorLido[];
  vms: VmDaFrota[];
}

export interface DesejadoGravado {
  desejado: "drenar" | "ativo";
  donos: string[];
  vm: string | null;
}

/** O pedido de um lote: os hashes e como rodar (o "Rodar tudo" de cada um). */
export interface PedidoDeLote {
  /** 1 a 500. */
  hashes: string[];
  plano?: PlanoDaEsteira;
  /**
   * C, D e E (22/10/2026): `"B"` SEM `plano` resolve o plano POR EXAME pela
   * categoria (B e2, C c_e2, D d_e2, E e_e2); a A sai `plano_incompativel`.
   */
  familia?: "B";
  modo?: "completo" | "analises";
  reusar?: boolean;
  /** 1 = passa a frente da fila. */
  prioridade?: 0 | 1;
  /** Deixa entrar exame que ja tem execucao CONCLUIDA (roda de novo). */
  incluir_processados?: boolean;
  meta_termino_em?: string | null;
}

/** O pedido mais o ECO da previa confirmada; 409 se ela mudou. */
export interface ConfirmacaoDeLote extends PedidoDeLote {
  n: number;
  custo_p50: number;
}

export type SituacaoNaPrevia =
  | "aceito"
  | "plano_incompativel"
  | "fora_do_espelho"
  | "nao_avaliado_pelo_examinador"
  | "sem_video"
  | "ja_vivo"
  | "ja_processado"
  | "hash_invalido"
  | "repetido";

export interface ExameDaPrevia {
  exame_hash: string;
  situacao: SituacaoNaPrevia;
  aceito: boolean;
  /** A viva (`ja_vivo`) ou a concluida (`ja_processado`). */
  execucao_id: number | null;
  estado: string | null;
  categoria: string | null;
  duracao_video_s: number | null;
  /** `duracao_desconhecida`: tem o video, falta a duracao (o custo vai pela mediana). */
  aviso: string | null;
}

export interface PreviaDoLote {
  lido_em: string;
  /** Quantos entram na fila. */
  n: number;
  /** O eco que a confirmacao devolve (4 casas). */
  custo_p50: number;
  contagem: Record<string, number>;
  /** Na ordem do pedido. */
  exames: ExameDaPrevia[];
  custo: CustoEstimado;
  /** O fim do ULTIMO exame do lote; null = a fila nao anda. */
  fim_estimado: InstanteEstimado | null;
  previsao_estado: string;
  previsao_frase: string;
  /** A recomendacao com o lote ja na fila. */
  recomendacao: RecomendacaoDaFrota;
}

export interface LoteCriado {
  /** `lote-YYYYmmddTHHMMSS-xxxx`. */
  run_id: string;
  n: number;
  criadas: { exame_hash: string; execucao_id: number; estado: string }[];
  recusadas: {
    exame_hash: string;
    situacao: string;
    motivo: string | null;
    execucao_id: number | null;
  }[];
  custo: CustoEstimado;
  fim_estimado: InstanteEstimado | null;
}

export interface LoteCancelado {
  run_id: string;
  so_na_fila: boolean;
  canceladas: number[];
  recusadas: { execucao_id: number; motivo: string }[];
}

export interface PrioridadeGravada {
  execucao_id: number;
  prioridade: number;
  lote: string | null;
}

/** O teto de exames de um lote (o `max_length` do backend). */
export const MAX_EXAMES_POR_LOTE = 500;

/**
 * A fila, a frota, a previsao e a recomendacao, numa leitura (cache de 3 s no
 * backend). Sem `desde`, as terminadas das ultimas 6 h; `run_id` so filtra o
 * que aparece — as vivas entram todas na previsao.
 */
export async function getFila(
  filtro: { run_id?: string | null; desde?: string | null } = {},
  signal?: AbortSignal,
): Promise<FilaLida> {
  const params = new URLSearchParams();
  if (filtro.run_id) params.set("run_id", filtro.run_id);
  if (filtro.desde) params.set("desde", filtro.desde);
  const qs = params.toString();
  return request<FilaLida>(`/esteira/fila${qs ? `?${qs}` : ""}`, { signal, timeoutMs: 20_000 });
}

/**
 * Pede ao trabalhador que pare de admitir e termine o que e dele. O `dono`
 * tem "/", "#" e ":" — vai com `encodeURIComponent` (a rota e `{dono:path}`).
 * 404 = nao esta no registro.
 */
export async function drenarTrabalhador(dono: string, signal?: AbortSignal): Promise<DesejadoGravado> {
  return request<DesejadoGravado>(`/esteira/trabalhadores/${encodeURIComponent(dono)}/drenar`, {
    method: "POST",
    signal,
  });
}

/** Desfaz o drenar: o trabalhador volta a admitir. */
export async function reativarTrabalhador(dono: string, signal?: AbortSignal): Promise<DesejadoGravado> {
  return request<DesejadoGravado>(`/esteira/trabalhadores/${encodeURIComponent(dono)}/reativar`, {
    method: "POST",
    signal,
  });
}

/** Drena todos os trabalhadores da VM — o passo antes de desligar a VM no GCP. */
export async function drenarVm(vm: string, signal?: AbortSignal): Promise<DesejadoGravado> {
  return request<DesejadoGravado>(`/esteira/vms/${encodeURIComponent(vm)}/drenar`, { method: "POST", signal });
}

/** Reativa todos os trabalhadores da VM. */
export async function reativarVm(vm: string, signal?: AbortSignal): Promise<DesejadoGravado> {
  return request<DesejadoGravado>(`/esteira/vms/${encodeURIComponent(vm)}/reativar`, { method: "POST", signal });
}

/**
 * A PREVIA de um lote: por exame, se entra na fila ou por que nao; o custo e o
 * fim ESTIMADOS; a recomendacao com o lote incluido. So le — nada e criado.
 */
export async function previaDoLote(pedido: PedidoDeLote, signal?: AbortSignal): Promise<PreviaDoLote> {
  return request<PreviaDoLote>("/esteira/lotes/previa", {
    method: "POST",
    body: pedido,
    signal,
    timeoutMs: 60_000,
  });
}

/**
 * ENFILEIRA o lote da previa (201). O corpo leva o eco da previa confirmada;
 * 409 = ela mudou (a nova vem em `previaDoConflito(erro)`), 422 = nenhum
 * exame aceito. O prazo e longo: o backend cria as execucoes uma a uma, e um
 * tempo esgotado aqui NAO quer dizer que o lote nao foi criado.
 */
export async function enfileirarLote(confirmacao: ConfirmacaoDeLote, signal?: AbortSignal): Promise<LoteCriado> {
  return request<LoteCriado>("/esteira/lotes", {
    method: "POST",
    body: confirmacao,
    signal,
    timeoutMs: 180_000,
  });
}

/** A previa nova que acompanha o 409 do `enfileirarLote`; null em qualquer outro erro. */
export function previaDoConflito(erro: unknown): PreviaDoLote | null {
  if (!(erro instanceof ApiError) || erro.status !== 409 || !isRecord(erro.corpo)) return null;
  const detail = erro.corpo.detail;
  if (!isRecord(detail) || !isRecord(detail.previa)) return null;
  const previa = detail.previa;
  return typeof previa.n === "number" && Array.isArray(previa.exames) ? (previa as unknown as PreviaDoLote) : null;
}

/** Cancela o lote: so o que esta na fila (padrao) ou tambem o que roda (cooperativo). */
export async function cancelarLote(
  runId: string,
  soNaFila = true,
  signal?: AbortSignal,
): Promise<LoteCancelado> {
  return request<LoteCancelado>(
    `/esteira/lotes/${encodeURIComponent(runId)}/cancelar?so_na_fila=${soNaFila ? "true" : "false"}`,
    { method: "POST", signal, timeoutMs: 60_000 },
  );
}

/** "Passar a frente" (1) ou voltar a ordem normal (0). So em NA_FILA: 409 nas outras. */
export async function priorizarExecucao(
  id: number,
  prioridade: 0 | 1,
  signal?: AbortSignal,
): Promise<PrioridadeGravada> {
  return request<PrioridadeGravada>(`/esteira/execucoes/${id}/prioridade`, {
    method: "PATCH",
    body: { prioridade },
    signal,
  });
}
// ==== fim fila do cluster ====

// ==== consumo ====
/*
  O CONSUMO DE UM EXAME — custo, tempo e armazenamento das dezessete linhas da
  esteira, por etapa e no total (`GET /exams/{hash}/consumo`; o miolo e
  backend/app/compliance/consumo.py). E o que a tira fixa da moldura e a tabela
  da Visao geral mostram, em todas as abas. So le.

  O CUSTO E O RELOGIO SAO OS DA ESTEIRA (a mesma conta da aba Processamento);
  o armazenamento e medido na hora — `banco` (as linhas deste exame nas tabelas
  da etapa) e `disco` (a midia local, por inode). `null` e "nao se sabe", nunca
  zero.
*/

/** Um arquivo, ou uma pasta somada (nome com "/" no fim), da pasta da etapa. */
export interface ItemEmDisco {
  nome: string;
  bytes: number;
  arquivos: number;
}

export interface ConsumoDaEtapa {
  etapa: string;
  papel: string | null;
  /** `etapa` ou `etapa:papel` — a chave do grafo (a mesma de `Aba.etapas`). */
  chave: string;
  /** O rotulo do grafo ("Áudio", "Câmera interna"...). */
  rotulo: string;
  estado: string;
  /** O que se pagou (retentativas e descartado incluidos); `null` = nao se sabe. */
  custo_usd: number | null;
  custo_origem: OrigemDoCusto | null;
  chamadas: number | null;
  modelo: string | null;
  iniciado_em: string | null;
  terminado_em: string | null;
  /** A parede gravada; `null` em rodada anterior a 23/09/2026 (sem relogio). */
  parede_s: number | null;
  /** So na linha em curso: segundos desde o inicio, ate `lido_em`. */
  decorrido_s: number | null;
  /** CPU do cgroup (ledger de host), so quando e da MESMA execucao. */
  cpu_s: number | null;
  /** `etapa` | `visao_4_papeis` (a visao e medida inteira). */
  cpu_escopo: string | null;
  cpu_run_id: string | null;
  banco_bytes: number | null;
  banco_linhas: number | null;
  banco_tabelas: Record<string, number> | null;
  disco_bytes: number | null;
  disco_itens: ItemEmDisco[];
  /** banco + disco; `null` se uma das duas medidas falhou. */
  armazenamento_bytes: number | null;
}

/** O que nao e de uma etapa so: o video de origem e o recorte comum da visao. */
export interface ConsumoCompartilhado {
  chave: string;
  rotulo: string;
  descricao: string;
  disco_bytes: number;
  disco_itens: ItemEmDisco[];
}

export interface TotalDoConsumo {
  custo_usd: number | null;
  /** `false`: alguma etapa que chamou modelo nao tem custo — a soma e piso. */
  custo_completo: boolean | null;
  /**
   * A parede de cada etapa somada (as em curso ate `lido_em`), com a visao UMA
   * vez, pelo papel mais longo (C1.16 da auditoria de 23/09, orquestrador-6).
   */
  tempo_soma_s: number;
  /** A uniao dos intervalos: o relogio em que ALGUMA etapa rodava. */
  tempo_ocupado_s: number;
  primeiro_inicio: string | null;
  ultimo_fim: string | null;
  /** As `pronto` sem relogio: o tempo total e piso. */
  etapas_sem_relogio: string[];
  cpu_s: number | null;
  cpu_run_id: string | null;
  banco_bytes: number | null;
  /** Por inode: o hard link do recorte da camera nao conta duas vezes. */
  disco_bytes: number | null;
  armazenamento_bytes: number | null;
}

export interface ConsumoDoExame {
  exame_hash: string;
  no_espelho: boolean;
  duracao_video_s: number | null;
  /** Alguma etapa em curso, ou o "Rodar tudo" andando: o polling apressa. */
  andando: boolean;
  /** Sempre dezessete, na ordem canonica do grafo (a visao por papel). */
  etapas: ConsumoDaEtapa[];
  compartilhado: ConsumoCompartilhado[];
  total: TotalDoConsumo;
  lido_em: string;
}

/** O consumo deste exame. So leitura; barata o bastante para polling. */
export async function getConsumoDoExame(
  hash: string,
  signal?: AbortSignal,
): Promise<ConsumoDoExame> {
  return request<ConsumoDoExame>(`/exams/${encodeURIComponent(hash)}/consumo`, { signal });
}
// ==== fim consumo ====

// ==== registro completo da producao legada ====

/*
  O REGISTRO COMPLETO DA PRODUCAO, por exame — contrato em
  docs/producao-legada/REGISTRO-COMPLETO.md (§7 as rotas, §8 a tela).

  NAO E O ESPELHO. `ExamDetalhe` continua sendo o que o examinador registrou
  (22 colunas de `exams`, sem a IA de producao); o registro e a producao CRUA,
  versionada, e inclui o resultado da IA de la. Por isso mora em tipos e rotas
  proprios, e nunca se mistura ao `ExamDetalhe`: quem le um sabe o que esta
  lendo, quem le o outro tambem.

  ESCOPO CADASTRAL (decisao do dono, 26/09 ~21h: "logs nao precisa, preciso de
  informacoes cadastrais"): so o cadastro, o gabarito oficial, a revisao
  humana, o resultado da IA de la e a referencia da norma. Logs (exam_events,
  os_eventos, auditor_telemetria), visoes e contagens SAIRAM da resposta; o
  backend nao os devolve mais, e esta camada nao os le.

  PARSE CAMPO A CAMPO, pela licao registrada em `toProducao`: declarar o tipo
  sem copiar o campo faz a tela mentir com o dado na mao. Cada funcao `to*`
  abaixo e a UNICA porta de um campo do contrato para a tela; campo novo no
  backend precisa de tipo E de copia aqui.

  O conteudo de cada linha (`linha`) atravessa CRU, como `unknown`: a forma e da
  producao (to_jsonb da linha la), nao nossa — coluna nova la aparece na tela
  sem mudar nada deste lado, e a tela o percorre com o renderizador generico.
*/

/**
 * Os grupos da tela, NA ORDEM da tela (§7). Nao ha mais `outras`: o mapa do
 * backend e lista de permissao, e tabela de grupo desconhecido e DESCARTADA no
 * parse — o que nao foi decidido como cadastro e o que a tela nao deve mostrar.
 */
export const GRUPOS_DO_REGISTRO = [
  "cadastro",
  // 27/09, decisao do dono: a telemetria do veiculo, logo depois do cadastro
  "telemetria",
  "gabarito_oficial",
  "revisao_humana",
  "ia_producao",
  "referencia",
] as const;

export type GrupoDoRegistro = (typeof GRUPOS_DO_REGISTRO)[number];

/** Uma VERSAO de uma linha da producao (`producao_linha`). */
export interface LinhaDoRegistro {
  /** `producao_linha.id` daqui — a ancora do keyset, nao o id da producao. */
  id: number;
  /** A chave da linha NA ORIGEM (id, exam_id...) ou o md5 do conteudo. */
  chave: string;
  /** Relogio DAQUI: quando o registro viu esta versao pela primeira/ultima vez. */
  primeiro_visto: string | null;
  ultimo_visto: string | null;
  /**
   * So nas versoes encerradas (`historico=1`). O contrato §7 nao lista estes
   * dois na forma da linha, mas o DDL os tem: lidos quando vierem, `null`
   * quando nao — e a tela nao conclui "vigente" da ausencia deles.
   */
  encerrada_em: string | null;
  /** 'alterada' | 'removida' (CHECK do DDL), ou null. */
  motivo_fim: string | null;
  /** A linha inteira, como a producao a tem (to_jsonb). Forma da producao. */
  linha: unknown;
}

export interface TabelaDoRegistro {
  /** Nome da tabela NA PRODUCAO, cru ("exams", "exam_events"...). */
  tabela: string;
  grupo: GrupoDoRegistro;
  rotulo: string;
  /** Versoes vigentes (encerrada_em NULL). */
  vigentes: number;
  /** Versoes encerradas ('alterada' ou 'removida'): o historico. */
  historicas: number;
  /** Ha mais vigentes do que as `linhas` entregues (corte do `limite`). */
  truncado: boolean;
  /**
   * As vigentes mais recentes. Vazio nas de REFERENCIA (dimensoes, iguais em
   * todo exame): a tela as pagina quando a gaveta abre.
   */
  linhas: LinhaDoRegistro[];
}

/**
 * Onde o exame esta na selecao AUTOMATICA do worker, no desenho "daqui para
 * frente" (DDL 006; decisao do dono em 26/09/2026: "so os que ja tenho, so que
 * daqui para frente o que voce puxar, puxar completo"). Quem calcula e o
 * backend, com um SQL que espelha `selecionar_exames` de sync/registro.py:
 *
 * - `nao_ativado`: sem ativacao, o worker nao escolhe exame nenhum sozinho;
 * - `em_quarentena`: quarentena vigente, a fila o pula;
 * - `na_fila`: o espelho puxou o exame depois da ativacao (novo, updated_at
 *   que andou, etapas com outro conteudo), a vigia das tabelas cadastrais viu
 *   a producao muda-lo (critério c), ou a leitura dele e anterior a ativacao
 *   e e refeita uma vez — `motivo_da_fila` diz qual;
 * - `lido`: lido por inteiro, e nada mudou desde entao;
 * - `fora_do_registro`: o espelho ja o tinha na ativacao e ele nao mudou —
 *   "fica como esta"; so o "atualizar agora" o le.
 */
export const SITUACOES_DO_REGISTRO = [
  "nao_ativado",
  "fora_do_registro",
  "na_fila",
  "lido",
  "em_quarentena",
] as const;

export type SituacaoNoRegistro = (typeof SITUACOES_DO_REGISTRO)[number];

/** A ativacao do registro (`producao_registro_inicio`). */
export interface InicioDoRegistro {
  /** Relogio do PORTAL: dali em diante, o que o espelho puxar e lido por inteiro. */
  inicio_em: string;
  /** Exames do espelho gravados na linha de base na ativacao. */
  exames_na_base: number | null;
}

/** A ultima mudanca que a vigia das tabelas cadastrais viu na producao. */
export interface MudancaNoCadastro {
  /** Relogio do PORTAL: quando a vigia a VIU (nao quando a producao gravou). */
  vista_em: string;
  /**
   * A(s) tabela(s) da producao da ultima passada da vigia que viu este exame
   * (auditor_pareceres, supervisor_decisoes...). Mais de uma vem separada por
   * virgula, em ordem alfabetica, como o sync grava `ultimo_fluxo`.
   */
  tabela: string;
  /** Linhas mudadas contadas para este exame, somadas desde sempre. */
  linhas: number | null;
}

export interface ControleDoRegistro {
  /**
   * Ultimo snapshot bem-sucedido do exame: as tabelas cadastrais dele, lidas
   * por inteiro. `null` = nunca lido. Anterior a `inicio.inicio_em` = feito
   * pelo desenho antigo (o worker o rele uma vez).
   */
  snapshot_em: string | null;
  /**
   * now() do portal na selecao que antecedeu o ultimo snapshot: o que a vigia
   * viu antes disto esta na leitura. `null` = leitura anterior a ele (vale o
   * `snapshot_em`), nunca lido, ou backend sem o DDL 007.
   */
  leitura_iniciada_em: string | null;
  /**
   * `exams.updated_at` da PRODUCAO que o ultimo snapshot capturou. `null` com
   * snapshot NAO quer dizer leitura antiga (o worker tambem o deixa vazio
   * quando o exame nao existia mais la); quem diz "anterior a ativacao" e
   * `snapshot_em < inicio.inicio_em`.
   */
  updated_at_lido: string | null;
  /** Ultima leitura do upload.json (null tambem quando nao precisa ser lido). */
  arquivos_em: string | null;
  /** `null` = nunca lido; `false` = o exame sumiu da producao. */
  existe_na_producao: boolean | null;
  /** Erro da ultima tentativa que falhou (sucesso apaga), ou null. Sem data. */
  erro: string | null;
  /** Falhas seguidas desde o ultimo sucesso; `null` = sem linha de controle. */
  falhas: number | null;
  /** Como esta no banco, inclusive ja vencida — quem diz se vale e `em_quarentena`. */
  quarentena_ate: string | null;
  /**
   * Quarentena valendo AGORA, pelo relogio do BANCO (o mesmo da fila do
   * worker), e nao do navegador. So `true` explicito liga.
   */
  em_quarentena: boolean;
  /** O ultimo "atualizar agora" (um por exame), atendido ou nao. */
  pedido_em: string | null;
  /** Quando o worker deu baixa nele — depois da tentativa, com ou sem sucesso. */
  pedido_atendido_em: string | null;
  /** `pedido_em` enquanto nao atendido (o campo de antes; backend antigo so tem este). */
  pedido_pendente_em: string | null;
  /**
   * A situacao na selecao do worker. `null` = o backend nao sabe (banco sem o
   * DDL 006, backend antigo, ou valor que esta tela nao conhece): a tela
   * mostra os fatos do controle e nao afirma nada sobre a fila.
   */
  situacao: SituacaoNoRegistro | null;
  /**
   * Por que esta na fila ('novo' | 'updated_at' | 'cadastro' | 'etapas' |
   * 'leitura_anterior'); so com `situacao === "na_fila"`. `string` e nao a
   * uniao, pelo motivo de `VideoDeOrigem.fonte`: um motivo novo do backend
   * aparece cru, nao some.
   */
  motivo_da_fila: string | null;
  /** O exame esta na linha de base da ativacao. `null` = sem ativacao ou sem o DDL 006. */
  na_base: boolean | null;
  /** A ativacao; `null` = nao ativado (ou backend sem o DDL 006/007). */
  inicio: InicioDoRegistro | null;
  /** A ultima mudanca vista pela vigia das tabelas cadastrais; `null` = nenhuma. */
  mudanca_no_cadastro: MudancaNoCadastro | null;
  /**
   * `false` = o hash nao tem o formato que o worker aceita: nem a fila nem o
   * "atualizar agora" leem este exame. So `false` explicito vale.
   */
  hash_aceito: boolean | null;
  /**
   * `false` = exame criado antes do piso do espelho: o espelho nao o
   * acompanha mais, e so a vigia do cadastro (ou o pedido) o poe na fila.
   */
  espelho_acompanha: boolean | null;
  /** O piso do espelho (AAAA-MM-DD), ou `null`. */
  piso_do_espelho: string | null;
}

/**
 * De onde o video veio: a pergunta que originou o registro inteiro.
 *
 * `fonte` e `string` e nao a uniao dos dois valores conhecidos
 * ('ingest_payload_raw' | 'upload.json') de proposito: um terceiro valor que o
 * backend passe a mandar nao pode apagar a atribuicao da URL — a tela traduz os
 * dois conhecidos e mostra o cru dos outros.
 */
export interface VideoDeOrigem {
  url: string | null;
  fonte: string | null;
}

/**
 * O upload.json do exame no disco da VM de producao (§4) — a versao vigente, ou,
 * se o arquivo SUMIU de la, a ultima guardada (`vigente: false`).
 *
 * `json_valido`, `vigente`, `encerrada_em`, `motivo_fim` e `omitido` estao no
 * schema do backend (app/schemas/registro_producao.py) e nao no exemplo do §7:
 * lidos aqui porque sem eles a tela afirmaria "arquivo presente" sobre um
 * arquivo que a producao apagou, ou desenharia texto cru como se fosse JSON.
 */
export interface UploadJsonDoRegistro {
  id: number | null;
  tamanho: number | null;
  modificado_em: string | null;
  md5: string | null;
  /**
   * O JSON parseado; o TEXTO cru quando os bytes nao sao JSON valido
   * (`json_valido: false`); `null` quando passou do teto e nao foi lido.
   */
  conteudo: unknown;
  /** `false` tambem quando nao ha conteudo — so `true` estrito vale. */
  json_valido: boolean;
  /** `false` = o arquivo sumiu da VM e esta e a ultima versao guardada. */
  vigente: boolean;
  encerrada_em: string | null;
  motivo_fim: string | null;
  /** Versoes anteriores (encerradas) do arquivo, no total. */
  historicas: number;
  /** 'acima_do_teto' quando os bytes nao vieram (o DDL nao admite outro). */
  omitido: string | null;
}

/**
 * O resumo da telemetria VIGENTE do exame (`exam_telemetria`, 27/09). O
 * registro guarda a telemetria como UMA linha por exame — o array dos pontos,
 * em ordem de `seq` (o percurso) e depois os eventos de sensor —, de ~88 KB em
 * media e ate ~0,5 MB: o resumo traz so as contas, feitas pelo backend no
 * banco, e o array vem pela rota de tabela quando a gaveta abre.
 */
export interface ResumoDaTelemetria {
  /** `producao_linha.id` da versao vigente. */
  id: number | null;
  primeiro_visto: string | null;
  ultimo_visto: string | null;
  /** `false` = a linha gravada nao e um array: contas zeradas, linha crua na gaveta. */
  forma_valida: boolean;
  pontos: number;
  /** Quantos de cada `origem` (hoje `percurso` e `sensor`). */
  por_origem: Record<string, number>;
  seq_min: number | null;
  seq_max: number | null;
  /**
   * `dt_evento` como gravado (ISO sem fuso: o relogio do EQUIPAMENTO). So os
   * eventos de sensor tem; o percurso nao tem relogio.
   */
  dt_evento_min: string | null;
  dt_evento_max: string | null;
  /** km/h (unidade INFERIDA, docs/compliance/TELEMETRIA-ESTUDO.md). */
  velocidade_max: number | null;
  notificacoes: number;
  notificacoes_aceitas: number;
  /** Eventos por `id_sensor`. */
  sensores: Record<string, number>;
}

export interface RegistroDaProducao {
  /**
   * `false` = o backend nao conseguiu ler o registro: as tabelas nao existem
   * neste portal (DDL 004 nao aplicado) ou a leitura falhou — o motivo vai para
   * o log dele, nao para a resposta. NAO e "exame nao lido": esse e
   * `controle.snapshot_em === null`, com `disponivel: true`.
   */
  disponivel: boolean;
  hash: string;
  exam_id: string | null;
  controle: ControleDoRegistro;
  /**
   * Vem MESMO com `disponivel: false`: o backend ainda diz o que o espelho sabe
   * (a `url` do `ingest_payload_raw`).
   */
  video_origem: VideoDeOrigem;
  /** So as tabelas cadastrais, na ordem da tela (inclusive as sem linha). */
  tabelas: TabelaDoRegistro[];
  upload_json: UploadJsonDoRegistro | null;
  /** `null` = sem versao vigente da telemetria (sem telemetria, nunca lido...). */
  telemetria: ResumoDaTelemetria | null;
}

/**
 * Uma pagina de UMA tabela do registro. Mesma ordem do resumo (id
 * DECRESCENTE): `apos` e exclusivo (`id < apos`), e o "carregar mais" continua
 * exatamente de onde as linhas do resumo pararam.
 */
export interface PaginaDoRegistro {
  /** `false` = o backend nao conseguiu ler o registro (tabelas ausentes, falha). */
  disponivel: boolean;
  tabela: string;
  linhas: LinhaDoRegistro[];
  /** Passe de volta como `apos`; `null` = acabou. */
  proximo: number | null;
}

/** Inteiro, ou null. Para id e tamanho: fracao so viria de payload corrompido. */
function toInteiroOuNull(value: unknown): number | null {
  const numero = toNumeroOuNull(value);
  return numero === null ? null : Math.trunc(numero);
}

/**
 * Instante ISO, ou null. E o proprio `toTextoOuNull` (string vazia nao e
 * instante, e `undefined` na tela viraria "Invalid Date"); o nome existe so
 * para o parse abaixo dizer o que cada campo e.
 */
const toInstanteOuNull = toTextoOuNull;

/**
 * Uma versao de linha, ou null quando nao da para usar.
 *
 * `id` e exigido: sem ele a linha nao tem key estavel na lista NEM ancora para
 * o "carregar mais" — uma linha sem id no meio da pagina faria o keyset pular
 * ou repetir. `chave` numerica (id inteiro da origem serializado como numero)
 * vira texto, porque o contrato a trata como texto.
 */
function toLinhaDoRegistro(raw: unknown): LinhaDoRegistro | null {
  if (!isRecord(raw)) return null;
  const id = toInteiroOuNull(raw.id);
  if (id === null) return null;
  const chave =
    typeof raw.chave === "string"
      ? raw.chave
      : typeof raw.chave === "number"
        ? String(raw.chave)
        : "";
  return {
    id,
    chave,
    primeiro_visto: toInstanteOuNull(raw.primeiro_visto),
    ultimo_visto: toInstanteOuNull(raw.ultimo_visto),
    encerrada_em: toInstanteOuNull(raw.encerrada_em),
    motivo_fim: toTextoOuNull(raw.motivo_fim),
    // `?? null` e nao `||`: objeto vazio e array vazio sao linha legitima.
    linha: raw.linha ?? null,
  };
}

function toLinhasDoRegistro(value: unknown): LinhaDoRegistro[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(toLinhaDoRegistro)
    .filter((linha): linha is LinhaDoRegistro => linha !== null);
}

function toGrupoDoRegistro(value: unknown): GrupoDoRegistro | null {
  return typeof value === "string" &&
    (GRUPOS_DO_REGISTRO as readonly string[]).includes(value)
    ? (value as GrupoDoRegistro)
    : null;
}

function toTabelaDoRegistro(raw: unknown): TabelaDoRegistro | null {
  if (!isRecord(raw)) return null;
  const tabela = toTextoOuNull(raw.tabela);
  // Sem nome nao ha o que pedir na rota de tabela nem o que mostrar no titulo.
  if (tabela === null) return null;
  // Grupo que esta tela nao conhece: fora do escopo cadastral, descartada.
  const grupo = toGrupoDoRegistro(raw.grupo);
  if (grupo === null) return null;
  return {
    tabela,
    grupo,
    rotulo: toTextoOuNull(raw.rotulo) ?? tabela,
    // Contagens: nunca null — a tela compara com zero para decidir o que dizer,
    // e `undefined > 0` daria false por acidente, nao por dado.
    vigentes: toContagemOuNull(raw.vigentes) ?? 0,
    historicas: toContagemOuNull(raw.historicas) ?? 0,
    truncado: raw.truncado === true,
    linhas: toLinhasDoRegistro(raw.linhas),
  };
}

/** `{texto: contagem}`, sem o que nao for contagem (a forma e do backend, mas a tela nao confia). */
function toContagensPorNome(value: unknown): Record<string, number> {
  if (!isRecord(value)) return {};
  const saida: Record<string, number> = {};
  for (const [nome, n] of Object.entries(value)) {
    const contagem = toContagemOuNull(n);
    if (contagem !== null) saida[nome] = contagem;
  }
  return saida;
}

function toResumoDaTelemetria(raw: unknown): ResumoDaTelemetria | null {
  if (!isRecord(raw)) return null;
  return {
    id: toInteiroOuNull(raw.id),
    primeiro_visto: toInstanteOuNull(raw.primeiro_visto),
    ultimo_visto: toInstanteOuNull(raw.ultimo_visto),
    forma_valida: raw.forma_valida === true,
    pontos: toContagemOuNull(raw.pontos) ?? 0,
    por_origem: toContagensPorNome(raw.por_origem),
    seq_min: toInteiroOuNull(raw.seq_min),
    seq_max: toInteiroOuNull(raw.seq_max),
    dt_evento_min: toTextoOuNull(raw.dt_evento_min),
    dt_evento_max: toTextoOuNull(raw.dt_evento_max),
    velocidade_max: toNumeroOuNull(raw.velocidade_max),
    notificacoes: toContagemOuNull(raw.notificacoes) ?? 0,
    notificacoes_aceitas: toContagemOuNull(raw.notificacoes_aceitas) ?? 0,
    sensores: toContagensPorNome(raw.sensores),
  };
}

function toUploadJsonDoRegistro(raw: unknown): UploadJsonDoRegistro | null {
  if (!isRecord(raw)) return null;
  return {
    id: toInteiroOuNull(raw.id),
    tamanho: toContagemOuNull(raw.tamanho),
    modificado_em: toInstanteOuNull(raw.modificado_em),
    md5: toTextoOuNull(raw.md5),
    conteudo: raw.conteudo ?? null,
    json_valido: raw.json_valido === true,
    // Ausente = vigente: e o caso do exemplo do §7, que nao traz o campo. So um
    // `false` EXPLICITO diz que o arquivo sumiu da producao.
    vigente: raw.vigente !== false,
    encerrada_em: toInstanteOuNull(raw.encerrada_em),
    motivo_fim: toTextoOuNull(raw.motivo_fim),
    historicas: toContagemOuNull(raw.historicas) ?? 0,
    omitido: toTextoOuNull(raw.omitido),
  };
}

/**
 * URL http(s), ou null. So esses esquemas contam como URL de origem: outro na
 * tela viraria um link que o navegador nao abre (ou, pior, `javascript:`).
 */
function toUrlHttpOuNull(value: unknown): string | null {
  const url = toTextoOuNull(value);
  return url !== null && /^https?:\/\//i.test(url) ? url : null;
}

/** Situacao conhecida, ou null — valor novo do backend nao vira texto errado. */
function toSituacaoNoRegistro(value: unknown): SituacaoNoRegistro | null {
  return typeof value === "string" &&
    (SITUACOES_DO_REGISTRO as readonly string[]).includes(value)
    ? (value as SituacaoNoRegistro)
    : null;
}

/** A mudanca vista pela vigia do cadastro, ou null. Sem instante ou tabela, nada. */
function toMudancaNoCadastro(raw: unknown): MudancaNoCadastro | null {
  if (!isRecord(raw)) return null;
  const vista = toInstanteOuNull(raw.vista_em);
  const tabela = toTextoOuNull(raw.tabela);
  if (vista === null || tabela === null) return null;
  return { vista_em: vista, tabela, linhas: toContagemOuNull(raw.linhas) };
}

/** Booleano do contrato: so `true`/`false` explicitos; o resto e "nao sei". */
function toBooleanoOuNull(value: unknown): boolean | null {
  return value === true || value === false ? value : null;
}

/** A ativacao, ou null. Sem `inicio_em` legivel nao ha ativacao a mostrar. */
function toInicioDoRegistro(raw: unknown): InicioDoRegistro | null {
  if (!isRecord(raw)) return null;
  const inicio = toInstanteOuNull(raw.inicio_em);
  if (inicio === null) return null;
  return { inicio_em: inicio, exames_na_base: toContagemOuNull(raw.exames_na_base) };
}

function toRegistroDaProducao(raw: unknown, hash: string): RegistroDaProducao {
  if (!isRecord(raw)) {
    throw new ApiError("Resposta da API em formato inesperado.", 500, API_BASE_URL);
  }
  const controle = isRecord(raw.controle) ? raw.controle : {};
  const video = isRecord(raw.video_origem) ? raw.video_origem : {};
  return {
    // Booleano ESTRITO: sem `disponivel: true` explicito, a tela diz que o
    // registro nao existe aqui — nunca desenha "nenhuma linha" sobre uma
    // resposta que ela nao entendeu.
    disponivel: raw.disponivel === true,
    hash: toTextoOuNull(raw.hash) ?? hash,
    exam_id: toTextoOuNull(raw.exam_id),
    controle: {
      snapshot_em: toInstanteOuNull(controle.snapshot_em),
      leitura_iniciada_em: toInstanteOuNull(controle.leitura_iniciada_em),
      updated_at_lido: toInstanteOuNull(controle.updated_at_lido),
      arquivos_em: toInstanteOuNull(controle.arquivos_em),
      existe_na_producao:
        controle.existe_na_producao === true || controle.existe_na_producao === false
          ? controle.existe_na_producao
          : null,
      erro: toTextoOuNull(controle.erro),
      falhas: toContagemOuNull(controle.falhas),
      quarentena_ate: toInstanteOuNull(controle.quarentena_ate),
      em_quarentena: controle.em_quarentena === true,
      pedido_em: toInstanteOuNull(controle.pedido_em),
      pedido_atendido_em: toInstanteOuNull(controle.pedido_atendido_em),
      pedido_pendente_em: toInstanteOuNull(controle.pedido_pendente_em),
      situacao: toSituacaoNoRegistro(controle.situacao),
      motivo_da_fila: toTextoOuNull(controle.motivo_da_fila),
      na_base: toBooleanoOuNull(controle.na_base),
      inicio: toInicioDoRegistro(controle.inicio),
      mudanca_no_cadastro: toMudancaNoCadastro(controle.mudanca_no_cadastro),
      hash_aceito: toBooleanoOuNull(controle.hash_aceito),
      espelho_acompanha: toBooleanoOuNull(controle.espelho_acompanha),
      piso_do_espelho: toTextoOuNull(controle.piso_do_espelho),
    },
    video_origem: {
      url: toUrlHttpOuNull(video.url),
      fonte: toTextoOuNull(video.fonte),
    },
    tabelas: Array.isArray(raw.tabelas)
      ? raw.tabelas
          .map(toTabelaDoRegistro)
          .filter((t): t is TabelaDoRegistro => t !== null)
      : [],
    upload_json: toUploadJsonDoRegistro(raw.upload_json),
    telemetria: toResumoDaTelemetria(raw.telemetria),
  };
}

/**
 * Timeout do registro: 30 s, e nao os 10 s do default. A resposta junta ate
 * `limite` versoes de cada uma das ~15 tabelas cadastrais, com a linha INTEIRA
 * (a de `exams` tem ~90 colunas, e `gemini_json` chega a 100 KB) — e a mais
 * pesada do portal por exame. Nada disto fala com a producao: e so o Postgres
 * local.
 */
const TIMEOUT_REGISTRO_MS = 30_000;

/**
 * O registro completo da producao deste exame (resumo, `limite` vigentes por
 * tabela). 404 = hash invalido (ou backend sem a rota); tabelas ausentes no
 * portal = `disponivel: false`, e nao erro.
 */
export async function getRegistroDaProducao(
  hash: string,
  limite = 50,
  signal?: AbortSignal,
): Promise<RegistroDaProducao> {
  const raw = await request<unknown>(
    `/exams/${encodeURIComponent(hash)}/registro-producao?limite=${encodeURIComponent(String(limite))}`,
    { signal, timeoutMs: TIMEOUT_REGISTRO_MS },
  );
  return toRegistroDaProducao(raw, hash);
}

/**
 * Uma pagina de UMA tabela do registro, por keyset em `producao_linha.id`.
 *
 * `historico: false` pagina as VIGENTES; `true`, so as versoes ENCERRADAS
 * ('alterada' e 'removida') — o unico jeito de ver o que a producao mudou ou
 * apagou.
 */
export async function getTabelaDoRegistro(
  hash: string,
  tabela: string,
  opcoes: { apos?: number | null; limite?: number; historico?: boolean } = {},
  signal?: AbortSignal,
): Promise<PaginaDoRegistro> {
  const params = new URLSearchParams();
  if (opcoes.apos !== undefined && opcoes.apos !== null) {
    params.set("apos", String(opcoes.apos));
  }
  params.set("limite", String(opcoes.limite ?? 200));
  params.set("historico", opcoes.historico === true ? "1" : "0");
  const raw = await request<unknown>(
    `/exams/${encodeURIComponent(hash)}/registro-producao/tabela/${encodeURIComponent(tabela)}?${params.toString()}`,
    { signal, timeoutMs: TIMEOUT_REGISTRO_MS },
  );
  if (!isRecord(raw)) {
    throw new ApiError("Resposta da API em formato inesperado.", 500, API_BASE_URL);
  }
  return {
    // O schema do backend tem `disponivel: bool = True`: so `false` explicito
    // desliga, e ausencia (backend que nao manda o campo) conta como lida.
    disponivel: raw.disponivel !== false,
    tabela: toTextoOuNull(raw.tabela) ?? tabela,
    linhas: toLinhasDoRegistro(raw.linhas),
    proximo: toInteiroOuNull(raw.proximo),
  };
}

/**
 * "Atualizar agora": grava o pedido para o worker do registro (upsert em
 * `producao_registro_pedido`, zerando `atendido_em`). E a UNICA escrita do
 * backend nestas tabelas, e NAO fala com a producao — quem le a producao e o
 * worker, na volta seguinte, pelo caminho somente-leitura de sempre.
 */
export async function pedirAtualizacaoDoRegistro(
  hash: string,
  signal?: AbortSignal,
): Promise<{ pedido_em: string | null }> {
  const raw = await request<unknown>(
    `/exams/${encodeURIComponent(hash)}/registro-producao/atualizar`,
    { method: "POST", signal, timeoutMs: 15_000 },
  );
  return { pedido_em: isRecord(raw) ? toInstanteOuNull(raw.pedido_em) : null };
}
// ==== fim registro completo da producao legada ====

// ==== visao compartilhada ====
/*
  A VISÃO COMPARTILHADA (pedido do dono, 30/09): nove vídeos de exame lado a
  lado, tocando ao mesmo tempo, para estudar a similaridade dos percursos de
  cada região — `GET /visao-compartilhada/exames`.

  A PAGINAÇÃO É KEYSET, o MESMO cursor de `GET /exams` (`ExamCursor`, ancorado
  em `(created_at, id)` DESC): o sync insere no topo a cada 300 s, e um OFFSET
  repetiria ou perderia exame entre uma página e outra. Trocar de filtro OBRIGA
  a soltar o cursor — ele é posição dentro de UM recorte.

  `total` é o recorte INTEIRO (uf + processado + só com vídeo servível e, com
  `camera=frontal`, só com a frontal mapeada) e não muda com o cursor; `sem_video` conta os do MESMO recorte que ficaram de fora
  por não terem vídeo servível — a tela diz os dois, para ninguém ler o total
  como o universo de exames da região.

  `uf` é só a SIGLA (as duas primeiras letras do RENACH, que o backend corta):
  o RENACH é dado pessoal e não viaja nesta rota.

  PARSE CAMPO A CAMPO, pela lição de `toProducao`: declarar o tipo sem copiar o
  campo faz a tela mentir com o dado na mão. Item sem `hash` ou sem `criado_em`
  é descartado — sem hash não há vídeo nem key, sem data não há legenda.
*/

/** As regiões que o dono pediu, na ordem em que ele as escreveu. */
export const UFS_DA_VISAO = ["PI", "PE", "SE", "MA"] as const;
export type UfDaVisao = (typeof UFS_DA_VISAO)[number];

/** Processado pela NOSSA IA (veredito pronto), não pela IA da produção legada. */
export const FILTROS_DE_PROCESSADO = ["todos", "sim", "nao"] as const;
export type FiltroDeProcessado = (typeof FILTROS_DE_PROCESSADO)[number];

/**
 * Como cada bloco mostra o vídeo (pedido do dono, 30/09): o MOSAICO 2×2 como
 * o arquivo vem, ou SÓ o quadrante da câmera frontal. "frontal" também é
 * FILTRO: o backend devolve só os exames com a frontal mapeada com confiança
 * (o `frontal` do item não-nulo), e o `total`/`sem_video` contam esse recorte.
 */
export const CAMERAS_DA_VISAO = ["mosaico", "frontal"] as const;
export type CameraDaVisao = (typeof CAMERAS_DA_VISAO)[number];

export function ehUfDaVisao(valor: string): valor is UfDaVisao {
  return (UFS_DA_VISAO as readonly string[]).includes(valor);
}

export function ehFiltroDeProcessado(valor: string | null): valor is FiltroDeProcessado {
  return valor !== null && (FILTROS_DE_PROCESSADO as readonly string[]).includes(valor);
}

export interface FiltrosDaVisaoCompartilhada {
  /** Vazio = todas as regiões. Só as quatro válidas chegam aqui. */
  ufs: readonly UfDaVisao[];
  processado: FiltroDeProcessado;
  /** Ausente ou "mosaico" = o parâmetro não vai; "frontal" restringe o recorte. */
  camera?: CameraDaVisao;
  /** 1..36 no backend; a tela pede 9. */
  per_page?: number;
}

export interface ItemDaVisaoCompartilhada {
  /** Minúsculo, como em `exams`. É por ele que o portal cita exame. */
  hash: string;
  /** A sigla da UF (PI, PE…), NUNCA o RENACH. `null` quando não se sabe. */
  uf: string | null;
  /** `exams.created_at` (ISO). */
  criado_em: string;
  duracao_s: number | null;
  categoria: string | null;
  status: string;
  /** Veredito do EXAMINADOR (A/R/N), não da nossa IA. */
  resultado_exame: ResultadoExame | null;
  /** A nossa IA fechou o veredito deste exame (`veredito_processamento` pronto). */
  processado: boolean;
  /** O payload traz percurso de GPS (mesmo critério da listagem de exames). */
  gps: boolean;
  /** Quadrante do mosaico onde está a câmera FRONTAL, pelo mapa vigente; `null` = não mapeada. */
  frontal: Posicao | null;
}

export interface PaginaDaVisaoCompartilhada {
  items: ItemDaVisaoCompartilhada[];
  total: number;
  sem_video: number;
  per_page: number;
  /** `null` = esta é a última página do recorte. */
  proximo_cursor: ExamCursor | null;
}

function toPosicaoOuNull(value: unknown): Posicao | null {
  return value === "tl" || value === "tr" || value === "bl" || value === "br" ? value : null;
}

function toItemDaVisaoCompartilhada(raw: unknown): ItemDaVisaoCompartilhada | null {
  if (!isRecord(raw)) return null;
  const hash = toTextoOuNull(raw.hash);
  const criado = toTextoOuNull(raw.criado_em);
  if (hash === null || criado === null) return null;
  const uf = toTextoOuNull(raw.uf);
  return {
    hash: hash.toLowerCase(),
    // Duas letras e nada mais: se um dia viesse o RENACH inteiro por engano,
    // a tela mostraria só a sigla, e não o documento.
    uf: uf === null ? null : uf.slice(0, 2).toUpperCase(),
    criado_em: criado,
    duracao_s: toNumeroOuNull(raw.duracao_s),
    categoria: toTextoOuNull(raw.categoria),
    status: toTextoOuNull(raw.status) ?? "",
    resultado_exame: toResultadoExame(raw.resultado_exame),
    // Só `true` explícito liga: um selo "IA ✓" ou "GPS" que não é verdade é
    // pior do que um que falta.
    processado: raw.processado === true,
    gps: raw.gps === true,
    frontal: toPosicaoOuNull(raw.frontal),
  };
}

function toPaginaDaVisaoCompartilhada(raw: unknown, perPagePedido: number): PaginaDaVisaoCompartilhada {
  if (!isRecord(raw)) {
    throw new ApiError("Resposta da API em formato inesperado.", 500, API_BASE_URL);
  }
  const items = Array.isArray(raw.items)
    ? raw.items
        .map(toItemDaVisaoCompartilhada)
        .filter((item): item is ItemDaVisaoCompartilhada => item !== null)
    : [];
  return {
    items,
    // Sem `total`, o que chegou; nunca um número inventado maior que ele.
    total: Math.max(0, Math.trunc(toNumeroOuNull(raw.total) ?? items.length)),
    sem_video: Math.max(0, Math.trunc(toNumeroOuNull(raw.sem_video) ?? 0)),
    per_page: toNumeroOuNull(raw.per_page) ?? perPagePedido,
    proximo_cursor: toExamCursor(raw.proximo_cursor),
  };
}

/**
 * Uma página da visão compartilhada. Sem `cursor`, a primeira do recorte; com
 * ele, a continuação ancorada na última linha já entregue.
 *
 * `uf` REPETE (`?uf=PI&uf=MA`, `append` e não `set`), como os filtros múltiplos
 * de `/exams`: com `set` a segunda região apagaria a primeira em silêncio.
 * Nenhuma UF marcada = parâmetro ausente = todas as regiões.
 */
export async function listarVisaoCompartilhada(
  filtros: FiltrosDaVisaoCompartilhada,
  cursor: ExamCursor | null,
  signal?: AbortSignal,
): Promise<PaginaDaVisaoCompartilhada> {
  const params = new URLSearchParams();
  for (const uf of filtros.ufs) params.append("uf", uf);
  if (filtros.processado !== "todos") params.set("processado", filtros.processado);
  // O padrão do backend é o mosaico: só o recorte da frontal viaja.
  if (filtros.camera === "frontal") params.set("camera", "frontal");
  const perPage = filtros.per_page ?? 9;
  params.set("per_page", String(perPage));
  // Os DOIS ou NENHUM, como em `buildExamQuery`: meio cursor é 422 no backend.
  if (cursor !== null && cursor.valor !== "" && cursor.id !== "") {
    params.set("cursor_valor", cursor.valor);
    params.set("cursor_id", cursor.id);
  }
  const raw = await request<unknown>(`/visao-compartilhada/exames?${params.toString()}`, {
    signal,
    // Folga sobre os 10 s do default: o COUNT do recorte e o EXISTS do
    // veredito andam juntos com a página.
    timeoutMs: 20_000,
  });
  return toPaginaDaVisaoCompartilhada(raw, perPage);
}
// ==== fim visao compartilhada ====
